import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DatePickerField } from '../components/ui/date-picker-field';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { ComponentDocSheet } from '../components/estimator/ComponentDocSheet';
import logger from '../lib/logger';
import { HOURS_INCREMENT_OPTIONS, COST_INCREMENT_OPTIONS, formatHours, formatCost } from '../lib/rounding';
import { Settings, ChevronRight, ChevronLeft, ChevronDown,
  Check, Plus, RotateCcw, Sparkles,
  Code, Trash2, Info, BarChart3, DollarSign,
  HelpCircle, BookOpen, Save, AlertTriangle, CalendarDays, Briefcase, GitCompareArrows, Library, BookmarkPlus, Lock,
  GitBranch, ArrowLeft, Clock, FileText, X, Settings2, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from '../components/ui/dropdown-menu';
import SectionGuide, { resetGuides } from '../components/SectionGuide';
import { SmartDefaultsBanner } from '../components/estimator/SmartDefaultsBanner';
import { HypercareScopePanel } from '../components/estimator/HypercareScopePanel';
import { DeliverablesEditorDialog } from '../components/estimator/DeliverablesEditorDialog';
import { CloneScopeSkeletonDialog } from '../components/estimator/CloneScopeSkeletonDialog';
import ReleaseAnchorPicker from '../components/estimator/ReleaseAnchorPicker';
import { SaveAsTemplateDialog } from '../components/estimator/SaveAsTemplateDialog';
import { SaveReviewDialog } from '../components/estimator/SaveReviewDialog';
import { RateDriftBanner } from '../components/estimator/RateDriftBanner';
import { EstimateLockControl } from '../components/estimator/EstimateLockControl';
import QuickStartGuide from '../components/QuickStartGuide';
import MathExplainer from '../components/MathExplainer';
import WorkspaceSubNav, { computeEstimateProgress } from '../components/WorkspaceSubNav';

// Extracted sub-components
import {
  READINESS_MODIFIERS, READINESS_LABELS, READINESS_VALUES,
  DATA_INTEGRITY_LABELS, DATA_INTEGRITY_VALUES,
  COMPLEXITY_MODIFIERS, COMPLEXITY_LABELS, COMPLEXITY_VALUES,
  IS_COMPLEXITY_SLIDER, OPTIMAL_MODIFIER_VALUES,
  GROUP_ICONS, GROUP_COLORS,
  COMPONENT_INFO, FACTOR_INFO,
  ROLE_LABELS, IMPLEMENTATION_TYPES,
  RATE_CARD_NAMES,
  PLANNER_OVERRIDE_GROUPS
} from './estimator/constants';
import { getGroupLabel } from '../lib/groupLabels';
import { ProgressIndicator } from './estimator/ProgressIndicator';
import { WizardStep1 } from './estimator/WizardStep1';
import { WizardStep2 } from './estimator/WizardStep2';
import { WizardStep3 } from './estimator/WizardStep3';
import { ResultsSidebar } from './estimator/ResultsSidebar';
import { ComponentRow } from './estimator/ComponentRow';
import EstimateVersionChip from '../components/estimator/EstimateVersionChip';
import EstimateStatusSelect from '../components/EstimateStatusSelect';
import { usePaletteCommands } from '../context/CommandPaletteContext';
import EditConditionsDialog from '../components/EditConditionsDialog';
import {
  ImplTypeInfoModal, ComplexityLevelInfoModal, HostingInfoModal,
  RangeInfoModal, ConfidenceInfoModal, ComplexityInfoModal,
  ComponentInfoModal, FactorInfoModal
} from './estimator/InfoModals';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_QUICK_PICKS = [
  { label: 'Integration Build', components: ['Design / Discovery', 'SSO', 'Other Integrations', 'Testing / Validation Support', 'Documentation'] },
  { label: 'Content Build', components: ['Design / Discovery', 'Content Configuration', 'Testing / Validation Support', 'Documentation'] },
  { label: 'Config Change', components: ['Design / Discovery', 'General Application Configuration', 'Testing / Validation Support', 'Documentation'] },
  { label: 'Reporting/Analytics', components: ['Design / Discovery', 'Reporting Configuration', 'Testing / Validation Support', 'Documentation'] },
];

function QuickPickManagerDialog({ open, onOpenChange, quickPicks, components, onChanged }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const grouped = useMemo(() => {
    const g = {};
    (components || []).forEach(c => { (g[c.group] = g[c.group] || []).push(c.name); });
    return g;
  }, [components]);

  const toggleComp = (name) => setEditing(prev => {
    const selected = prev.components.includes(name);
    const hours = { ...(prev.hours || {}) };
    if (selected) delete hours[name];
    return {
      ...prev,
      components: selected ? prev.components.filter(c => c !== name) : [...prev.components, name],
      hours,
    };
  });

  const save = async () => {
    if (!editing.label.trim()) { toast.error('Give the bundle a name'); return; }
    if (editing.components.length === 0) { toast.error('Pick at least one component'); return; }
    setSaving(true);
    try {
      const hours = {};
      Object.entries(editing.hours || {}).forEach(([n, v]) => {
        const num = Number(v);
        if (editing.components.includes(n) && num > 0) hours[n] = num;
      });
      const payload = {
        label: editing.label.trim(),
        components: editing.components,
        hours: Object.keys(hours).length ? hours : null,
      };
      if (editing.id) {
        await axios.put(`${API_URL}/api/project-quick-picks/${editing.id}`, payload);
      } else {
        await axios.post(`${API_URL}/api/project-quick-picks`, payload);
      }
      toast.success(`Quick pick "${payload.label}" saved`);
      setEditing(null);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save quick pick');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (qp) => {
    try {
      await axios.delete(`${API_URL}/api/project-quick-picks/${qp.id}`);
      toast.success(`Quick pick "${qp.label}" deleted`);
      if (editing?.id === qp.id) setEditing(null);
      onChanged();
    } catch {
      toast.error('Could not delete quick pick');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setEditing(null); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl" data-testid="quickpick-manager-dialog">
        <DialogHeader>
          <DialogTitle>Manage Project Quick Picks</DialogTitle>
          <DialogDescription>Bundles reps can add to a Project estimate in one click. Admin only.</DialogDescription>
        </DialogHeader>
        {!editing ? (
          <div className="space-y-2">
            {quickPicks.map(qp => (
              <div key={qp.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 py-2" data-testid={`quickpick-row-${qp.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#0F2040]">{qp.label}</div>
                  <div className="text-[11px] text-slate-500 truncate">
                    {qp.components.map(n => qp.hours?.[n] ? `${n} (${qp.hours[n]}h)` : n).join(' · ')}
                  </div>
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setEditing({ id: qp.id, label: qp.label, components: [...qp.components], hours: { ...(qp.hours || {}) } })}
                  data-testid={`quickpick-edit-${qp.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  onClick={() => remove(qp)}
                  data-testid={`quickpick-delete-${qp.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            ))}
            <Button variant="outline" className="w-full border-dashed text-sm"
              onClick={() => setEditing({ label: '', components: [], hours: {} })}
              data-testid="quickpick-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New quick pick
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Bundle name</Label>
              <Input value={editing.label} onChange={e => setEditing(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g. Data Migration" className="mt-1" data-testid="quickpick-label-input" />
            </div>
            <div>
              <Label className="text-xs">Components ({editing.components.length} selected)</Label>
              <div className="mt-1 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-3">
                {Object.entries(grouped).map(([group, names]) => (
                  <div key={group}>
                    <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{group}</div>
                    <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                      {names.map(name => (
                        <label key={name} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                          <input type="checkbox" checked={editing.components.includes(name)}
                            onChange={() => toggleComp(name)} className="accent-[#0F2040]"
                            data-testid={`quickpick-comp-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                          <span className="truncate">{name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {editing.components.length > 0 && (
              <div>
                <Label className="text-xs">Suggested hours (optional — blank keeps the component default)</Label>
                <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-1.5">
                  {editing.components.map(name => (
                    <div key={name} className="flex items-center gap-2">
                      <span className="flex-1 text-xs text-slate-700 truncate">{name}</span>
                      <input
                        type="number" min="1" max="10000"
                        value={editing.hours?.[name] ?? ''}
                        onChange={e => setEditing(prev => ({
                          ...prev,
                          hours: { ...(prev.hours || {}), [name]: e.target.value },
                        }))}
                        placeholder="default"
                        className="w-24 text-xs border border-slate-200 rounded-md px-2 py-1"
                        data-testid={`quickpick-hours-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      />
                      <span className="text-[10px] text-slate-400">hrs</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditing(null)} data-testid="quickpick-cancel-btn">Back</Button>
              <Button size="sm" onClick={save} disabled={saving}
                className="bg-[#0F2040] hover:bg-[#1a3156] text-white" data-testid="quickpick-save-btn">
                {saving ? 'Saving…' : 'Save quick pick'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function EstimatorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadedEstimateId, setLoadedEstimateId] = useState(null);
  // v260.38/v260.39/v260.40 — Highlight stepper. URL `?highlight=` may
  // include either bare component names ("Badge Configuration") or
  // explicit-kind prefixes ("comp::Badge Configuration", "int::sso").
  // Targets are parsed into {kind, name?|type?} objects so a single
  // chip can step through both components AND integrations added by
  // the Apply flow.
  const [highlightTargets, setHighlightTargets] = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [highlightDismissed, setHighlightDismissed] = useState(false);
  // v260.41 — Full Apply diff stashed by RequirementsFormPage's
  // Open Estimator button (sessionStorage, keyed by estimate id). Lets
  // the stepper's "Done" finale re-open the complete diff (deliverables
  // added, auto-flagged-from-narrative, etc.) without a roundtrip.
  const [applyDiff, setApplyDiff] = useState(null);
  const [applyDiffDialogOpen, setApplyDiffDialogOpen] = useState(false);
  // Edit-conditions sheet (only meaningful when hasConditions=true)
  const [editConditionsOpen, setEditConditionsOpen] = useState(false);
  
  // Wizard state
  const [step, setStep] = useState(1);
  const [stepDirection, setStepDirection] = useState('forward');
  const [animKey, setAnimKey] = useState(0);
  const [implementationType, setImplementationType] = useState('');
  const [projectKind, setProjectKind] = useState('customer');
  const projectKindTouchedRef = useRef(false);
  const [quickPicks, setQuickPicks] = useState([]);
  const [quickPickManagerOpen, setQuickPickManagerOpen] = useState(false);
  const [rndCategory, setRndCategory] = useState('Other');
  const [rndCategories, setRndCategories] = useState(['Platform', 'AI', 'Integrations', 'Tooling', 'Content', 'Other']);
  const [complexity, setComplexity] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Project details
  const [orgName, setOrgName] = useState('');
  const [implName, setImplName] = useState('');
  const [implDescription, setImplDescription] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [showProjectForm, setShowProjectForm] = useState(false);
  const [hostingModel, setHostingModel] = useState('public_cloud');
  // ── Project Conditions (Batch B) ─────────────────────────────
  // Populated from sessionStorage when user arrives via /estimator/new wizard.
  // When `hasConditions` is true, the saved estimate gets `has_conditions=true`
  // so the Planner becomes a read-only derived view (Batch C).
  const [pendingConditions, setPendingConditions] = useState(null);
  const [pendingSalesLead, setPendingSalesLead] = useState(null);
  const [pendingCSLead, setPendingCSLead] = useState(null);
  const [hasConditions, setHasConditions] = useState(false);
  // SOW drift summary for the Project Conditions chip — surfaces an
  // amber pulse when this estimate has SOWs whose dates diverge from
  // the saved Conditions, so PMs notice before saving more changes.
  const [sowDriftSummary, setSowDriftSummary] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [orgMode, setOrgMode] = useState('select');
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgIndustry, setNewOrgIndustry] = useState('');
  const [newOrgCountry, setNewOrgCountry] = useState('');
  const [newOrgContactEmail, setNewOrgContactEmail] = useState('');
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [fuzzyLoading, setFuzzyLoading] = useState(false);
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [orgLookups, setOrgLookups] = useState({ industries: [], countries: [] });
  const [narrative, setNarrative] = useState(null);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [narrativeExpanded, setNarrativeExpanded] = useState(false);
  const [docComponent, setDocComponent] = useState(null);
  
  // Data state
  const [components, setComponents] = useState([]);
  const [templates, setTemplates] = useState({});
  const [scopeTemplates, setScopeTemplates] = useState([]);
  const [selectedComponents, setSelectedComponents] = useState({});
  const [modifiers, setModifiers] = useState({
    governance: 1.0,
    content_readiness: 1.0,
    integration_complexity: 1.0,
    org_complexity: 1.0,
    timeline_pressure: 1.0,
    client_capability: 1.0,
    change_readiness: 1.0,
    data_integrity: 1.0,
    pm_meetings_per_week: 2,
    pm_fte_per_meeting: 4,
    project_contingency_pct: 0,
    apply_timeline_stretch_tax: true,
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const skipAutoCalcRef = useRef(false);
  const [saving, setSaving] = useState(false);
  // Pre-save review — last committed inputs/results and the pending review.
  const lastSavedRef = useRef(null);
  const [saveReview, setSaveReview] = useState(null);
  const [rateDrift, setRateDrift] = useState(null);
  const [effortCostLock, setEffortCostLock] = useState(null);
  const [estimateName, setEstimateName] = useState('');
  // v258.1 — Requirements doc summary used to render the header drift
  // chip next to the Requirements button. Holds only the timestamps we
  // need (updated_at, last_applied_at) — the full doc is fetched lazily
  // by the RequirementsFormPage when the user opens it.
  const [requirementsDocMeta, setRequirementsDocMeta] = useState(null);

  // v258.3 — Public Cloud release calendar consumed by the
  // ReleaseAnchorPicker on each New Dev / Integration row. Fetched once
  // per Estimator mount; release shape is `{id, release_date, label}`.
  const [publicCloudReleases, setPublicCloudReleases] = useState([]);
  // Inline-rename UX in the workspace header: when active, the h1 swaps to
  // an editable input. Commit on Enter / blur, cancel on Escape. The save
  // bar input is gone now that this surface owns rename.
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [savedInfo, setSavedInfo] = useState(null);
  const [hourOverrides, setHourOverrides] = useState({});
  const [plannerStartOverrides, setPlannerStartOverrides] = useState({}); // {componentName: int} for Data Migration start-week overrides
  // v258.6 — per-component release anchors keyed by component name.
  // Shape: { [componentName]: { release_id, lead_weeks } }
  // Drives the ReleaseAnchorPicker on each Integration-group row in
  // the ComponentRow so the UI can re-display the anchor after reload
  // (the actual schedule is held by plannerStart/WeeksOverrides as
  // before — these IDs are purely for UI re-display).
  const [plannerReleaseAnchors, setPlannerReleaseAnchors] = useState({});
  const [plannerWeeksOverrides, setPlannerWeeksOverrides] = useState({}); // {componentName: int} for Data Migration / Integrations duration overrides
  // Block-schedule overrides (paused-and-resumed components):
  // { [componentName]: [{start_week:int, weeks:int}, {start_week:int, weeks:int}?] }
  // When ≥1 block is set for a component, takes precedence over the legacy
  // planner_start_week / planner_weeks overrides on the backend.
  const [plannerBlocksOverrides, setPlannerBlocksOverrides] = useState({});
  // Per-component deliverables: { [componentName]: [{id, text, is_milestone}] }.
  // Persists with the estimate save; flows into SOW Section 3.3
  // (Component-Level Deliverables) and milestone-flagged bullets get
  // auto-promoted into the SOW Milestones table (Section 6.2 / 7.3).
  const [componentDeliverables, setComponentDeliverables] = useState({});
  const [deliverablesEditorFor, setDeliverablesEditorFor] = useState(null);
  const [cloneSkeletonOpen, setCloneSkeletonOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [rateCardOverrides, setRateCardOverrides] = useState({});
  // Per-estimate blanket discount (Batch D). Range: -50 to +50.
  // Positive = discount; Negative = surcharge. Applied AFTER rateCardOverrides.
  const [rateCardBlanketDiscountPct, setRateCardBlanketDiscountPct] = useState(0);
  const [rateCardBlanketDiscountReason, setRateCardBlanketDiscountReason] = useState('');
  // The reason string is audit metadata only — it must NOT trigger
  // recalculation when typed. We mirror it into a ref so
  // `calculateEstimate` and `saveEstimate` always read the latest
  // value from the closure without needing it as a hook dependency.
  // This replaces the v217 eslint-disable on `calculateEstimate`.
  const rateCardBlanketDiscountReasonRef = useRef(rateCardBlanketDiscountReason);
  useEffect(() => {
    rateCardBlanketDiscountReasonRef.current = rateCardBlanketDiscountReason;
  }, [rateCardBlanketDiscountReason]);
  // Derived: whether the user must still provide a justification before save.
  // Save buttons are disabled while this is true so we never persist a
  // non-zero discount/surcharge without an audit-trail reason.
  const _discountReasonMissing = rateCardBlanketDiscountPct !== 0
    && (rateCardBlanketDiscountReason || '').trim().length < 10;
  // Cached aggregate of last-50 estimates (powers the pricing-impact preview).
  // Loaded for super-admins only; null otherwise.
  const [pricingBaseline, setPricingBaseline] = useState(null);
  // Rounding starts from the user's saved default, then may be overridden
  // per-estimate on the Project Factors step. When an existing estimate is
  // loaded we replace this with the stored estimate.rounding.
  const _userDefaultRounding = user?.preferences?.default_rounding || {};
  const [rounding, setRounding] = useState({
    hours_increment: Number(_userDefaultRounding.hours_increment || 0),
    cost_increment: Number(_userDefaultRounding.cost_increment || 0),
  });
  // Currency override for this estimate. Resolution cascade (lowest→highest):
  //   allowed currencies default → user pref → org default → estimate override
  // The estimator keeps a single `currencyCode` in state; the effective
  // resolution is done client-side against the loaded lists.
  const [currencyCode, setCurrencyCode] = useState(null);
  const [taxInclusive, setTaxInclusive] = useState(false);
  const [preferredStartDate, setPreferredStartDate] = useState(''); // ISO 'YYYY-MM-DD' — stakeholders' desired project start
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  // v376 — Seertech entity ownership: drives currency inheritance and the
  // regional rate card applied by the calculator.
  const [seertechEntities, setSeertechEntities] = useState([]);
  const [contractingEntityId, setContractingEntityId] = useState(null);
  const [region, setRegion] = useState(null);
  const [regionalRateCards, setRegionalRateCards] = useState({});
  // v377 — live FX effective rates (1 AUD = X CCY), the estimate's saved
  // snapshot, and leadership-only internal cost rates (AUD) for margin.
  const [fxEffective, setFxEffective] = useState(null);
  const [fxSnapshot, setFxSnapshot] = useState(null); // {rate, date, code}
  const [costRates, setCostRates] = useState(null);
  const [minMarginPct, setMinMarginPct] = useState(null);
  const selectedEntity = useMemo(
    () => seertechEntities.find(e => e.id === contractingEntityId) || null,
    [seertechEntities, contractingEntityId]);

  // Resolve effective currency via cascade: estimate override → entity default →
  // org default → user preference → admin default → USD. Pure derivation, no state.
  const effectiveCurrency = useMemo(() => {
    const lookup = (code) => {
      if (!code) return null;
      const needle = String(code).toUpperCase();
      return availableCurrencies.find(c => (c.code || '').toUpperCase() === needle) || null;
    };
    const selectedOrg = organizations.find(o => o.org_id === selectedOrgId);
    return (
      lookup(currencyCode)
      || lookup(selectedEntity?.default_currency_code)
      || lookup(selectedOrg?.default_currency_code)
      || lookup(user?.preferences?.default_currency_code)
      || availableCurrencies.find(c => c.is_default)
      || availableCurrencies[0]
      || { code: 'USD', symbol: '$', name: 'US Dollar' }
    );
  }, [currencyCode, organizations, selectedOrgId, user, availableCurrencies, selectedEntity]);
  const currencySymbol = effectiveCurrency.symbol || '$';

  // Default the entity from the user's saved preference for new estimates.
  useEffect(() => {
    const pref = user?.preferences?.default_contracting_entity_id;
    if (pref) setContractingEntityId(prev => prev ?? pref);
  }, [user]);

  // Currency picker options — when the owning entity restricts available
  // currencies, honor that list (always keeping the effective one visible).
  const entityFilteredCurrencies = useMemo(() => {
    const avail = selectedEntity?.available_currencies || [];
    if (!avail.length) return availableCurrencies;
    const allowed = new Set(avail.map(c => String(c).toUpperCase()));
    if (effectiveCurrency?.code) allowed.add(String(effectiveCurrency.code).toUpperCase());
    const filtered = availableCurrencies.filter(c => allowed.has((c.code || '').toUpperCase()));
    return filtered.length ? filtered : availableCurrencies;
  }, [availableCurrencies, selectedEntity, effectiveCurrency]);

  // v377 — AUD conversion rate for the effective currency: prefer the saved
  // estimate's snapshot (audit-stable), fall back to live effective rates.
  const audRateInfo = useMemo(() => {
    const code = (effectiveCurrency.code || 'USD').toUpperCase();
    if (code === 'AUD') return { rate: 1, date: null, live: false };
    if (fxSnapshot?.rate && (fxSnapshot.code || '').toUpperCase() === code) {
      return { rate: fxSnapshot.rate, date: fxSnapshot.date, live: false };
    }
    const per = fxEffective?.[code];
    return per ? { rate: 1 / Number(per), date: null, live: true } : null;
  }, [effectiveCurrency, fxSnapshot, fxEffective]);

  // v377 — leadership-only margin (AUD): sell (converted) − internal cost.
  const marginInfo = useMemo(() => {
    if (!costRates || !results?.effort_by_role || !audRateInfo) return null;
    const sellAud = (results.total_cost || 0) * audRateInfo.rate;
    let costAud = 0;
    Object.entries(results.effort_by_role).forEach(([role, hrs]) => {
      const r = parseFloat(costRates[role]);
      if (Number.isFinite(r)) costAud += (hrs || 0) * r;
    });
    if (sellAud <= 0) return null;
    const marginAud = sellAud - costAud;
    const marginPct = (marginAud / sellAud) * 100;
    return {
      sellAud, costAud, marginAud, marginPct,
      minMarginPct,
      belowMin: minMarginPct != null && marginPct < minMarginPct,
    };
  }, [costRates, results, audRateInfo, minMarginPct]);
  const [breakdownView, setBreakdownView] = useState('hours');
  const [showConfidenceInfo, setShowConfidenceInfo] = useState(false);
  const [showComplexityInfo, setShowComplexityInfo] = useState(false);
  const [showRangeInfo, setShowRangeInfo] = useState(false);
  const [showHostingInfo, setShowHostingInfo] = useState(false);
  const [showTypeInfo, setShowTypeInfo] = useState(null);
  const [showComplexityLevelInfo, setShowComplexityLevelInfo] = useState(null);
  const [showComponentInfo, setShowComponentInfo] = useState(null);
  const [showFactorInfo, setShowFactorInfo] = useState(null);
  const [editingHour, setEditingHour] = useState(null);
  const [activeTab, setActiveTab] = useState('');
  const [visitedTabs, setVisitedTabs] = useState(new Set());
  const [rateCard, setRateCard] = useState({});
  const [volumeMetrics, setVolumeMetrics] = useState([]);
  const [volumeOverrides, setVolumeOverrides] = useState({});
  const [hypercareScope, setHypercareScope] = useState(null);  // null = use preset; {weeks,fte,hours_per_day,days_per_week} = custom
  const [guideResetKey, setGuideResetKey] = useState(0);
  const [demoMode, setDemoMode] = useState(false);
  const [newDevelopments, setNewDevelopments] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [savedEstimateId, setSavedEstimateId] = useState(null);
  const pendingProductPricingRef = useRef(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveGateModal, setShowSaveGateModal] = useState(null); // destination path or null
  const [justSaved, setJustSaved] = useState(false);

  // ── Data Fetching ──────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [compRes, tempRes, settingsRes, volRes] = await Promise.all([
          axios.get(`${API_URL}/api/components`),
          axios.get(`${API_URL}/api/templates`),
          axios.get(`${API_URL}/api/settings`),
          axios.get(`${API_URL}/api/settings/volume-metrics`, { withCredentials: true }).catch(() => ({ data: { metrics: [] } }))
        ]);
        setComponents(compRes.data.components);
        setTemplates(tempRes.data.templates);
        if (settingsRes.data?.rate_card) setRateCard(settingsRes.data.rate_card);
        setVolumeMetrics(volRes.data.metrics || []);
      } catch (error) {
        logger.error('Error fetching data:', error);
      }
    };
    fetchData();
    // Fetch user-saved scope templates (separate from static /api/templates).
    // These are workspace-wide and powered by promotions from past estimates.
    const fetchScopeTemplates = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/scope-templates`, { withCredentials: true });
        setScopeTemplates(res.data?.templates || []);
      } catch { /* not signed in or empty */ }
    };
    fetchScopeTemplates();
    const fetchOrgs = async () => {
      try {
        const [orgRes, lookupRes] = await Promise.all([
          axios.get(`${API_URL}/api/organizations`, { withCredentials: true }),
          axios.get(`${API_URL}/api/organizations/lookups`, { withCredentials: true }),
        ]);
        setOrganizations(orgRes.data.organizations || []);
        setOrgLookups(lookupRes.data || { industries: [], countries: [] });
      } catch { /* non-admin users can't fetch orgs */ }
    };
    fetchOrgs();

    // Load the admin-curated list of currencies for the per-estimate picker.
    axios
      .get(`${API_URL}/api/settings/currencies`, { withCredentials: true })
      .then(res => setAvailableCurrencies(res.data?.currencies || []))
      .catch(() => setAvailableCurrencies([]));

    // v376 — Seertech entities (currency defaults) + regional rate cards
    // (default-rate display in the Rate Card Override grid).
    axios
      .get(`${API_URL}/api/settings/seertech-entities`, { withCredentials: true })
      .then(res => setSeertechEntities(res.data?.entities || []))
      .catch(() => setSeertechEntities([]));
    axios
      .get(`${API_URL}/api/settings/regional-rate-cards`, { withCredentials: true })
      .then(res => setRegionalRateCards(res.data?.cards || {}))
      .catch(() => setRegionalRateCards({}));
    axios
      .get(`${API_URL}/api/fx/rates`, { withCredentials: true })
      .then(res => setFxEffective(res.data?.effective || null))
      .catch(() => setFxEffective(null));
    // Leadership-only (403 for reps/viewers → margin stays hidden).
    axios
      .get(`${API_URL}/api/settings/cost-rate-card`, { withCredentials: true })
      .then(res => {
        setCostRates(res.data?.rates && Object.keys(res.data.rates).length ? res.data.rates : null);
        setMinMarginPct(res.data?.min_margin_pct ?? null);
      })
      .catch(() => setCostRates(null));

    // v258.3 — Public Cloud release calendar feeds the per-row release
    // anchor picker on New Developments / Integrations. Normalize the
    // settings event shape into the picker's expected contract
    // ({id, release_date, label}).
    axios
      .get(`${API_URL}/api/settings/public-cloud-releases`, { withCredentials: true })
      .then(res => {
        const events = res.data?.events || [];
        const labelFor = (ev) => {
          const rt = (ev.release_type || '').toLowerCase();
          const suffix = rt === 'test' ? 'Test' : rt === 'prod' ? 'Prod' : rt === 'maintenance' ? 'Maintenance' : '';
          return ev.label && ev.label.trim()
            ? ev.label
            : `${ev.quarter || 'Release'}${suffix ? ' ' + suffix : ''}`;
        };
        setPublicCloudReleases(events.map((ev) => ({
          id: ev.id, release_date: ev.date, label: labelFor(ev),
        })));
      })
      .catch(() => setPublicCloudReleases([]));
  }, []);

  // ── Pre-select organization from ?org_id= query param ────────
  // Triggered by the ⌘K palette's "New estimate for…" action so the
  // user lands on the Estimator with their target org already chosen.
  useEffect(() => {
    const orgId = searchParams.get('org_id');
    if (!orgId || organizations.length === 0) return;
    const match = organizations.find((o) => o.org_id === orgId);
    if (match) setSelectedOrgId(orgId);
  }, [searchParams, organizations]);

  // ── Wizard hand-off (Batch B) ───────────────────────────────
  // When the user comes from /estimator/new with ?from_wizard=1, hydrate
  // org/project/conditions from sessionStorage exactly once, then clear it.
  useEffect(() => {
    if (searchParams.get('from_wizard') !== '1') return;
    let stash = null;
    try {
      const raw = sessionStorage.getItem('pendingNewEstimateWizardV1');
      if (raw) {
        stash = JSON.parse(raw);
        sessionStorage.removeItem('pendingNewEstimateWizardV1');
      }
    } catch (e) { /* noop */ }
    if (!stash) return;

    if (stash.organization_name) setOrgName(stash.organization_name);
    if (stash.implementation_name) setImplName(stash.implementation_name);
    if (stash.implementation_description) setImplDescription(stash.implementation_description);
    if (stash.target_audience != null) setTargetAudience(String(stash.target_audience));
    if (stash.hosting_model) setHostingModel(stash.hosting_model);
    if (stash.org_id) setSelectedOrgId(stash.org_id);
    if (stash.project_conditions) setPendingConditions(stash.project_conditions);
    if (stash.has_conditions) setHasConditions(true);
    if (stash.sales_lead) setPendingSalesLead(stash.sales_lead);
    if (stash.cs_lead) setPendingCSLead(stash.cs_lead);
    if (stash.contracting_entity_id) setContractingEntityId(stash.contracting_entity_id);
    if (stash.region) setRegion(stash.region);
    if (stash.product_pricing_inputs) pendingProductPricingRef.current = stash.product_pricing_inputs;
    setShowProjectForm(true);
    // Drop the from_wizard query so refresh doesn't re-trigger
    const next = new URLSearchParams(searchParams);
    next.delete('from_wizard');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load saved estimate from ?load= query param ──────────
  useEffect(() => {
    const loadId = searchParams.get('load');
    if (!loadId || components.length === 0 || loadedEstimateId === loadId) return;

    const loadEstimate = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/estimates/${loadId}`, { withCredentials: true });
        const est = res.data;

        setLoadedEstimateId(loadId);
        setSavedEstimateId(loadId);
        baselineFingerprintRef.current = null; // re-baseline after this load settles
        setEstimateName(est.estimate_name || '');
        setSavedInfo(est.estimate_name ? {
          name: est.estimate_name,
          time: est.created_at ? new Date(est.created_at) : new Date(),
          version: est.version_number ?? null,
          status: est.workflow_status || est.approval_status || 'draft',
          status_override_by: est.workflow_status_override_by_name || null,
          status_override_at: est.workflow_status_override_at || null,
        } : null);
        const rd = est.requirements_doc || null;
        setRequirementsDocMeta(rd ? {
          updated_at: rd.updated_at || null,
          last_applied_at: rd.last_applied_at || null,
          present: !!(rd.updated_at
            || (rd.maturity_assessment && rd.maturity_assessment.length)
            || (rd.functional_requirements && rd.functional_requirements.length)
            || (rd.integrations && rd.integrations.length)
            || (rd.timeline_targets && Object.keys(rd.timeline_targets).length)),
        } : null);
        setOrgName(est.organization_name || '');
        setImplName(est.implementation_name || '');
        setImplDescription(est.implementation_description || '');
        setTargetAudience(est.target_audience ? String(est.target_audience) : '');
        setHostingModel(est.hosting_model || 'public_cloud');
        if (est.has_conditions && est.project_conditions) {
          setHasConditions(true);
          setPendingConditions(est.project_conditions);
        } else {
          setHasConditions(false);
          setPendingConditions(null);
        }
        setImplementationType(est.implementation_type || '');
        projectKindTouchedRef.current = true;
        setProjectKind(est.project_kind || 'customer');
        setRndCategory(est.rnd_category || 'Other');
        setComplexity(est.complexity || 'standard');

        const restored = {};
        const restoredHourOverrides = {};
        const restoredVolumeOverrides = {};
        const restoredStartOverrides = {};
        const restoredWeeksOverrides = {};
        const restoredBlocksOverrides = {};
        const restoredDeliverables = {};
        const restoredAnchors = {};
        (est.selected_components || []).forEach(c => {
          restored[c.component_name] = c.quantity || 1;
          if (c.hour_override) restoredHourOverrides[c.component_name] = c.hour_override;
          if (c.use_volume && c.volume_entries) {
            restoredVolumeOverrides[c.component_name] = { enabled: true, entries: c.volume_entries };
          }
          if (c.component_name === 'Hypercare Support' && c.hypercare_scope) {
            setHypercareScope(c.hypercare_scope);
          }
          if (c.planner_start_week) restoredStartOverrides[c.component_name] = c.planner_start_week;
          if (c.planner_weeks) restoredWeeksOverrides[c.component_name] = c.planner_weeks;
          if (Array.isArray(c.planner_blocks) && c.planner_blocks.length > 0) {
            restoredBlocksOverrides[c.component_name] = c.planner_blocks;
          }
          if (Array.isArray(c.deliverables) && c.deliverables.length > 0) {
            restoredDeliverables[c.component_name] = c.deliverables;
          }
          // v258.6 — restore per-component release anchors so the picker
          // re-displays the chosen release on reload.
          if (c.anchor_release_id || c.anchor_lead_weeks) {
            restoredAnchors[c.component_name] = {
              release_id: c.anchor_release_id || null,
              lead_weeks: c.anchor_lead_weeks || null,
            };
          }
        });
        setSelectedComponents(restored);
        setHourOverrides(restoredHourOverrides);
        setVolumeOverrides(restoredVolumeOverrides);
        setComponentDeliverables(restoredDeliverables);
        setPlannerStartOverrides(restoredStartOverrides);
        setPlannerWeeksOverrides(restoredWeeksOverrides);
        setPlannerBlocksOverrides(restoredBlocksOverrides);
        setPlannerReleaseAnchors(restoredAnchors);

        if (est.rate_card_override && Object.keys(est.rate_card_override).length > 0) {
          setRateCardOverrides(est.rate_card_override);
        }
        if (typeof est.rate_card_blanket_discount_pct === 'number') {
          setRateCardBlanketDiscountPct(est.rate_card_blanket_discount_pct);
        }
        if (typeof est.rate_card_blanket_discount_reason === 'string') {
          setRateCardBlanketDiscountReason(est.rate_card_blanket_discount_reason);
        }
        if (est.rounding) {
          setRounding({
            hours_increment: Number(est.rounding.hours_increment || 0),
            cost_increment: Number(est.rounding.cost_increment || 0),
          });
        }
        if (est.currency_code) setCurrencyCode(est.currency_code);
        if (est.contracting_entity_id) setContractingEntityId(est.contracting_entity_id);
        if (est.region) setRegion(est.region);
        if (est.fx_rate_to_aud) setFxSnapshot({ rate: est.fx_rate_to_aud, date: est.fx_rate_date, code: est.currency_code });
        if (typeof est.tax_inclusive === 'boolean') setTaxInclusive(est.tax_inclusive);
        if (est.preferred_start_date) setPreferredStartDate(est.preferred_start_date);
        if (est.modifiers) setModifiers(prev => ({ ...prev, ...est.modifiers }));
        setNewDevelopments((est.new_developments || []).map(d => ({
          ...d,
          _key: d._key || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`),
        })));
        setIntegrations(est.integrations || []);

        if (est.results) {
          setResults(est.results);
          skipAutoCalcRef.current = true;
        }
        lastSavedRef.current = {
          results: est.results || null,
          inputs: null,
          fromEst: {
            contractingEntityId: est.contracting_entity_id || null,
            region: est.region || null,
            currencyCode: est.currency_code || null,
          },
        };
        setRateDrift(null);

        setStepDirection('forward');
        setAnimKey(prev => prev + 1);
        setStep(4);
        // v260.38/v260.39/v260.40 — capture highlight entries BEFORE
        // clearing search params. Each entry may be:
        //   "comp::<Component Name>"  → component highlight
        //   "int::<integration_type>" → integration highlight
        //   "<bare name>"             → legacy: treated as component
        // We filter against the in-memory components catalogue / known
        // integration types so the chip count never includes ghosts.
        const hi = searchParams.get('highlight') || '';
        if (hi) {
          const KNOWN_INTEGRATION_TYPES = new Set([
            'sso','hris','authoring_tool','user_service','virtual_class',
            'content_library','payment_gateway','order_management',
            'tax_service','caas','badging','custom',
          ]);
          const validCompNames = new Set(components.map((c) => c.name));
          const targets = [];
          for (const raw of hi.split(',')) {
            const entry = decodeURIComponent((raw || '').trim());
            if (!entry) continue;
            if (entry.startsWith('int::')) {
              const t = entry.slice(5).trim();
              if (KNOWN_INTEGRATION_TYPES.has(t)) targets.push({ kind: 'integration', type: t });
              continue;
            }
            const name = entry.startsWith('comp::') ? entry.slice(6).trim() : entry;
            if (validCompNames.has(name)) targets.push({ kind: 'component', name });
          }
          if (targets.length > 0) {
            setHighlightTargets(targets);
            setHighlightIdx(0);
            setHighlightDismissed(false);
          }
          // v260.41 — pick up the stashed Apply diff (if the caller is
          // RequirementsFormPage). Used by the stepper's Done finale.
          try {
            const stashKey = `seer:lastApplyDiff:${loadId}`;
            const raw = sessionStorage.getItem(stashKey);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && parsed.diff) setApplyDiff(parsed);
              // One-shot: clear after read so reloads of the page don't
              // re-surface a stale diff.
              sessionStorage.removeItem(stashKey);
            }
          } catch (_) { /* swallow */ }
        }
        // v260.43 — Server-persisted fallback. Whether or not the URL
        // carries ?highlight=, fetch `last_apply_diff` so the chip's
        // Done finale + "Last apply" floating pill can resurface
        // across page refreshes AND for teammates opening the same
        // estimate. Only sets state when the in-memory `applyDiff` is
        // still null (sessionStorage stash already wins above).
        try {
          const r = await axios.get(`${API_URL}/api/estimates/${loadId}/requirements/last-apply-diff`, { withCredentials: true });
          const rec = r?.data?.last_apply_diff || null;
          if (rec && rec.diff) setApplyDiff((cur) => cur || rec);
        } catch (_) { /* swallow */ }
        setSearchParams({}, { replace: true });
      } catch (error) {
        logger.error('Failed to load estimate:', error);
      }
    };
    loadEstimate();
  }, [searchParams, components, loadedEstimateId, setSearchParams]);

  // ── Calculate estimate ──────────────────────────────────
  const loadQuickPicks = useCallback(() => {
    axios.get(`${API_URL}/api/project-quick-picks`)
      .then(res => setQuickPicks(res.data.quick_picks || []))
      .catch(() => {});
    axios.get(`${API_URL}/api/rnd-categories`)
      .then(res => setRndCategories(res.data.categories || []))
      .catch(() => {});
  }, []);
  useEffect(() => { if (implementationType === 'Project') loadQuickPicks(); }, [implementationType, loadQuickPicks]);

  const calculateEstimate = useCallback(async (opts = {}) => {
    const probe = opts && opts.probe === true;
    const selected = Object.entries(selectedComponents)
      .filter(([, qty]) => qty > 0)
      .map(([name, quantity]) => {
        const vo = volumeOverrides[name];
        const hasVolume = vo && vo.enabled && vo.entries && Object.values(vo.entries).some(v => v > 0);
        return {
          component_name: name, quantity,
          hour_override: hourOverrides[name] || null,
          use_volume: hasVolume || false,
          volume_entries: hasVolume ? vo.entries : null,
          hypercare_scope: name === 'Hypercare Support' ? (hypercareScope || null) : null,
          planner_start_week: plannerStartOverrides[name] || null,
          planner_weeks: plannerWeeksOverrides[name] || null,
          planner_blocks: (Array.isArray(plannerBlocksOverrides[name]) && plannerBlocksOverrides[name].length > 0) ? plannerBlocksOverrides[name] : null,
          deliverables: componentDeliverables[name] || [],
        };
      });
    
    if (selected.length === 0) { if (!probe) setResults(null); return; }

    if (!probe) setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/calculate`, {
        implementation_type: implementationType, complexity, mode: 'simple',
        selected_components: selected, modifiers,
        rate_card_override: Object.keys(rateCardOverrides).length > 0 ? rateCardOverrides : null,
        rate_card_blanket_discount_pct: rateCardBlanketDiscountPct || null,
        contracting_entity_id: contractingEntityId || null,
        region: region || null,
        // Read latest justification via ref so this callback's identity
        // doesn't churn when the user is typing in the reason field.
        rate_card_blanket_discount_reason: rateCardBlanketDiscountPct !== 0
          ? (rateCardBlanketDiscountReasonRef.current || '').trim() || null
          : null,
        hosting_model: hostingModel,
        new_developments: newDevelopments.length > 0 ? newDevelopments : null,
        integrations: integrations.length > 0 ? integrations : null,
        // Project Conditions feed the planner's `_calculate_dynamic_weeks`
        // floor + break extension + explicit go-live anchor. Without these,
        // the live `/api/calculate` returns effort-only `projected_weeks`
        // (e.g. 30w) while the saved planner correctly reports the conditions-
        // aware total (e.g. 72w). Footer pill / Quote PDF / Insights PDF all
        // read this value, so the gap was visible across every surface.
        project_conditions: hasConditions ? pendingConditions : null,
        has_conditions: hasConditions,
      });
      if (probe) {
        // Drift probe: compare today's pricing of the SAVED inputs against the saved results.
        const saved = lastSavedRef.current?.results;
        const live = response.data;
        if (saved && live) {
          const costDiff = (live.total_cost || 0) - (saved.total_cost || 0);
          const effortDiff = (live.total_effort || 0) - (saved.total_effort || 0);
          setRateDrift((Math.abs(costDiff) >= 0.5 || Math.abs(effortDiff) >= 0.05)
            ? { savedCost: saved.total_cost || 0, liveCost: live.total_cost || 0, savedEffort: saved.total_effort || 0, liveEffort: live.total_effort || 0, live }
            : null);
        }
        return;
      }
      setResults(response.data);
    } catch (error) {
      logger.error('Calculation error:', error);
    } finally { if (!probe) setLoading(false); }
  }, [selectedComponents, implementationType, complexity, modifiers, hourOverrides, rateCardOverrides, rateCardBlanketDiscountPct, hostingModel, volumeOverrides, newDevelopments, integrations, hypercareScope, plannerStartOverrides, plannerWeeksOverrides, plannerBlocksOverrides, componentDeliverables, hasConditions, pendingConditions, contractingEntityId, region]);

  // Auto-calculate — `calculateEstimate` already memoizes against all
  // relevant inputs, so its identity change is the single source of
  // truth for "something the calc cares about changed". Re-listing the
  // individual deps here was redundant and prone to drift.
  useEffect(() => {
    if (step === 4 && Object.keys(selectedComponents).length > 0) {
      if (skipAutoCalcRef.current) {
        // Saved results are being displayed verbatim — probe today's rates for drift instead.
        skipAutoCalcRef.current = false;
        const probeTimer = setTimeout(() => calculateEstimate({ probe: true }), 300);
        return () => clearTimeout(probeTimer);
      }
      const timer = setTimeout(calculateEstimate, 300);
      return () => clearTimeout(timer);
    }
  }, [step, calculateEstimate, selectedComponents]);

  // Fingerprint of every mutable, persistable estimate field. Memoizing
  // it lets the unsaved-changes effect depend on a single value instead
  // of a 12-deep dep array — and the next time someone adds a saveable
  // field they only have to extend this memo.
  // NOTE: `rateCardBlanketDiscountReason` is included so that editing the
  // audit reason on an existing non-zero discount also flips the dirty
  // flag (the value still must NOT trigger a recalc — that's why it's
  // a ref in `calculateEstimate`, not a hook dep).
  const _estimateDirtyFingerprint = useMemo(() => JSON.stringify({
    selectedComponents, modifiers, hourOverrides, rateCardOverrides,
    rateCardBlanketDiscountPct, rateCardBlanketDiscountReason,
    hostingModel, newDevelopments,
    integrations, hypercareScope, preferredStartDate,
  }), [selectedComponents, modifiers, hourOverrides, rateCardOverrides, rateCardBlanketDiscountPct, rateCardBlanketDiscountReason, hostingModel, newDevelopments, integrations, hypercareScope, preferredStartDate]);

  // Track unsaved changes against a baseline fingerprint. The first
  // settle after load/calc records the baseline instead of flagging
  // dirty — so simply opening a saved estimate never triggers the
  // save gate; only a real field change does (v276 fix).
  const baselineFingerprintRef = useRef(null);
  const snapshotInputs = () => ({
    selectedComponents, modifiers, hourOverrides, volumeOverrides, rateCardOverrides,
    rateCardBlanketDiscountPct, rateCardBlanketDiscountReason, hostingModel, newDevelopments,
    integrations, hypercareScope, preferredStartDate, contractingEntityId, region, currencyCode,
    taxInclusive, rounding, complexity, implementationType,
  });
  useEffect(() => {
    if (step === 4 && results) {
      if (baselineFingerprintRef.current === null) {
        baselineFingerprintRef.current = _estimateDirtyFingerprint;
        if (lastSavedRef.current && !lastSavedRef.current.inputs) {
          lastSavedRef.current.inputs = { ...snapshotInputs(), ...(lastSavedRef.current.fromEst || {}) };
        }
        return;
      }
      if (_estimateDirtyFingerprint === baselineFingerprintRef.current) {
        setHasUnsavedChanges(false);
        return;
      }
      setHasUnsavedChanges(true);
      setJustSaved(false);
    }
  }, [_estimateDirtyFingerprint, step, results]);


  // ── Baseline & Template ──────────────────────────────────
  useEffect(() => {
    if (implementationType === 'Project' && orgName && !projectKindTouchedRef.current) {
      setProjectKind(orgName.toLowerCase().includes('seertech') ? 'rnd' : 'customer');
    }
  }, [implementationType, orgName]);

  const applyBaseline = useCallback(() => {
    // Baseline = all required + recommended components (both pre-selected on fresh estimate).
    // 'Project' estimates are à la carte — nothing pre-selected, everything selectable.
    if (implementationType === 'Project') { setSelectedComponents({}); return; }
    const baseline = components.filter(c => c.required || c.recommended);
    const newSelected = {};
    baseline.forEach(comp => { newSelected[comp.name] = 1; });
    setSelectedComponents(newSelected);
  }, [components, implementationType]);

  const applyTemplate = (templateKey) => {
    const template = templates[templateKey];
    if (!template) return;
    if (!orgName.trim() || !implName.trim()) {
      setPendingTemplate(templateKey);
      setImplName(template.name || '');
      setShowProjectForm(true);
      return;
    }
    setSelectedTemplate(templateKey);
    setImplementationType(template.implementation_type);
    setComplexity('standard');
    setModifiers(prev => ({ ...prev, ...template.default_modifiers }));
    const newSelected = {};
    template.default_components.forEach(comp => { newSelected[comp.component_name] = comp.quantity; });
    setSelectedComponents(newSelected);
    setStepDirection('forward');
    setAnimKey(prev => prev + 1);
    setStep(4);
  };

  // Apply a user-saved Scope Template (from /api/scope-templates). Unlike the
  // static templates, these carry per-component milestone deliverables and
  // optionally the original modifier set. Customer fields are not part of the
  // template — the user still has to supply the org/impl name on Step 1.
  const applyScopeTemplate = (tpl) => {
    if (!tpl) return;
    if (!orgName.trim() || !implName.trim()) {
      // Stash for later application after the project form is filled in.
      setPendingTemplate({ __scopeTemplate: tpl });
      setImplName(tpl.name || '');
      setShowProjectForm(true);
      return;
    }
    setSelectedTemplate(`scope:${tpl.id}`);
    if (tpl.implementation_type) setImplementationType(tpl.implementation_type);
    if (tpl.complexity) setComplexity(tpl.complexity);
    if (tpl.modifiers) setModifiers(prev => ({ ...prev, ...tpl.modifiers }));
    const newSelected = {};
    const newDeliverables = {};
    (tpl.components || []).forEach(c => {
      newSelected[c.component_name] = c.quantity || 1;
      const bullets = (c.deliverables || [])
        .map(b => ({ id: b.id, text: b.text, is_milestone: !!b.is_milestone }))
        .filter(b => (b.text || '').trim());
      if (bullets.length > 0) newDeliverables[c.component_name] = bullets;
    });
    setSelectedComponents(newSelected);
    setComponentDeliverables(newDeliverables);
    setStepDirection('forward');
    setAnimKey(prev => prev + 1);
    setStep(4);
    toast.success(`Applied template "${tpl.name}" — ${(tpl.components || []).length} components, ${tpl.milestone_count || 0} milestones pre-loaded.`);
  };

  const refreshScopeTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/scope-templates`, { withCredentials: true });
      setScopeTemplates(res.data?.templates || []);
    } catch { /* ignore */ }
  };

  // ── Navigation ──────────────────────────────────────────
  const goToStep = (newStep) => {
    if (newStep === 3 && !implementationType) return;
    if (newStep === 4 && (!implementationType || !complexity)) return;
    if (newStep === 4 && step < 4) applyBaseline();
    setStepDirection(newStep > step ? 'forward' : 'backward');
    setAnimKey(prev => prev + 1);
    setStep(newStep);
  };

  const resetEstimate = () => {
    setStepDirection('backward');
    setAnimKey(prev => prev + 1);
    setStep(1);
    setImplementationType(''); setComplexity('');
    setSelectedComponents({});
    setModifiers({ governance: 1.0, content_readiness: 1.0, integration_complexity: 1.0, org_complexity: 1.0, timeline_pressure: 1.0, client_capability: 1.0, change_readiness: 1.0, data_integrity: 1.0, pm_meetings_per_week: 2, pm_fte_per_meeting: 4, project_contingency_pct: 0, apply_timeline_stretch_tax: true });
    setNewDevelopments([]); setResults(null); setSelectedTemplate(null);
    setEstimateName(''); setSavedInfo(null); setHourOverrides({}); setPlannerStartOverrides({}); setPlannerWeeksOverrides({}); setPlannerReleaseAnchors({});
    setEditingHour(null); setOrgName(''); setImplName('');
    setImplDescription(''); setTargetAudience('');
    setShowProjectForm(false); setHostingModel('public_cloud');
    setRateCardOverrides({}); setLoadedEstimateId(null);
    // Reset rounding back to the user's saved default (not always 0).
    setRounding({
      hours_increment: Number(user?.preferences?.default_rounding?.hours_increment || 0),
      cost_increment: Number(user?.preferences?.default_rounding?.cost_increment || 0),
    });
  };

  // ── Component handlers ──────────────────────────────────
  const toggleComponent = (name) => {
    const comp = components.find(c => c.name === name);
    if (comp?.required && selectedComponents[name] && implementationType !== 'Project') {
      toast.info(`${name} is a required component and cannot be removed.`);
      return;
    }
    setSelectedComponents(prev => {
      const newSelected = { ...prev };
      const turningOn = !newSelected[name];
      if (newSelected[name]) { delete newSelected[name]; } else { newSelected[name] = 1; }

      // Dependency handling (comp.requires): selecting a dependent auto-selects
      // its prerequisite; deselecting a prerequisite removes its dependents.
      if (turningOn && comp?.requires && !newSelected[comp.requires]) {
        newSelected[comp.requires] = 1;
        toast.info(`${comp.requires} was added — ${name} requires it.`);
      }
      if (!turningOn) {
        const dependents = components.filter(c => c.requires === name && newSelected[c.name]);
        if (dependents.length > 0) {
          dependents.forEach(d => { delete newSelected[d.name]; });
          toast.info(`${dependents.map(d => d.name).join(', ')} removed — ${name} is required for ${dependents.length === 1 ? 'it' : 'them'}.`);
        }
      }
      return newSelected;
    });
  };

  // v260.121 — Pending maturity override (slider key + new value the user
  // just chose, held back until they enter a reason or click "Skip").
  const [pendingOverride, setPendingOverride] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  const updateModifier = (key, sliderValue) => {
    // Direct-value fields (count or %) — store the raw number rather than
    // resolving a slider-index into the bucketed multiplier values.
    if (key === 'pm_meetings_per_week' || key === 'pm_fte_per_meeting' || key === 'project_contingency_pct') {
      setModifiers(prev => ({ ...prev, [key]: sliderValue }));
      return;
    }
    // Toggle/boolean fields — store the value as-is.
    if (key === 'apply_timeline_stretch_tax') {
      setModifiers(prev => ({ ...prev, [key]: Boolean(sliderValue) }));
      return;
    }
    // Resolve the target multiplier
    let nextValue;
    if (key === 'data_integrity') {
      nextValue = DATA_INTEGRITY_VALUES[sliderValue];
    } else {
      const values = IS_COMPLEXITY_SLIDER.has(key) ? COMPLEXITY_VALUES : READINESS_VALUES;
      nextValue = values[sliderValue];
    }

    // v260.121 — When this slider is currently AUTO-set from maturity AND
    // the user is moving it off the suggestion, hold the change behind a
    // reason prompt. They can submit a reason or Skip — either way the
    // override metadata is stamped so finance has the audit trail.
    const matMeta = modifiers?._maturity_meta?.per_factor?.[key];
    const currentValue = modifiers?.[key];
    const wasAuto = matMeta && Math.abs((currentValue ?? 0) - matMeta.suggested) < 1e-6;
    const isDiverging = matMeta && Math.abs(nextValue - matMeta.suggested) > 1e-6;
    if (wasAuto && isDiverging) {
      setPendingOverride({ key, nextValue });
      setOverrideReason('');
      return;
    }

    setModifiers(prev => ({ ...prev, [key]: nextValue }));
  };

  // Commit the pending override, stamping per-factor audit metadata so the
  // pill flips to MANUAL with attribution + reason on the next render.
  const commitPendingOverride = (reasonText) => {
    if (!pendingOverride) return;
    const { key, nextValue } = pendingOverride;
    const now = new Date().toISOString();
    const prevValue = modifiers?.[key];
    const matMeta = modifiers?._maturity_meta || {};
    const suggested = matMeta?.per_factor?.[key]?.suggested;
    const band = matMeta?.band;
    setModifiers(prev => {
      const meta = prev?._maturity_meta || {};
      const perFactor = { ...(meta.per_factor || {}) };
      const factor = { ...(perFactor[key] || {}) };
      factor.source = 'manual_override';
      factor.overridden_at = now;
      factor.overridden_by_name = user?.name || user?.email || 'Unknown';
      factor.override_reason = reasonText || null;
      perFactor[key] = factor;
      return {
        ...prev,
        [key]: nextValue,
        _maturity_meta: { ...meta, per_factor: perFactor },
      };
    });
    // v260.122 — fire-and-forget activity-log entry. Always best-effort:
    // a network failure here must not block the slider change.
    if (loadedEstimateId && suggested != null) {
      axios.post(
        `${API_URL}/api/estimates/${loadedEstimateId}/maturity-overrides`,
        {
          factor_key: key,
          previous_value: prevValue,
          new_value: nextValue,
          suggested_value: suggested,
          band: band || null,
          reason: reasonText || null,
        },
        { withCredentials: true },
      ).catch((err) => {
        console.warn('maturity-override log entry failed', err);
      });
    }
    setPendingOverride(null);
    setOverrideReason('');
  };

  // ── Project estimates: factors can be deselected (value → null / n.a.) ──
  const projectDisabledFactors = modifiers.project_disabled_factors || {};
  const toggleFactorEnabled = (key) => {
    setModifiers(prev => {
      const dis = { ...(prev.project_disabled_factors || {}) };
      const notes = { ...(prev.project_disabled_notes || {}) };
      if (key in dis) {
        const restored = dis[key];
        delete dis[key];
        delete notes[key];
        return { ...prev, [key]: restored, project_disabled_factors: dis, project_disabled_notes: notes };
      }
      dis[key] = prev[key];
      return { ...prev, [key]: null, project_disabled_factors: dis, project_disabled_notes: notes };
    });
  };
  const setFactorNote = (key, text) => {
    setModifiers(prev => ({
      ...prev,
      project_disabled_notes: { ...(prev.project_disabled_notes || {}), [key]: text },
    }));
  };

  const getSliderIndex = (key, value) => {
    if (key === 'data_integrity') {
      const idx = DATA_INTEGRITY_VALUES.findIndex(v => Math.abs(v - value) < 0.05);
      return idx >= 0 ? idx : 3;
    }
    const values = IS_COMPLEXITY_SLIDER.has(key) ? COMPLEXITY_VALUES : READINESS_VALUES;
    const idx = values.findIndex(v => Math.abs(v - value) < 0.05);
    return idx >= 0 ? idx : 2;
  };

  // ── Project Factor effort impact (Δ vs neutral) ──────────────
  // Each modifier multiplies into the composite effort modifier; a
  // value of 1.0 is neutral. So delta_pct = (modifier - 1.0) × 100
  // gives a direct signed % impact on every component's effort. We
  // surface this inline next to each slider so users see "−20% effort"
  // when they slide to Very Strong, killing the "slider feels reversed"
  // class of confusion. data_integrity is shown the same way even
  // though it primarily affects Migration components — the chip
  // expresses the per-modifier multiplier impact, not the global
  // total impact (which depends on scope mix).
  const getModifierImpact = (modifierValue) => {
    if (typeof modifierValue !== 'number' || Math.abs(modifierValue - 1.0) < 0.005) {
      return null;
    }
    const deltaPct = (modifierValue - 1.0) * 100;
    const rounded = Math.abs(deltaPct) < 1 ? Math.round(deltaPct * 10) / 10 : Math.round(deltaPct);
    const isSaving = deltaPct < 0;
    return {
      deltaPct: rounded,
      label: `${isSaving ? '' : '+'}${rounded}% effort`,
      tone: isSaving
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
        : 'bg-amber-50 text-amber-800 border-amber-200',
    };
  };

  // ── SOW drift summary fetch ────────────────────────────────
  // Drives the amber pulse on the Project Conditions chip in the
  // Estimator footer. Refetched when loadedEstimateId changes and
  // after Project Conditions are saved (handled inline below).
  const fetchSowDriftSummary = useCallback(async (estimateId) => {
    if (!estimateId) {
      setSowDriftSummary(null);
      return;
    }
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}/sow-drift-summary`, { withCredentials: true });
      setSowDriftSummary(res.data);
    } catch {
      setSowDriftSummary(null);
    }
  }, []);
  useEffect(() => {
    fetchSowDriftSummary(loadedEstimateId);
  }, [loadedEstimateId, fetchSowDriftSummary]);

  // ── Save & Export ──────────────────────────────────────
  // Auto-generate estimate name if empty
  const autoGenerateName = useCallback(() => {
    const org = orgName.trim() || 'Estimate';
    const type = implementationType || '';
    const date = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${org} - ${type} - ${date}`;
  }, [orgName, implementationType]);

  // Auto-persist the inline rename. Fires the moment the user commits a
  // rename in the workspace header h1 (Enter / blur). For unsaved
  // estimates this is a no-op — the name lands with the first POST. For
  // saved estimates we fire-and-forget a PATCH so the new name survives
  // a refresh without the user needing to click Save (Notion / Linear
  // pattern). Backend folds the change into the latest version's
  // inplace_edits audit trail.
  const persistRename = useCallback(async (newName) => {
    if (!savedEstimateId) return;
    const trimmed = (newName || '').trim();
    if (!trimmed) return;
    try {
      await axios.patch(
        `${API_URL}/api/estimates/${savedEstimateId}/name`,
        { name: trimmed },
        { withCredentials: true },
      );
      setSavedInfo((prev) => (prev ? { ...prev, name: trimmed, time: new Date() } : { name: trimmed, time: new Date() }));
      toast.success('Name saved');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save name';
      toast.error(msg);
    }
  }, [savedEstimateId]);

  const commitSave = async (opts = {}) => {
    // Discount Justification gate — require a meaningful reason (>= 10 chars)
    // whenever the user is saving with a non-zero blanket discount/surcharge
    // so we build an audit trail and prevent silent margin leaks.
    const _trimmedReason = (rateCardBlanketDiscountReason || '').trim();
    if (rateCardBlanketDiscountPct !== 0 && _trimmedReason.length < 10) {
      toast.error('Add a justification of at least 10 characters for the blanket discount before saving.');
      const el = document.querySelector('[data-testid="estimator-blanket-discount-reason"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => { try { el.focus(); } catch (_) {} }, 250);
      }
      return;
    }
    // Auto-fill name if empty
    let nameToSave = estimateName.trim();
    if (!nameToSave) {
      nameToSave = autoGenerateName();
      setEstimateName(nameToSave);
    }
    if (!results) return;
    setSaving(true);
    try {
      const selected = Object.entries(selectedComponents)
        .filter(([, qty]) => qty > 0)
        .map(([name, quantity]) => {
          const vo = volumeOverrides[name];
          const hasVolume = vo && vo.enabled && vo.entries && Object.values(vo.entries).some(v => v > 0);
          return {
            component_name: name, quantity,
            hour_override: hourOverrides[name] || null,
            use_volume: hasVolume || false,
            volume_entries: hasVolume ? vo.entries : null,
            hypercare_scope: name === 'Hypercare Support' ? (hypercareScope || null) : null,
            planner_start_week: plannerStartOverrides[name] || null,
            planner_weeks: plannerWeeksOverrides[name] || null,
            planner_blocks: (Array.isArray(plannerBlocksOverrides[name]) && plannerBlocksOverrides[name].length > 0) ? plannerBlocksOverrides[name] : null,
            deliverables: componentDeliverables[name] || [],
            // v258.6 — release anchor IDs (for UI re-display only; the
            // actual schedule lives in planner_start_week / planner_weeks).
            anchor_release_id: plannerReleaseAnchors[name]?.release_id || null,
            anchor_lead_weeks: plannerReleaseAnchors[name]?.lead_weeks || null,
          };
        });
      const payload = {
        estimate_name: nameToSave, organization_name: orgName,
        implementation_name: implName, implementation_description: implDescription,
        target_audience: targetAudience ? parseInt(targetAudience) : null,
        implementation_type: implementationType, complexity, mode: 'simple',
        project_kind: implementationType === 'Project' ? projectKind : null,
        rnd_category: implementationType === 'Project' && projectKind === 'rnd' ? rndCategory : null,
        selected_components: selected, modifiers, hosting_model: hostingModel,
        org_id: selectedOrgId || null,
        new_developments: newDevelopments.length > 0 ? newDevelopments : null,
        integrations: integrations.length > 0 ? integrations : null,
        rate_card_override: Object.keys(rateCardOverrides).length > 0 ? rateCardOverrides : null,
        rate_card_blanket_discount_pct: rateCardBlanketDiscountPct || null,
        rate_card_blanket_discount_reason: rateCardBlanketDiscountPct !== 0 ? (rateCardBlanketDiscountReason || '').trim() || null : null,
        rounding: (rounding.hours_increment || rounding.cost_increment) ? rounding : null,
        currency_code: currencyCode || null,
        contracting_entity_id: contractingEntityId || null,
        region: region || null,
        tax_inclusive: taxInclusive,
        preferred_start_date: preferredStartDate || null,
        project_conditions: hasConditions ? pendingConditions : null,
        has_conditions: hasConditions,
        sales_lead: pendingSalesLead || null,
        cs_lead: pendingCSLead || null,
        results,
      };

      // Estimate Proliferation Fix (v240) — when we already have a saved
      // estimate id, PUT the update IN PLACE instead of POSTing a new
      // doc. Whether this PUT bumps the version number (v3 → v4) or
      // simply appends to the latest version's `inplace_edits` audit
      // trail is decided by `opts.createNewVersion` — sourced from the
      // split-button: primary "Save" defaults to in-place updates,
      // the caret menu surfaces explicit "Save as new version".
      let res;
      if (savedEstimateId) {
        if (opts.changeSummary) payload.change_summary = opts.changeSummary;
        payload.create_new_version = !!opts.createNewVersion;
        res = await axios.put(`${API_URL}/api/estimates/${savedEstimateId}`, payload, { withCredentials: true });
      } else {
        res = await axios.post(`${API_URL}/api/estimates`, payload, { withCredentials: true });
      }
      const returnedId = res.data?.id || res.data?.estimate_id;
      const versionNum = res.data?.version_number;
      const saveMode = res.data?.save_mode;
      if (returnedId) setSavedEstimateId(returnedId);
      if (returnedId && pendingProductPricingRef.current) {
        const ppInputs = pendingProductPricingRef.current;
        pendingProductPricingRef.current = null;
        axios.put(`${API_URL}/api/estimates/${returnedId}/product-pricing`, ppInputs, { withCredentials: true })
          .then((r) => toast.success(`Product pricing saved — ${r.data.results?.package?.label} · A$${Math.round(r.data.results?.totals?.net || 0).toLocaleString()} TCO`))
          .catch((e) => toast.error(e?.response?.data?.detail || 'Product pricing from the wizard could not be saved'));
      }
      setSavedInfo({ name: nameToSave, time: new Date(), version: versionNum });
      if (res.data?.fx_rate_to_aud) {
        setFxSnapshot({ rate: res.data.fx_rate_to_aud, date: res.data.fx_rate_date, code: res.data.currency_code || currencyCode });
      }
      if ((res.data?.synced_sows || []).length) {
        toast.info(`Payment currency synced on ${res.data.synced_sows.length} editable SOW(s)`);
      }
      setHasUnsavedChanges(false);
      baselineFingerprintRef.current = _estimateDirtyFingerprint;
      lastSavedRef.current = { results, inputs: snapshotInputs() };
      setSaveReview(null);
      setRateDrift(null);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);

      if (savedEstimateId) {
        if (saveMode === 'in_place') {
          toast.success(`v${versionNum || '?'} updated in place`);
        } else {
          toast.success(`Saved as v${versionNum || '?'} · new version`);
        }
      } else {
        toast.success(`Estimate saved · v${versionNum || 1}`);
      }

      // If navigating after save (from save gate)
      if (opts.navigateTo && returnedId) {
        const dest = opts.navigateTo.replace('{id}', returnedId);
        navigate(dest);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 423) toast.error(detail || 'Effort & cost are locked on this estimate.', { duration: 8000 });
      else toast.error(typeof detail === 'string' ? detail : 'Failed to save estimate');
    } finally { setSaving(false); }
  };

  // ── Pre-save review: diff current inputs/results vs last saved ──
  const _entityLabel = (id) => (id ? (seertechEntities.find(e => e.id === id)?.name || id) : '— none');
  const _pretty = (v) => {
    if (v === null || v === undefined || v === '') return '— none';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') return JSON.stringify(v);
    return String(v).replace(/_/g, ' ');
  };
  const SETTING_FIELDS = [
    ['contractingEntityId', 'Contracting entity', _entityLabel],
    ['region', 'Region', (v) => _pretty(v ? String(v).toUpperCase() : v)],
    ['currencyCode', 'Currency', (v) => (v ? String(v).toUpperCase() : `inherited (${effectiveCurrency.code})`)],
    ['implementationType', 'Implementation type', _pretty],
    ['complexity', 'Complexity', _pretty],
    ['hostingModel', 'Hosting model', _pretty],
    ['rateCardBlanketDiscountPct', 'Blanket discount / surcharge', (v) => `${v || 0}%`],
    ['rateCardOverrides', 'Rate card overrides', (v) => {
      const keys = Object.keys(v || {});
      return keys.length ? keys.map(k => `${k}: ${v[k]}`).join(', ') : '— none';
    }],
    ['taxInclusive', 'Tax inclusive', _pretty],
    ['preferredStartDate', 'Preferred start date', _pretty],
    ['hypercareScope', 'Hypercare scope', (v) => (v && typeof v === 'object' ? Object.entries(v).map(([k, x]) => `${k.replace(/_/g, ' ')} ${x}`).join(' · ') : 'preset')],
    ['rounding', 'Rounding', (v) => (v && (v.hours_increment || v.cost_increment)) ? `hours ${v.hours_increment || 0} · cost ${v.cost_increment || 0}` : '— none'],
  ];
  const buildSaveReview = () => {
    const saved = lastSavedRef.current;
    if (!savedEstimateId || !saved?.inputs) return null;
    const cur = snapshotInputs();
    const settings = [];
    SETTING_FIELDS.forEach(([key, label, fmt]) => {
      if (JSON.stringify(saved.inputs[key] ?? null) !== JSON.stringify(cur[key] ?? null)) {
        settings.push({ key, label, from: fmt(saved.inputs[key]), to: fmt(cur[key]) });
      }
    });
    Object.keys({ ...(saved.inputs.modifiers || {}), ...(modifiers || {}) }).forEach((k) => {
      if (JSON.stringify(saved.inputs.modifiers?.[k] ?? null) !== JSON.stringify(modifiers?.[k] ?? null)) {
        settings.push({ key: `modifier:${k}`, label: `Modifier · ${_pretty(k)}`, from: _pretty(saved.inputs.modifiers?.[k]), to: _pretty(modifiers?.[k]) });
      }
    });
    const sr = saved.results;
    const totals = sr ? {
      effortFrom: sr.total_effort || 0, effortTo: results?.total_effort || 0,
      costFrom: sr.total_cost || 0, costTo: results?.total_cost || 0, symbol: currencySymbol,
    } : null;
    const comps = [];
    if (sr) {
      const before = new Map((sr.component_breakdown || []).map(c => [c.name, c]));
      const after = new Map((results?.component_breakdown || []).map(c => [c.name, c]));
      new Set([...before.keys(), ...after.keys()]).forEach((name) => {
        const b = before.get(name); const a = after.get(name);
        const row = { name, effortFrom: b?.final_effort || 0, effortTo: a?.final_effort || 0, costFrom: b?.cost || 0, costTo: a?.cost || 0 };
        if (!b) comps.push({ ...row, kind: 'added' });
        else if (!a) comps.push({ ...row, kind: 'removed' });
        else if (Math.abs(row.effortFrom - row.effortTo) >= 0.05 || Math.abs(row.costFrom - row.costTo) >= 0.5) comps.push({ ...row, kind: 'changed' });
      });
    }
    const totalsChanged = totals && (Math.abs(totals.effortFrom - totals.effortTo) >= 0.05 || Math.abs(totals.costFrom - totals.costTo) >= 0.5);
    if (!totalsChanged && settings.length === 0 && comps.length === 0) return null;
    return { totals, settings, components: comps };
  };

  const saveEstimate = (opts = {}) => {
    if (!opts.skipReview) {
      const review = buildSaveReview();
      if (review) {
        const t = review.totals;
        const effortCostMoved = (t && (Math.abs(t.costTo - t.costFrom) >= 0.5 || Math.abs(t.effortTo - t.effortFrom) >= 0.05)) || review.components.length > 0;
        review.lockBlocked = !!(effortCostLock?.locked && effortCostMoved);
        setShowSaveGateModal(null); setSaveReview({ review, opts }); return;
      }
    }
    return commitSave(opts);
  };

  const INPUT_SETTERS = {
    selectedComponents: setSelectedComponents, modifiers: setModifiers, hourOverrides: setHourOverrides,
    volumeOverrides: setVolumeOverrides, rateCardOverrides: setRateCardOverrides,
    rateCardBlanketDiscountPct: setRateCardBlanketDiscountPct, rateCardBlanketDiscountReason: setRateCardBlanketDiscountReason,
    hostingModel: setHostingModel, newDevelopments: setNewDevelopments, integrations: setIntegrations,
    hypercareScope: setHypercareScope, preferredStartDate: setPreferredStartDate,
    contractingEntityId: setContractingEntityId, region: setRegion, currencyCode: setCurrencyCode,
    taxInclusive: setTaxInclusive, rounding: setRounding, complexity: setComplexity, implementationType: setImplementationType,
  };
  const restoreSavedField = (key) => {
    const saved = lastSavedRef.current?.inputs;
    if (!saved) return;
    if (key.startsWith('modifier:')) {
      const k = key.slice(9);
      setModifiers(prev => ({ ...prev, [k]: saved.modifiers?.[k] }));
    } else if (INPUT_SETTERS[key]) {
      INPUT_SETTERS[key](saved[key]);
    }
    setSaveReview(null);
    toast.info('Saved value restored — effort and cost are being recalculated. Save again when ready.');
  };
  const CALC_KEYS = ['selectedComponents', 'modifiers', 'hourOverrides', 'volumeOverrides', 'rateCardOverrides', 'rateCardBlanketDiscountPct', 'hostingModel', 'newDevelopments', 'integrations', 'hypercareScope', 'contractingEntityId', 'region', 'complexity', 'implementationType'];
  const restoreAllSaved = () => {
    const saved = lastSavedRef.current?.inputs;
    if (!saved) return;
    const cur = snapshotInputs();
    const calcChanged = CALC_KEYS.some(k => JSON.stringify(cur[k] ?? null) !== JSON.stringify(saved[k] ?? null));
    Object.entries(INPUT_SETTERS).forEach(([key, setter]) => { if (key in saved) setter(saved[key]); });
    if (lastSavedRef.current.results) {
      if (calcChanged) skipAutoCalcRef.current = true;
      setResults(lastSavedRef.current.results);
    }
    if (baselineFingerprintRef.current === '__repriced__') baselineFingerprintRef.current = null;
    setHasUnsavedChanges(false);
    setSaveReview(null);
    toast.info('Restored the last saved values, effort and cost.');
  };
  const confirmReviewedSave = (note) => {
    const r = saveReview?.review;
    const parts = [];
    if (r?.totals) {
      const t = r.totals;
      if (Math.abs(t.costTo - t.costFrom) >= 0.5) parts.push(`Cost ${t.symbol}${Math.round(t.costFrom).toLocaleString()} → ${t.symbol}${Math.round(t.costTo).toLocaleString()}`);
      if (Math.abs(t.effortTo - t.effortFrom) >= 0.05) parts.push(`Effort ${t.effortFrom.toFixed(1)}h → ${t.effortTo.toFixed(1)}h`);
    }
    (r?.settings || []).slice(0, 4).forEach(s => parts.push(`${s.label}: ${s.from} → ${s.to}`));
    const auto = parts.join(' · ');
    const changeSummary = [note, auto].filter(Boolean).join(' — ').slice(0, 600) || undefined;
    commitSave({ ...(saveReview?.opts || {}), changeSummary, skipReview: true });
  };
  const repriceAtTodaysRates = () => {
    if (!rateDrift?.live) return;
    setResults(rateDrift.live);
    // Inputs are unchanged, so force the dirty flag via a stale baseline.
    baselineFingerprintRef.current = '__repriced__';
    setRateDrift(null);
    toast.info("Repriced at today's rates — review and save to commit the new figures.");
  };

  // Save gate: check if estimate is saved before navigating. View-only
  // users can't save (403) — never gate them, just navigate.
  const navigateWithSaveGate = useCallback((path) => {
    if (user?.role === 'user' && savedEstimateId) {
      navigate(path.replace('{id}', savedEstimateId));
      return;
    }
    if (!savedEstimateId || hasUnsavedChanges) {
      setShowSaveGateModal(path);
    } else {
      navigate(path.replace('{id}', savedEstimateId));
    }
  }, [savedEstimateId, hasUnsavedChanges, navigate, user?.role]);

  // ── Quick Start Demo ──────────────────────────────────
  const launchQuickStart = async () => {
    setOrgName('Demo Corp');
    setImplName('Quick Start Demo \u2014 Corporate LMS');
    setImplDescription('A standard corporate learning management deployment with SSO, HRIS integration, and reporting.');
    setTargetAudience('5000');
    setEstimateName('Demo Corp \u2014 Quick Start LMS Estimate');
    setImplementationType('Corporate');
    setComplexity('standard');
    setHostingModel('cloud');

    const templateKey = 'internal_lms';
    const tpl = templates[templateKey];
    if (tpl) {
      const newSel = {};
      tpl.default_components.forEach(c => { newSel[c.component_name] = c.quantity; });
      setSelectedComponents(newSel);
      if (tpl.default_modifiers) setModifiers(prev => ({ ...prev, ...tpl.default_modifiers }));
    }

    try {
      const tplComps = tpl ? tpl.default_components : [];
      const res = await axios.post(`${API_URL}/api/calculate`, {
        implementation_type: 'Corporate', complexity: 'standard', mode: 'simple',
        selected_components: tplComps, modifiers: tpl?.default_modifiers || modifiers,
        hosting_model: 'cloud',
      });
      setResults(res.data);
    } catch { /* still jump to Step 4 */ }

    setDemoMode(true);
    setStep(4);
    setAnimKey(k => k + 1);
  };

  const getExportPayload = () => {
    const selected = Object.entries(selectedComponents)
      .filter(([, qty]) => qty > 0)
      .map(([name, quantity]) => ({ component_name: name, quantity }));
    return {
      implementation_type: implementationType, complexity, mode: 'simple',
      selected_components: selected, modifiers,
      organization_name: orgName, implementation_name: implName,
      implementation_description: implDescription,
      target_audience: targetAudience ? parseInt(targetAudience) : null,
      hosting_model: hostingModel, org_id: selectedOrgId || null
    };
  };

  const generateNarrative = async () => {
    if (!results) return;
    setNarrativeLoading(true);
    setNarrative(null);
    try {
      const selected = Object.entries(selectedComponents)
        .filter(([, qty]) => qty > 0)
        .map(([name, quantity]) => ({ name, quantity }));
      const res = await axios.post(`${API_URL}/api/narrative/generate`, {
        estimate_results: results, implementation_type: implementationType,
        complexity, organization_name: orgName, implementation_name: implName,
        implementation_description: implDescription,
        target_audience: targetAudience ? parseInt(targetAudience) : null,
        modifiers, selected_components: selected,
        currency_code: effectiveCurrency.code || 'USD',
        currency_symbol: currencySymbol
      }, { withCredentials: true });
      setNarrative(res.data.narrative);
      setNarrativeExpanded(true);
    } catch {
      alert('Failed to generate AI narrative. Please try again.');
    } finally { setNarrativeLoading(false); }
  };

  // ── Fuzzy match for org name ──────────────────────────
  const fuzzyTimerRef = useRef(null);
  const handleNewOrgNameChange = (value) => {
    setNewOrgName(value);
    setFuzzyMatches([]);
    if (fuzzyTimerRef.current) clearTimeout(fuzzyTimerRef.current);
    if (value.trim().length >= 2) {
      setFuzzyLoading(true);
      fuzzyTimerRef.current = setTimeout(async () => {
        try {
          const res = await axios.post(`${API_URL}/api/organizations/fuzzy-match`, { name: value }, { withCredentials: true });
          setFuzzyMatches(res.data.matches || []);
        } catch { /* ignore */ }
        finally { setFuzzyLoading(false); }
      }, 400);
    } else { setFuzzyLoading(false); }
  };

  const handleInlineOrgCreate = async () => {
    if (!newOrgName.trim()) return;
    setCreatingOrg(true);
    try {
      const formData = new FormData();
      formData.append('name', newOrgName.trim());
      formData.append('industry', newOrgIndustry);
      formData.append('country', newOrgCountry);
      formData.append('contact_email', newOrgContactEmail);
      const res = await axios.post(`${API_URL}/api/organizations`, formData, { withCredentials: true });
      const newOrg = res.data;
      setOrganizations(prev => [...prev, newOrg]);
      setSelectedOrgId(newOrg.org_id);
      setOrgName(newOrg.name);
      setOrgMode('select');
      setNewOrgName(''); setNewOrgIndustry(''); setNewOrgCountry(''); setNewOrgContactEmail('');
      setFuzzyMatches([]);
      toast.success(`Organization "${newOrg.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    } finally { setCreatingOrg(false); }
  };

  const handleFuzzySelect = (org) => {
    setSelectedOrgId(org.org_id);
    setOrgName(org.name);
    setOrgMode('select');
    setNewOrgName('');
    setFuzzyMatches([]);
    toast.success(`Selected existing organization "${org.name}"`);
  };

  // ── Helpers & Derived State ──────────────────────────────
  const groupedComponents = useMemo(() => components.reduce((acc, comp) => {
    if (!acc[comp.group]) acc[comp.group] = [];
    acc[comp.group].push(comp);
    return acc;
  }, {}), [components]);

  const isAdmin = ['admin', 'cs', 'cro', 'cco', 'super_admin'].includes(user?.role);
  const isSuperAdmin = user?.role === 'super_admin';
  const canCreateOrg = isAdmin;
  const allTabs = useMemo(() => [...Object.keys(groupedComponents), 'new-development', 'integration-scope', 'project-factors'], [groupedComponents]);

  // Load pricing-impact baseline (avg total_cost over last 50 estimates) for
  // super-admins so the per-estimate Rate Card Override section can show the
  // live "≈ −$12.4k impact on the average estimate" preview when a blanket
  // discount or surcharge is dialed in.
  useEffect(() => {
    if (!isSuperAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/settings/pricing-impact-baseline`,
          { withCredentials: true },
        );
        if (!cancelled) setPricingBaseline(res.data || null);
      } catch (e) { /* silent — feature degrades gracefully */ }
    })();
    return () => { cancelled = true; };
  }, [isSuperAdmin]);

  useEffect(() => {
    if (step === 4 && !estimateName && !loadedEstimateId && orgName && implName) {
      setEstimateName(`${orgName} - ${implName}`);
    }
  }, [step, estimateName, loadedEstimateId, orgName, implName]);

  useEffect(() => {
    if (step === 4 && allTabs.length > 0 && !activeTab) {
      setActiveTab(allTabs[0]);
      setVisitedTabs(new Set([allTabs[0]]));
    }
  }, [step, allTabs, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVisitedTabs(prev => new Set([...prev, tab]));
  };

  // ── Jump from the "Active overrides" popover straight to a specific
  // component row: switch to its group tab, scroll it into view, and
  // pulse an amber highlight so the eye lands on it instantly.
  const jumpToComponent = (name, group) => {
    if (group && group !== activeTab) handleTabChange(group);
    const slug = name.replace(/\s+/g, '-').toLowerCase();
    // Wait for the tab content to render before searching the DOM.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="component-${slug}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'transition-all');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2');
      }, 1600);
    }, 80);
  };

  // v260.40 — Same pattern but for integration rows on the
  // Integration Scope tab. Used by the highlight stepper when stepping
  // through `int::<type>` entries pushed by the Requirements Apply diff.
  const jumpToIntegration = (type) => {
    if (activeTab !== 'integration-scope') handleTabChange('integration-scope');
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="integration-${type}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.add('ring-2', 'ring-amber-400', 'ring-offset-2', 'transition-all');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-amber-400', 'ring-offset-2');
      }, 1600);
    }, 80);
  };

  // v260.38/v260.39/v260.40 — Whenever the highlight cursor changes
  // (initial landing OR user clicks Prev/Next on the stepper chip),
  // jump to the current target. Components route through
  // jumpToComponent; integrations route through jumpToIntegration.
  // Gated on step=4 + components ready + activeTab set (avoids
  // racing with the default-tab-init effect). Idempotent — strict-mode
  // double-fire is harmless.
  useEffect(() => {
    if (step !== 4 || highlightTargets.length === 0 || components.length === 0 || !activeTab) return;
    if (highlightIdx >= highlightTargets.length) return; // Done state — no jump
    const cur = highlightTargets[highlightIdx];
    if (!cur) return;
    setTimeout(() => {
      if (cur.kind === 'integration') {
        jumpToIntegration(cur.type);
      } else {
        const comp = components.find((c) => c.name === cur.name);
        if (comp) jumpToComponent(comp.name, comp.group);
      }
    }, 350);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, highlightTargets, highlightIdx, components, activeTab]);

  // ── Register components as global ⌘K palette commands (Step 4 only).
  // Each command surfaces the inline Start / Wks / reset / select widgets
  // from the previous EstimatorCommandPalette, so the global palette
  // keeps full parity with the deep-route experience.
  usePaletteCommands(
    'estimator-components',
    () => ({
      heading: 'Components — schedule overrides',
      priority: 5,  // Surface above the built-in "Go to" group.
      items: components.map((comp) => {
        const slug = comp.name.replace(/\s+/g, '-').toLowerCase();
        return {
          id: `comp-${slug}`,
          value: `${comp.name} ${comp.group} ${comp.role || ''}`,
          onSelect: () => jumpToComponent(comp.name, comp.group),
          render: () => {
            const isSelected = (selectedComponents[comp.name] || 0) > 0;
            const start = plannerStartOverrides[comp.name] ?? '';
            const weeks = plannerWeeksOverrides[comp.name] ?? '';
            const hasOverride = !!(start || weeks);
            const stop = (e) => e.stopPropagation();
            return (
              <>
                <button
                  type="button"
                  onClick={(e) => { stop(e); toggleComponent(comp.name); }}
                  onMouseDown={stop}
                  className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                    isSelected ? 'bg-[#0F2040] border-[#0F2040]' : 'border-slate-300 hover:border-[#0F2040]'
                  }`}
                  title={isSelected ? 'Deselect component' : 'Select component'}
                  data-testid={`cmdk-toggle-${slug}`}
                >
                  {isSelected && <Check className="w-3 h-3 text-white" />}
                </button>
                <div className="flex flex-col min-w-0 flex-1">
                  <span className="text-sm font-medium text-[#0F2040] truncate flex items-center gap-1.5">
                    {comp.name}
                    {comp.sub_group === 'Learning Intelligence' && (
                      <span className="shrink-0 inline-flex items-center rounded-full bg-[#4ABFCF]/15 text-[#0F6B78] border border-[#4ABFCF]/40 text-[9px] font-semibold px-1.5 py-0 leading-4" title="Learning Intelligence 'I' application" data-testid={`cmdk-i-app-badge-${slug}`}>
                        ‘I’ app
                      </span>
                    )}
                  </span>
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <span
                      className="w-1.5 h-1.5 rounded-full inline-block"
                      style={{ backgroundColor: GROUP_COLORS[comp.group] || '#64748B' }}
                    />
                    {comp.group}
                    <span className="text-slate-300">·</span>
                    {comp.role || '—'}
                    {hasOverride && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-amber-700 font-medium">override</span>
                      </>
                    )}
                  </span>
                </div>
                <div
                  className="flex items-center gap-1.5 shrink-0"
                  onClick={stop}
                  onMouseDown={stop}
                  onPointerDown={stop}
                >
                  <span className="text-[10px] text-slate-400">Start</span>
                  <input
                    type="number"
                    min="1"
                    placeholder="Auto"
                    defaultValue={start}
                    onBlur={(e) => {
                      const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                      setPlannerStartOverrides((prev) => {
                        const n = { ...prev };
                        if (v && v > 0) n[comp.name] = v; else delete n[comp.name];
                        return n;
                      });
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); e.stopPropagation(); }}
                    className="w-12 h-6 text-[11px] text-center border border-slate-200 rounded bg-white text-[#0F2040] focus:border-[#4ABFCF] focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                    data-testid={`cmdk-start-${slug}`}
                  />
                  <span className="text-[10px] text-slate-400">Wks</span>
                  <input
                    type="number"
                    min="1"
                    placeholder="Auto"
                    defaultValue={weeks}
                    onBlur={(e) => {
                      const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                      setPlannerWeeksOverrides((prev) => {
                        const n = { ...prev };
                        if (v && v > 0) n[comp.name] = v; else delete n[comp.name];
                        return n;
                      });
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); e.stopPropagation(); }}
                    className="w-12 h-6 text-[11px] text-center border border-slate-200 rounded bg-white text-[#0F2040] focus:border-[#4ABFCF] focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                    data-testid={`cmdk-weeks-${slug}`}
                  />
                  {hasOverride && (
                    <button
                      type="button"
                      onClick={(e) => {
                        stop(e);
                        setPlannerStartOverrides((prev) => { const n = { ...prev }; delete n[comp.name]; return n; });
                        setPlannerWeeksOverrides((prev) => { const n = { ...prev }; delete n[comp.name]; return n; });
                      }}
                      onMouseDown={stop}
                      className="text-slate-300 hover:text-red-500 transition-colors"
                      title="Reset overrides for this component"
                      data-testid={`cmdk-reset-${slug}`}
                    >
                      <RotateCcw className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </>
            );
          },
        };
      }),
    }),
    {
      enabled: step === 4 && components.length > 0,
      deps: [step, components, selectedComponents, plannerStartOverrides, plannerWeeksOverrides, activeTab],
    }
  );

  const goNextTab = () => {
    const idx = allTabs.indexOf(activeTab);
    if (idx < allTabs.length - 1) {
      const nextTab = allTabs[idx + 1];
      setActiveTab(nextTab);
      setVisitedTabs(prev => new Set([...prev, nextTab]));
    }
  };

  const goPrevTab = () => {
    const idx = allTabs.indexOf(activeTab);
    if (idx > 0) setActiveTab(allTabs[idx - 1]);
  };

  const getUseCaseWeight = (comp) => {
    if (implementationType === 'EXE_Ecommerce') return comp.exe_weight;
    if (implementationType === 'Workforce_Enablement') return comp.workforce_weight;
    return comp.corporate_weight;
  };

  const getWeightIndicator = (weight) => {
    if (weight >= 1.3) return { label: 'High priority', color: 'text-green-600 bg-green-50' };
    if (weight >= 1.1) return { label: 'Recommended', color: 'text-blue-600 bg-blue-50' };
    if (weight <= 0.7) return { label: 'Less common', color: 'text-slate-400 bg-slate-50' };
    if (weight <= 0.9) return { label: 'Optional', color: 'text-slate-500 bg-slate-50' };
    return null;
  };

  const stepAnimClass = stepDirection === 'forward' ? 'animate-slideInRight' : 'animate-slideInLeft';

  const sortedCostByRole = useMemo(() =>
    Object.entries(results?.cost_by_role || {}).sort((a, b) => b[1] - a[1]),
    [results?.cost_by_role]
  );
  const sortedEffortByGroup = useMemo(() =>
    Object.entries(results?.effort_by_group || {}).filter(([, hrs]) => hrs > 0).sort((a, b) => b[1] - a[1]),
    [results?.effort_by_group]
  );
  const sortedCostByGroup = useMemo(() =>
    Object.entries(results?.cost_by_group || {}).filter(([, cost]) => cost > 0).sort((a, b) => b[1] - a[1]),
    [results?.cost_by_group]
  );

  const groupChartData = results ? Object.entries(results.effort_by_group || {})
    .filter(([, hours]) => hours > 0)
    .map(([group, hours]) => ({ name: group, value: Math.round(hours), color: GROUP_COLORS[group] || '#64748B' })) : [];

  // ── Active per-component schedule overrides ─────────────────
  // Surfaces the user-defined Start Week / Weeks (duration) overrides
  // currently in play across selected components. Drives the
  // "Active overrides" pill on the sticky save-bar.
  const activeScheduleOverrides = useMemo(() => {
    const items = [];
    components.forEach((c) => {
      if (!selectedComponents[c.name]) return;
      const start = plannerStartOverrides[c.name];
      const weeks = plannerWeeksOverrides[c.name];
      if (start || weeks) {
        items.push({
          name: c.name,
          group: c.group,
          start: start || null,
          weeks: weeks || null,
        });
      }
    });
    // Group by component-group, preserving GROUP_COLORS order.
    const byGroup = {};
    items.forEach((i) => {
      if (!byGroup[i.group]) byGroup[i.group] = [];
      byGroup[i.group].push(i);
    });
    return {
      count: items.length,
      groups: Object.keys(byGroup),
      byGroup,
      items,
    };
  }, [components, selectedComponents, plannerStartOverrides, plannerWeeksOverrides]);

  const clearAllScheduleOverrides = () => {
    setPlannerStartOverrides({});
    setPlannerWeeksOverrides({});
    toast.success(`Cleared ${activeScheduleOverrides.count} schedule override${activeScheduleOverrides.count === 1 ? '' : 's'}`);
  };

  // ── renderComp: Single component row in Step 4 ───────────
  // Heavy lifting lives in <ComponentRow>; this is the prop-bridge that hands
  // the row component the bundle of state + setters it needs (`ctx`).
  // Phase-1 break overlap surfacing: derive a per-component break list so
  // each row in Step 4 can show "⚠ overlaps Easter Freeze" when the
  // currently-loaded estimate has honored breaks scoped to this component.
  // Phase-1 break overlap surfacing: derive a per-component break list so
  // each row in Step 4 can show "⚠ overlaps Easter Freeze" when the
  // currently-loaded estimate has honored breaks scoped to this component.
  // Each entry resolves to its week-range relative to goal_start_date so
  // ComponentRow can run overlap math against the configured start/weeks
  // and suggest fixes ("resume at W7", "fit ≤ 4w").
  const breaksByComponent = useMemo(() => {
    const map = { __ALL__: [] };
    if (!hasConditions || !pendingConditions) return map;
    const startIso = pendingConditions.goal_start_date || preferredStartDate;
    const baseDate = startIso ? new Date(startIso + 'T00:00:00') : null;
    const breaks = pendingConditions.breaks || [];
    breaks.forEach((b) => {
      const applies = (b.applies_to || 'all').toLowerCase();
      if (applies === 'none') return;
      const label = b.label || 'Break';
      // Compute week-range when we have a baseline date.
      let weekRange = null;
      if (baseDate && !isNaN(baseDate.getTime()) && b.start_date && b.end_date) {
        const s = new Date(b.start_date + 'T00:00:00');
        const e = new Date(b.end_date + 'T00:00:00');
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e >= s) {
          const sw = Math.max(1, Math.floor((s - baseDate) / (1000 * 60 * 60 * 24) / 7) + 1);
          const ew = Math.max(sw, Math.floor((e - baseDate) / (1000 * 60 * 60 * 24) / 7) + 1);
          weekRange = { start_week: sw, end_week: ew };
        }
      }
      const item = { id: b.id, label, weekRange };
      if (applies === 'all') {
        map.__ALL__.push(item);
      } else if (applies === 'specific') {
        (b.component_names || []).forEach((n) => {
          if (!map[n]) map[n] = [];
          map[n].push(item);
        });
      }
    });
    return map;
  }, [hasConditions, pendingConditions, preferredStartDate]);

  const goalStartDateForComponentSchedule = useMemo(() => {
    if (!hasConditions) return null;
    return (pendingConditions && pendingConditions.goal_start_date) || preferredStartDate || null;
  }, [hasConditions, pendingConditions, preferredStartDate]);

  // Compute the FIRST break currently being violated by any active
  // component override. When the user clicks the chip / banner / pill we
  // pass this id to the flyout so it auto-scrolls to (and pulses) the
  // matching break tile — turning the warning into a one-click jump-to-edit.
  const firstViolatedBreakId = useMemo(() => {
    if (!hasConditions || !pendingConditions) return null;
    const breaks = pendingConditions.breaks || [];
    if (breaks.length === 0) return null;
    const startIso = pendingConditions.goal_start_date || preferredStartDate;
    if (!startIso) return null;
    const baseDate = new Date(startIso + 'T00:00:00');
    if (isNaN(baseDate.getTime())) return null;
    const isoToWeek = (iso) => {
      const d = new Date(iso + 'T00:00:00');
      if (isNaN(d.getTime())) return null;
      return Math.max(1, Math.floor((d - baseDate) / (1000 * 60 * 60 * 24) / 7) + 1);
    };
    for (const b of breaks) {
      const applies = (b.applies_to || 'all').toLowerCase();
      if (applies === 'none' || !b.start_date || !b.end_date) continue;
      const bs = isoToWeek(b.start_date);
      const be = isoToWeek(b.end_date);
      if (!bs || !be) continue;
      const targets = applies === 'specific' ? new Set(b.component_names || []) : null;
      if (targets && targets.size === 0) continue;
      const compNames = new Set([
        ...Object.keys(plannerStartOverrides || {}),
        ...Object.keys(plannerWeeksOverrides || {}),
        ...Object.keys(plannerBlocksOverrides || {}),
      ]);
      for (const name of compNames) {
        if (targets && !targets.has(name)) continue;
        const blocks = plannerBlocksOverrides?.[name];
        if (Array.isArray(blocks) && blocks.length > 0) {
          const valid = blocks.filter(x => x && x.start_week && x.weeks);
          for (const blk of valid) {
            const cs = blk.start_week;
            const ce = blk.start_week + blk.weeks - 1;
            if (be >= cs && bs <= ce) return b.id;
          }
          continue;
        }
        const cs = plannerStartOverrides?.[name];
        const cw = plannerWeeksOverrides?.[name];
        if (!cs || !cw) continue;
        const ce = cs + cw - 1;
        if (be >= cs && bs <= ce) return b.id;
      }
    }
    return null;
  }, [hasConditions, pendingConditions, preferredStartDate, plannerStartOverrides, plannerWeeksOverrides, plannerBlocksOverrides]);

  const componentRowCtx = {
    selectedComponents, toggleComponent,
    hourOverrides, setHourOverrides,
    editingHour, setEditingHour,
    volumeMetrics,
    volumeOverrides, setVolumeOverrides,
    plannerStartOverrides, setPlannerStartOverrides,
    plannerWeeksOverrides, setPlannerWeeksOverrides,
    plannerBlocksOverrides, setPlannerBlocksOverrides,
    // v258.6 — per-component release anchor state for the ReleaseAnchorPicker
    // rendered on each Integration-group ComponentRow.
    plannerReleaseAnchors, setPlannerReleaseAnchors,
    publicCloudReleases,
    results,
    hypercareScope, setHypercareScope,
    complexity,
    setDocComponent,
    setShowComponentInfo,
    getUseCaseWeight,
    getWeightIndicator,
    componentDeliverables,
    onOpenDeliverablesEditor: setDeliverablesEditorFor,
    breaksByComponent,
    goalStartDate: goalStartDateForComponentSchedule,
  };
  const renderComp = (comp) => (
    <ComponentRow key={comp.name}
      comp={implementationType === 'Project' ? { ...comp, required: false, recommended: false } : comp}
      ctx={componentRowCtx} />
  );

  // ── Render ──────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Contextual Sub-header */}
      <div className="bg-white border-b border-slate-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <p className="text-sm text-slate-500">
            {step === 1 && 'Get started with your estimate'}
            {step === 2 && 'Select your implementation type'}
            {step === 3 && 'Choose complexity level'}
            {step === 4 && 'Configure components & view results'}
          </p>
          <div className="flex items-center gap-3">
            {step === 4 && hasConditions && (
              <button
                type="button"
                onClick={() => setEditConditionsOpen(true)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors relative ${
                  sowDriftSummary?.drift_count > 0
                    ? 'bg-amber-50 hover:bg-amber-100 border-amber-300 text-amber-900'
                    : 'bg-[#4ABFCF]/10 hover:bg-[#4ABFCF]/20 border-[#4ABFCF]/30 text-[#0F2040]'
                }`}
                data-testid="estimator-edit-conditions-btn"
                title={sowDriftSummary?.drift_count > 0
                  ? `${sowDriftSummary.drift_count} downstream SOW${sowDriftSummary.drift_count === 1 ? '' : 's'} ${sowDriftSummary.drift_count === 1 ? 'has' : 'have'} dates that don't match these Conditions. Saving here may widen the drift — visit Admin → All SOWs to bulk-sync after.`
                  : 'Edit Project Conditions — changes flow into the calculator and Planner'
                }
              >
                <Lock className="w-3 h-3" />
                Project Conditions
                {sowDriftSummary?.drift_count > 0 && (
                  <span
                    className="relative flex items-center"
                    data-testid="estimator-conditions-drift-pulse"
                  >
                    <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-amber-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
                    <span className="ml-1 text-[10px] font-bold tabular-nums">{sowDriftSummary.drift_count}</span>
                  </span>
                )}
              </button>
            )}
            {step === 4 && !hasConditions && loadedEstimateId && (
              <button
                type="button"
                onClick={() => {
                  // Adopt the unidirectional Conditions Flow on a legacy estimate.
                  // Seeds an empty conditions object, flips has_conditions=true,
                  // and opens the EditConditionsDialog so the user can fill in
                  // start date / breaks before saving. The dialog handles the
                  // null-id path too, so unsaved estimates still work.
                  setPendingConditions({
                    goal_duration_weeks: null,
                    goal_start_date: preferredStartDate || '',
                    goal_go_live_date: '',
                    hypercare_weeks: null,
                    breaks: [],
                    blackouts: [],
                    holiday_region: null,
                  });
                  setHasConditions(true);
                  setEditConditionsOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-300/80 text-[11px] font-semibold text-amber-900 transition-colors"
                data-testid="estimator-migrate-conditions-btn"
                title="Adopt the Conditions Flow — locks the Planner behind the Estimate and turns on date-aware break math."
              >
                <Lock className="w-3 h-3" />
                + Migrate to Conditions Flow
              </button>
            )}
            {step === 4 && loadedEstimateId && (
              <button
                type="button"
                onClick={() => navigate(`/estimator/${loadedEstimateId}/requirements`)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-50 hover:bg-cyan-100 border border-cyan-300/80 text-[11px] font-semibold text-cyan-800 transition-colors"
                data-testid="estimator-requirements-btn"
                title="Open the Implementation Requirements form — captures upfront discovery and can pre-populate Project Conditions, components, and project factors."
              >
                <FileText className="w-3 h-3" />
                Requirements
              </button>
            )}
            {step === 4 && loadedEstimateId && requirementsDocMeta?.present && (() => {
              // Drift chip — surfaced inside the estimate header so PMs
              // working on the estimate get the same "out of sync" signal
              // the Pre-Sales board shows. Logic mirrors the workspace
              // row chip (v258.1): drift = updated_at > last_applied_at
              // OR last_applied_at is null (never applied).
              const u = requirementsDocMeta.updated_at;
              const a = requirementsDocMeta.last_applied_at;
              const drift = u && (!a || new Date(u) > new Date(a));
              return drift ? (
                <button
                  type="button"
                  onClick={() => navigate(`/estimator/${loadedEstimateId}/requirements`)}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-300/80 text-[11px] font-semibold text-amber-800 transition-colors"
                  data-testid="estimator-requirements-drift-chip"
                  title={a
                    ? `Requirements updated ${new Date(u).toLocaleDateString()} — last applied ${new Date(a).toLocaleDateString()}. Click to re-open and Apply.`
                    : 'Requirements captured but never applied to the Estimator. Click to open and Apply.'}
                >
                  <AlertTriangle className="w-3 h-3" />
                  Requirements out of sync
                </button>
              ) : (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200/80 text-[11px] font-semibold text-emerald-800"
                  data-testid="estimator-requirements-synced-chip"
                  title={`Requirements applied ${new Date(a).toLocaleDateString()}`}
                >
                  ✓ Requirements in sync
                </span>
              );
            })()}
            {step === 4 && (
              <button
                type="button"
                onClick={() => {
                  // Synthesize a ⌘K event to open the palette — keeps the keyboard
                  // shortcut as the single source of truth for the open/close logic.
                  const e = new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true });
                  window.dispatchEvent(e);
                }}
                className="hidden sm:inline-flex items-center gap-1.5 text-[10px] text-slate-400 hover:text-[#0F2040] transition-colors group"
                data-testid="cmdk-hint-button"
                title="Open the component command palette"
              >
                <span>Quick search</span>
                <span className="inline-flex items-center gap-0.5">
                  <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 group-hover:border-slate-300 transition-colors font-mono text-[9px] text-slate-500">⌘</kbd>
                  <kbd className="px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 group-hover:border-slate-300 transition-colors font-mono text-[9px] text-slate-500">K</kbd>
                </span>
              </button>
            )}
            <button onClick={() => { resetGuides(); setGuideResetKey(k => k + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); toast.success('Guides restored'); }}
              className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="reset-guides-btn">
              <HelpCircle className="w-3 h-3" /> Show Guides
            </button>
          </div>
        </div>
      </div>

      {/* Workspace-style page header (Step 4 only) — rendered OUTSIDE the
          page <main> wrapper so its white background and bottom
          border span the full viewport width, matching the Planner /
          Full Report / Quote pages. The inner row uses full-bleed
          padding so the heading + metrics stay aligned with the
          page content below. WorkspaceSubNav also goes full-width here
          (its own internal layout handles centering). */}
      {step === 4 && (
        <div className="bg-white border-b border-slate-200" data-testid="estimator-workspace-header">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <button
                type="button"
                onClick={() => goToStep(3)}
                className="shrink-0 p-1.5 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                title="Back to complexity step"
                data-testid="step4-back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0 flex-1">
                {editingName ? (
                  <Input
                    autoFocus
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    onBlur={() => {
                      const next = (draftName || '').trim();
                      if (next && next !== estimateName) {
                        setEstimateName(next);
                        persistRename(next);
                      }
                      setEditingName(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const next = (draftName || '').trim();
                        if (next && next !== estimateName) {
                          setEstimateName(next);
                          persistRename(next);
                        }
                        setEditingName(false);
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setDraftName(estimateName);
                        setEditingName(false);
                      }
                    }}
                    placeholder={autoGenerateName()}
                    className="h-8 text-lg font-semibold w-full max-w-md border-[#4ABFCF] focus:border-[#4ABFCF] font-['Outfit']"
                    data-testid="estimator-name-input"
                  />
                ) : (
                  <h1
                    className="text-lg font-semibold text-[#0F2040] truncate font-['Outfit'] flex items-center gap-2 group"
                    data-testid="estimator-workspace-name"
                  >
                    <button
                      type="button"
                      onClick={() => { setDraftName(estimateName || autoGenerateName() || ''); setEditingName(true); }}
                      className="text-left truncate hover:text-[#4ABFCF] transition-colors flex items-center gap-1.5"
                      title="Click to rename"
                      data-testid="estimator-name-edit-btn"
                    >
                      <span className="truncate">{estimateName?.trim() || autoGenerateName() || 'Untitled estimate'}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#4ABFCF] opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                        aria-hidden="true"
                      >
                        <path d="M2.695 14.763l-1.262 3.154a.5.5 0 00.65.65l3.155-1.262a4 4 0 001.343-.885L17.5 5.5a2.121 2.121 0 00-3-3L3.58 13.42a4 4 0 00-.885 1.343z" />
                      </svg>
                    </button>
                    {savedEstimateId && savedInfo?.version != null && (
                      <span className="inline-block align-middle">
                        <EstimateVersionChip
                          estimateId={savedEstimateId}
                          versionNumber={savedInfo.version}
                          onRestored={() => window.location.reload()}
                        />
                      </span>
                    )}
                    {savedEstimateId && (
                      <span className="inline-block align-middle ml-2">
                        <EstimateLockControl estimateId={savedEstimateId} onChange={setEffortCostLock} refreshKey={savedInfo?.status || ''} />
                      </span>
                    )}
                    {savedEstimateId && (
                      <span className="inline-block align-middle ml-2">
                        <EstimateStatusSelect
                          estimateId={savedEstimateId}
                          currentStatus={savedInfo?.status || 'draft'}
                          overrideBy={savedInfo?.status_override_by}
                          overrideAt={savedInfo?.status_override_at}
                          onChange={(r) => {
                            // Patch local state without a full reload so the
                            // header reflects the new status immediately.
                            setSavedInfo((prev) => ({
                              ...(prev || {}),
                              status: r?.status || null,
                              status_override_by: r?.override_by_name || null,
                              status_override_at: r?.override_at || null,
                            }));
                          }}
                          testIdPrefix="estimator-status"
                        />
                      </span>
                    )}
                  </h1>
                )}
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {orgName && (
                    <span className="text-[10px] font-medium text-slate-400 tracking-wide uppercase" data-testid="step4-org-name">{orgName}</span>
                  )}
                  <Badge variant="outline" className="text-[10px] py-0" data-testid="selected-impl-type">
                    {IMPLEMENTATION_TYPES.find(t => t.id === implementationType)?.title}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] py-0 capitalize" data-testid="selected-complexity">
                    {complexity}
                  </Badge>
                  {implName && <span className="text-[11px] text-slate-500 truncate" data-testid="step4-impl-name">{implName}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              {results && (
                <>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-slate-400 text-[10px] justify-end">
                      <Clock className="w-3 h-3" />
                      Effort
                    </div>
                    <p className="text-sm font-bold text-[#0F2040] font-['Outfit']" data-testid="estimator-header-effort">
                      {Math.round(results.total_effort || 0).toLocaleString()} hrs
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-slate-400 text-[10px] justify-end">
                      <DollarSign className="w-3 h-3" />
                      Cost
                    </div>
                    <p className="text-sm font-bold text-[#4ABFCF] font-['Outfit']" data-testid="estimator-header-cost">
                      {currencySymbol}{Math.round(results.total_cost || 0).toLocaleString()}
                    </p>
                  </div>
                </>
              )}
              {user?.role !== 'user' && (
              <Button
                variant="outline"
                size="sm"
                onClick={resetEstimate}
                className="text-slate-600 hover:text-red-600 hover:border-red-300"
                data-testid="start-over-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Start Over
              </Button>
              )}
            </div>
          </div>
          {(savedEstimateId || hasConditions) && (
            <WorkspaceSubNav
              estimateId={savedEstimateId}
              activeTab="estimate"
              hasConditions={hasConditions}
              onProjectConditionsClick={() => setEditConditionsOpen(true)}
              onNavigate={(tab) => navigateWithSaveGate(tab.getPath('{id}'))}
              customerName={orgName}
              projectName={implName}
              progress={computeEstimateProgress({
                has_conditions: hasConditions,
                results: results,
                requirements_doc: null,
              })}
            />
          )}
        </div>
      )}

      <main className="w-full px-4 sm:px-6 lg:px-8 py-6">
        {/* Step 1 */}
        {step === 1 && (
          <WizardStep1
            step={step} animKey={animKey} stepAnimClass={stepAnimClass} guideResetKey={guideResetKey}
            showProjectForm={showProjectForm} setShowProjectForm={setShowProjectForm}
            orgName={orgName} setOrgName={setOrgName} implName={implName} setImplName={setImplName}
            implDescription={implDescription} setImplDescription={setImplDescription}
            targetAudience={targetAudience} setTargetAudience={setTargetAudience}
            orgMode={orgMode} setOrgMode={setOrgMode} selectedOrgId={selectedOrgId} setSelectedOrgId={setSelectedOrgId}
            organizations={organizations} canCreateOrg={canCreateOrg}
            newOrgName={newOrgName} handleNewOrgNameChange={handleNewOrgNameChange}
            fuzzyLoading={fuzzyLoading} fuzzyMatches={fuzzyMatches} handleFuzzySelect={handleFuzzySelect}
            newOrgIndustry={newOrgIndustry} setNewOrgIndustry={setNewOrgIndustry}
            newOrgCountry={newOrgCountry} setNewOrgCountry={setNewOrgCountry}
            newOrgContactEmail={newOrgContactEmail} setNewOrgContactEmail={setNewOrgContactEmail}
            orgLookups={orgLookups} handleInlineOrgCreate={handleInlineOrgCreate} creatingOrg={creatingOrg}
            pendingTemplate={pendingTemplate} setPendingTemplate={setPendingTemplate}
            templates={templates} applyTemplate={applyTemplate} goToStep={goToStep}
            scopeTemplates={scopeTemplates} applyScopeTemplate={applyScopeTemplate}
            setComponentDeliverables={setComponentDeliverables}
            setSelectedTemplate={setSelectedTemplate} setImplementationType={setImplementationType}
            setComplexity={setComplexity} setModifiers={setModifiers} setSelectedComponents={setSelectedComponents}
            setStep={setStep} setStepDirection={setStepDirection} setAnimKey={setAnimKey}
            launchQuickStart={launchQuickStart} setFuzzyMatches={setFuzzyMatches} setNewOrgName={setNewOrgName}
          />
        )}

        {/* Step 2 */}
        {step === 2 && (
          <WizardStep2
            step={step} animKey={animKey} stepAnimClass={stepAnimClass} guideResetKey={guideResetKey}
            implementationType={implementationType} setImplementationType={setImplementationType}
            goToStep={goToStep} setShowTypeInfo={setShowTypeInfo}
          />
        )}

        {/* Step 3 */}
        {step === 3 && (
          <WizardStep3
            step={step} animKey={animKey} stepAnimClass={stepAnimClass} guideResetKey={guideResetKey}
            complexity={complexity} setComplexity={setComplexity}
            hostingModel={hostingModel} setHostingModel={setHostingModel}
            goToStep={goToStep} setShowHostingInfo={setShowHostingInfo}
            setShowComplexityLevelInfo={setShowComplexityLevelInfo}
          />
        )}

        {/* Step 4: Components & Results */}
        {step === 4 && (
          <div key={`step4-${animKey}`} className={stepAnimClass} data-testid="wizard-step-4">
            {/* Section heading — workspace header is hoisted to render
                outside <main> so its white background spans the full
                viewport (matches Planner / Full Report / Quote). */}
            <SectionGuide id="estimator-configure" title="Components, Factors & Results" accent="#14B8A6" resetKey={guideResetKey}>
              <p>
                This is the heart of the estimator. On the <strong>left</strong>, walk through each component group (Consulting, Core Config, Integrations, etc.) &mdash; toggle items on/off, adjust quantities, and override default hours where needed. The <strong>Project Factors</strong> tab lets you tune readiness and complexity sliders that multiply effort across the board.
              </p>
              <p>
                On the <strong>right</strong>, the results panel updates live as you make changes &mdash; showing total cost, effort by role, a confidence score, and a visual breakdown by group. When you&rsquo;re satisfied, <em>save</em> the estimate, <em>export</em> it in multiple formats, or generate an <em>AI narrative</em> summarizing the scope and risks.
              </p>
            </SectionGuide>

            {implementationType === 'Project' && (
              <div className="mb-4 flex flex-wrap items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5"
                data-testid="project-kind-panel">
                <span className="text-xs font-semibold text-amber-800">Project estimate — à la carte components</span>
                <span className="text-[11px] text-amber-700">No components are pre-selected; pick exactly what this project needs. Baseline hours come from the Simple Corporate structure.</span>
                <span className="flex-1" />
                <span className="text-[11px] font-medium text-slate-600">Quick picks:</span>
                {(quickPicks.length ? quickPicks : DEFAULT_QUICK_PICKS).map(qp => (
                  <button key={qp.id || qp.label} type="button"
                    title={`Adds: ${qp.components.join(', ')}${qp.hours ? ' — with suggested hours' : ''} (existing selections kept)`}
                    onClick={() => {
                      setSelectedComponents(prev => {
                        const next = { ...prev };
                        qp.components.forEach(n => { if (components.some(c => c.name === n)) next[n] = next[n] || 1; });
                        return next;
                      });
                      if (qp.hours && Object.keys(qp.hours).length) {
                        setHourOverrides(prev => {
                          const next = { ...prev };
                          Object.entries(qp.hours).forEach(([n, h]) => {
                            if (components.some(c => c.name === n)) next[n] = h;
                          });
                          return next;
                        });
                      }
                      toast.success(`${qp.label} bundle added${qp.hours ? ' with suggested hours' : ''} — adjust anything you need`);
                    }}
                    className="px-2 py-0.5 text-[10px] font-semibold rounded-full border border-amber-300 bg-white text-amber-800 hover:bg-amber-100 transition-colors"
                    data-testid={`project-quickpick-${qp.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                    + {qp.label}
                  </button>
                ))}
                {['admin', 'super_admin'].includes(user?.role) && (
                  <button type="button" onClick={() => setQuickPickManagerOpen(true)}
                    title="Manage quick-pick bundles"
                    className="p-1 rounded-full text-amber-700 hover:bg-amber-100 transition-colors"
                    data-testid="quickpick-manage-btn">
                    <Settings2 className="w-3.5 h-3.5" />
                  </button>
                )}
                <span className="text-[11px] font-medium text-slate-600 ml-2">Classification:</span>
                <div className="flex rounded-md overflow-hidden border border-amber-300">
                  <button type="button" onClick={() => { projectKindTouchedRef.current = true; setProjectKind('customer'); }}
                    className={`px-3 py-1 text-[11px] font-semibold transition-colors ${projectKind !== 'rnd' ? 'bg-[#0F2040] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                    data-testid="project-kind-customer">Customer Project</button>
                  <button type="button" onClick={() => { projectKindTouchedRef.current = true; setProjectKind('rnd'); }}
                    className={`px-3 py-1 text-[11px] font-semibold transition-colors ${projectKind === 'rnd' ? 'bg-[#0F2040] text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                    data-testid="project-kind-rnd">Seertech R&D</button>
                </div>
                {projectKind === 'rnd' && (
                  <select
                    value={rndCategory}
                    onChange={e => setRndCategory(e.target.value)}
                    className="text-[11px] border border-amber-300 rounded-md px-2 py-1 bg-white text-amber-800 font-medium"
                    title="R&D category — drives the category breakdown on the R&D insights tab"
                    data-testid="rnd-category-select">
                    {rndCategories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                )}
                <QuickPickManagerDialog
                  open={quickPickManagerOpen}
                  onOpenChange={setQuickPickManagerOpen}
                  quickPicks={quickPicks.length ? quickPicks : []}
                  components={components}
                  onChanged={loadQuickPicks}
                />
              </div>
            )}

            <SmartDefaultsBanner
              selectedComponents={selectedComponents}
              onDeselect={(name) => {
                const comp = components.find(c => c.name === name);
                if (comp?.required && implementationType !== 'Project') return;   // never deselect locked components
                setSelectedComponents(prev => {
                  const next = { ...prev }; delete next[name]; return next;
                });
              }}
            />

            <RateDriftBanner
              drift={rateDrift}
              currencySymbol={currencySymbol}
              onReprice={repriceAtTodaysRates}
              onDismiss={() => setRateDrift(null)}
            />

            <div className="grid lg:grid-cols-12 gap-8">
              {/* Left: Components & Modifiers */}
              <div className="lg:col-span-8 space-y-6">
                <Card>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <CardTitle className="text-xl font-semibold text-[#0F2040] font-['Outfit']">Select Components</CardTitle>
                        <p className="text-sm text-slate-500">{implementationType === 'Project' ? 'À la carte — pick exactly the components this project needs' : 'Recommended baseline items are pre-selected'}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCloneSkeletonOpen(true)}
                          className="text-xs border-dashed border-cyan-300 text-cyan-700 hover:bg-cyan-50 hover:border-cyan-400"
                          data-testid="clone-skeleton-open-btn"
                          title="Bulk-import milestone deliverables from a similar past estimate"
                        >
                          <Library className="w-3.5 h-3.5 mr-1" />
                          Clone scope skeleton
                        </Button>
                        {savedEstimateId && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSaveAsTemplateOpen(true)}
                            className="text-xs border-dashed border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400"
                            data-testid="save-as-template-open-btn"
                            title="Promote this estimate's scope shape into a reusable template"
                          >
                            <BookmarkPlus className="w-3.5 h-3.5 mr-1" />
                            Save as Template
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Tabs value={activeTab || Object.keys(groupedComponents)[0]} onValueChange={handleTabChange} className="w-full">
                      {/* Mini Wizard Stepper */}
                      <div className="mb-6" data-testid="mini-wizard-stepper">
                        <div className="flex items-start justify-center w-full overflow-visible py-2 px-8">
                          {allTabs.map((tab, idx) => {
                            const isActive = activeTab === tab;
                            const isVisited = visitedTabs.has(tab) && !isActive;
                            const Icon = GROUP_ICONS[tab] || (tab === 'project-factors' ? Sparkles : Settings);
                            const label = getGroupLabel(tab, 'stepper');
                            const shortLabel = label.length > 14 ? label.split(' ').map(w => w[0]).join('') : label;
                            // Only wrap/break words for long multi-word labels. Single-word or short
                            // labels (≤12 chars) stay on one line so words like "Integration" don't
                            // break mid-word into "Integrati-on".
                            const longestWord = label.split(/\s+/).reduce((a, b) => (a.length > b.length ? a : b), '');
                            const needsWrap = label.length > 12 || longestWord.length > 12;
                            return (
                              <React.Fragment key={tab}>
                                {idx > 0 && (
                                  <div className={`shrink h-0.5 w-2 min-w-[6px] mt-[18px] transition-colors duration-300 ${
                                    isVisited || isActive ? 'bg-[#0F2040]' : 'bg-slate-200'
                                  }`} />
                                )}
                                <button
                                  onClick={() => handleTabChange(tab)}
                                  data-testid={`mini-step-${tab}`}
                                  className="shrink flex flex-col items-center gap-1.5 group cursor-pointer min-w-0"
                                  style={{ flex: '1 1 0', minWidth: 0, maxWidth: '110px' }}
                                >
                                  <div className={`relative flex items-center justify-center w-9 h-9 rounded-full border-2 transition-all duration-300 shrink-0 ${
                                    isActive ? 'bg-[#0F2040] border-[#0F2040] text-white shadow-lg shadow-[#0F2040]/25'
                                      : isVisited ? 'bg-emerald-50 border-emerald-500 text-emerald-600'
                                        : 'bg-white border-slate-300 text-slate-400 group-hover:border-slate-500 group-hover:text-slate-500'
                                  }`}>
                                    {isVisited ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                                  </div>
                                  <span className={`block w-full px-0.5 text-[9px] font-medium text-center leading-[1.15] min-h-[26px] line-clamp-3 transition-colors duration-200 ${
                                    needsWrap ? 'break-words hyphens-auto' : ''
                                  } ${
                                    isActive ? 'text-[#0F2040] font-semibold' : isVisited ? 'text-emerald-600' : 'text-slate-400'
                                  }`} title={label}>
                                    <span className="hidden md:inline">{label}</span>
                                    <span className="md:hidden">{shortLabel}</span>
                                  </span>
                                </button>
                              </React.Fragment>
                            );
                          })}
                        </div>
                        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-[#0F2040] to-[#2563EB] rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${((allTabs.indexOf(activeTab) + 1) / allTabs.length) * 100}%` }}
                            data-testid="mini-wizard-progress-bar" />
                        </div>
                        <div className="flex justify-between mt-1">
                          <span className="text-[10px] text-slate-400">Section {allTabs.indexOf(activeTab) + 1} of {allTabs.length}</span>
                          <span className="text-[10px] text-slate-400">{Math.round(((allTabs.indexOf(activeTab) + 1) / allTabs.length) * 100)}% complete</span>
                        </div>
                      </div>
                      
                      {/* Component Group Tabs */}
                      {Object.entries(groupedComponents).map(([group, comps]) => {
                        const subGroups = {};
                        const noSubGroup = [];
                        comps.forEach(comp => {
                          if (comp.sub_group) {
                            if (!subGroups[comp.sub_group]) subGroups[comp.sub_group] = [];
                            subGroups[comp.sub_group].push(comp);
                          } else { noSubGroup.push(comp); }
                        });
                        // Summary pill above groups where deselection matters —
                        // surfaces "N of M recommended · X hrs" so consultants
                        // get an instant read on how much effort the default is
                        // carrying before deselecting. Enabled for groups whose
                        // baseline is "pre-selected but optional".
                        const SUMMARY_GROUPS = {
                          'Data Migration': 'deselect any that don\'t apply to this rollout',
                          'Integrations': 'only enable the ones this rollout actually needs',
                        };
                        const summaryHint = SUMMARY_GROUPS[group];
                        const recommendedInGroup = summaryHint
                          ? comps.filter((c) => c.recommended || c.required)
                          : [];
                        const selectedRecommendedCount = recommendedInGroup
                          .filter((c) => selectedComponents[c.name]).length;
                        const groupHrs = summaryHint
                          ? Math.round(results?.effort_by_group?.[group] || 0)
                          : 0;
                        // Count per-component planner start-week / weeks overrides active
                        // within this group so we can show a "Clear overrides" link.
                        const groupOverrideCount = PLANNER_OVERRIDE_GROUPS.has(group)
                          ? comps.filter(c => selectedComponents[c.name] && (plannerStartOverrides[c.name] || plannerWeeksOverrides[c.name])).length
                          : 0;
                        return (
                          <TabsContent key={group} value={group}>
                            {summaryHint && recommendedInGroup.length > 0 && (
                              <div
                                className="mb-4 flex items-center gap-2 rounded-md border border-[#4ABFCF]/30 bg-[#4ABFCF]/5 px-3 py-2 text-[11px]"
                                data-testid={`group-summary-pill-${group.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <Info className="w-3.5 h-3.5 text-[#4ABFCF] shrink-0" />
                                <span className="text-[#0F2040] font-medium">
                                  {group}:
                                  {' '}
                                  {groupHrs > 0
                                    ? `${groupHrs.toLocaleString()} hrs`
                                    : '—'}
                                  {' '}across {selectedRecommendedCount} of{' '}
                                  {recommendedInGroup.length} recommended components
                                </span>
                                <span className="text-slate-500 ml-1">· {summaryHint}</span>
                              </div>
                            )}
                            {groupOverrideCount > 0 && (
                              <div
                                className="mb-3 flex items-center gap-2 rounded-md border border-cyan-200/60 bg-cyan-50/50 px-3 py-1.5 text-[11px]"
                                data-testid={`group-override-pill-${group.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <CalendarDays className="w-3.5 h-3.5 text-cyan-600 shrink-0" />
                                <span className="text-cyan-900 font-medium">
                                  {groupOverrideCount} component{groupOverrideCount === 1 ? '' : 's'} in {group} ha{groupOverrideCount === 1 ? 's' : 've'} a user-defined schedule.
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPlannerStartOverrides(prev => {
                                      const next = { ...prev };
                                      comps.forEach(c => { delete next[c.name]; });
                                      return next;
                                    });
                                    setPlannerWeeksOverrides(prev => {
                                      const next = { ...prev };
                                      comps.forEach(c => { delete next[c.name]; });
                                      return next;
                                    });
                                  }}
                                  className="ml-auto text-[10px] font-medium text-cyan-700 hover:text-cyan-900 underline decoration-dotted underline-offset-2"
                                  title={`Reset ${groupOverrideCount} schedule override${groupOverrideCount === 1 ? '' : 's'} to auto-schedule`}
                                  data-testid={`clear-start-overrides-${group.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  Clear {groupOverrideCount} override{groupOverrideCount === 1 ? '' : 's'}
                                </button>
                              </div>
                            )}
                            <div className="grid gap-3">
                              {noSubGroup.map(renderComp)}
                              {Object.entries(subGroups).map(([subGroup, subComps]) => (
                                <div key={subGroup}>
                                  <div className="flex items-center gap-2 mt-4 mb-2 px-1">
                                    <div className="w-1.5 h-1.5 rounded-full bg-[#4ABFCF]" />
                                    <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">{subGroup}</h5>
                                  </div>
                                  {subComps.map(renderComp)}
                                </div>
                              ))}
                            </div>
                          </TabsContent>
                        );
                      })}

                      {/* New Development Tab */}
                      <TabsContent value="new-development" data-testid="new-development-content">
                        <div className="space-y-6">
                          {newDevelopments.length > 0 && (() => {
                            // Summary pill — total change-request count + hours
                            // from the backend calculation. Keeps the New Dev
                            // tab consistent with Data Migration / Integrations
                            // where "count + hours" is the at-a-glance header.
                            const newDevHrs = Math.round(
                              (results?.new_developments || [])
                                .reduce((a, d) => a + (d.final_effort || 0), 0)
                            );
                            const newDevOverrideCount = newDevelopments.filter(d => d.planner_start_week || d.planner_weeks).length;
                            return (
                              <div
                                className="flex items-center gap-2 rounded-md border border-violet-300/50 bg-violet-50/50 px-3 py-2 text-[11px]"
                                data-testid="new-development-summary-pill"
                              >
                                <Info className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                                <span className="text-violet-900 font-medium">
                                  New Development:
                                  {' '}
                                  {newDevHrs > 0 ? `${newDevHrs.toLocaleString()} hrs` : '—'}
                                  {' '}across {newDevelopments.length} change request{newDevelopments.length === 1 ? '' : 's'}
                                </span>
                                <span className="text-slate-500 ml-1">
                                  · add, resize, or remove below
                                </span>
                                {newDevOverrideCount > 0 && (
                                  <button
                                    onClick={() => {
                                      setNewDevelopments(prev => prev.map(d => ({ ...d, planner_start_week: null, planner_weeks: null })));
                                    }}
                                    className="ml-auto text-[10px] font-medium text-cyan-700 hover:text-cyan-900 underline decoration-dotted underline-offset-2"
                                    title={`Reset ${newDevOverrideCount} schedule override${newDevOverrideCount === 1 ? '' : 's'} to auto-schedule`}
                                    data-testid="clear-start-overrides-new-development"
                                  >
                                    Clear {newDevOverrideCount} override{newDevOverrideCount === 1 ? '' : 's'}
                                  </button>
                                )}
                              </div>
                            );
                          })()}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">New Development Work</h5>
                              <button onClick={() => setShowFactorInfo(FACTOR_INFO.new_development)} className="text-slate-400 hover:text-[#4ABFCF] transition-colors cursor-help" data-testid="factor-info-new-development">
                                <Info className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <Button size="sm" className="bg-[#4ABFCF] hover:bg-[#3AAAB9]" onClick={() => setNewDevelopments(prev => [...prev, { _key: (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`), name: '', size: 'medium', hour_override: null, planner_start_week: null, planner_weeks: null }])} data-testid="add-new-dev-btn">
                              <Plus className="w-3.5 h-3.5 mr-1" /> Add Development Item
                            </Button>
                          </div>
                          {newDevelopments.length === 0 ? (
                            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center" data-testid="new-dev-empty">
                              <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-3">
                                <Code className="w-5 h-5 text-violet-400" />
                              </div>
                              <p className="text-sm text-slate-500 mb-1">No custom development items</p>
                              <p className="text-xs text-slate-400">Click "Add Development Item" above if this estimate includes bespoke features, custom integrations, or API connectors.</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {newDevelopments.map((dev, idx) => {
                                const sizeConfig = { small: { hours: 24, fte: 1.0 }, medium: { hours: 60, fte: 1.2 }, large: { hours: 120, fte: 1.4 } };
                                const sc = sizeConfig[dev.size] || sizeConfig.medium;
                                const effectiveHours = (dev.hour_override && dev.hour_override > 0) ? dev.hour_override : sc.hours;
                                return (
                                  <div key={dev._key || idx} className="border border-violet-200 bg-violet-50/30 rounded-xl p-4 space-y-3" data-testid={`new-dev-item-${idx}`}>
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 space-y-3">
                                        <div>
                                          <Label className="text-xs text-slate-500 mb-1 block">Development Name</Label>
                                          <Input placeholder="e.g., Custom API Connector, Bespoke Reporting..." value={dev.name}
                                            onChange={(e) => { const updated = [...newDevelopments]; updated[idx] = { ...dev, name: e.target.value }; setNewDevelopments(updated); }}
                                            className="text-sm" data-testid={`new-dev-name-${idx}`} />
                                        </div>
                                        <div className="grid grid-cols-4 gap-3">
                                          <div>
                                            <Label className="text-xs text-slate-500 mb-1 block">Size / Complexity</Label>
                                            <select value={dev.size}
                                              onChange={(e) => { const updated = [...newDevelopments]; updated[idx] = { ...dev, size: e.target.value, hour_override: null }; setNewDevelopments(updated); }}
                                              className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid={`new-dev-size-${idx}`}>
                                              <option value="small">Small (24h, 1.0 DE FTE)</option>
                                              <option value="medium">Medium (60h, 1.2 DE FTE)</option>
                                              <option value="large">Large (120h, 1.4 DE FTE)</option>
                                            </select>
                                          </div>
                                          <div>
                                            <Label className="text-xs text-slate-500 mb-1 block">Hours Override</Label>
                                            <Input type="number" min="1" placeholder={`${sc.hours}h default`} value={dev.hour_override || ''}
                                              onChange={(e) => { const updated = [...newDevelopments]; updated[idx] = { ...dev, hour_override: e.target.value ? parseFloat(e.target.value) : null }; setNewDevelopments(updated); }}
                                              className="text-sm" data-testid={`new-dev-hours-${idx}`} />
                                          </div>
                                          <div>
                                            <Label className="text-xs text-slate-500 mb-1 block">Planner Start Week</Label>
                                            <Input type="number" min="1" placeholder="Auto" value={dev.planner_start_week || ''}
                                              onChange={(e) => { const updated = [...newDevelopments]; updated[idx] = { ...dev, planner_start_week: e.target.value ? parseInt(e.target.value) : null }; setNewDevelopments(updated); }}
                                              className="text-sm" data-testid={`new-dev-start-week-${idx}`} />
                                          </div>
                                          <div>
                                            <Label className="text-xs text-slate-500 mb-1 block" title="Optional. How many weeks this dev item runs for. Leave blank to auto-size from hours and DE FTE.">Planner Weeks</Label>
                                            <Input type="number" min="1" placeholder="Auto" value={dev.planner_weeks || ''}
                                              onChange={(e) => { const updated = [...newDevelopments]; updated[idx] = { ...dev, planner_weeks: e.target.value ? parseInt(e.target.value) : null }; setNewDevelopments(updated); }}
                                              className="text-sm" data-testid={`new-dev-weeks-${idx}`} />
                                            {(() => {
                                              const w = dev.planner_weeks;
                                              const s = dev.planner_start_week || 1;
                                              const horizon = results?.projected_weeks;
                                              if (!w || !horizon || s + w - 1 <= horizon) return null;
                                              const fitted = Math.max(1, horizon - s + 1);
                                              return (
                                                <p className="text-[10px] text-amber-700 font-medium mt-1" data-testid={`new-dev-weeks-clamp-${idx}`}>
                                                  will be trimmed to {fitted}w
                                                </p>
                                              );
                                            })()}
                                          </div>
                                        </div>
                                        {/* v258.3 — release-anchored scheduling. Selecting a release +
                                            delivery weeks computes planner_start_week and planner_weeks
                                            so the dev item finishes ON the release date. Tracked via
                                            anchor_release_id / anchor_lead_weeks for re-display. */}
                                        <div className="mt-2">
                                          <ReleaseAnchorPicker
                                            releases={publicCloudReleases}
                                            projectStartDate={goalStartDateForComponentSchedule || preferredStartDate || null}
                                            anchorReleaseId={dev.anchor_release_id || null}
                                            anchorLeadWeeks={dev.anchor_lead_weeks || null}
                                            testidPrefix={`new-dev-anchor-${idx}`}
                                            onAnchor={({ release_id, lead_weeks, start_week, weeks }) => {
                                              const updated = [...newDevelopments];
                                              updated[idx] = {
                                                ...dev,
                                                anchor_release_id: release_id,
                                                anchor_lead_weeks: lead_weeks,
                                                planner_start_week: start_week ?? dev.planner_start_week,
                                                planner_weeks: weeks ?? dev.planner_weeks,
                                              };
                                              setNewDevelopments(updated);
                                            }}
                                            onClear={() => {
                                              const updated = [...newDevelopments];
                                              updated[idx] = { ...dev, anchor_release_id: null, anchor_lead_weeks: null };
                                              setNewDevelopments(updated);
                                            }}
                                          />
                                        </div>
                                      </div>
                                      <Button variant="ghost" size="sm" className="text-slate-400 hover:text-rose-500 mt-5 shrink-0"
                                        onClick={() => setNewDevelopments(prev => prev.filter((_, i) => i !== idx))} data-testid={`new-dev-remove-${idx}`}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                    <div className="flex gap-3 text-xs">
                                      <span className="text-violet-600 bg-violet-100 px-2 py-0.5 rounded">{effectiveHours}h effort</span>
                                      <span className="text-slate-500">{sc.fte} DE FTE</span>
                                      {dev.size === 'large' && <span className="text-rose-600 bg-rose-50 px-2 py-0.5 rounded">High risk</span>}
                                    </div>
                                  </div>
                                );
                              })}
                              {newDevelopments.length > 0 && results?.new_dev_overhead && (
                                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-xs space-y-1" data-testid="new-dev-summary">
                                  <p className="font-semibold text-violet-800">Overhead Impact from New Development:</p>
                                  <div className="grid grid-cols-4 gap-2 text-violet-700">
                                    <span>PM: +{results.new_dev_overhead.pm_hours}h</span>
                                    <span>Testing: +{results.new_dev_overhead.testing_hours}h</span>
                                    <span>Training: +{results.new_dev_overhead.training_hours}h</span>
                                    <span>Documentation: +{results.new_dev_overhead.documentation_hours}h</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      {/* Integrations Tab */}
                      <TabsContent value="integration-scope" data-testid="integration-scope-content">
                        <div className="space-y-4">
                          {(() => {
                            // Summary pill — how many integration types the
                            // consultant has enabled so far. Hours live on
                            // the "Integrations" group tab (component-level
                            // configuration), so this pill just surfaces the
                            // scope count to match the pattern elsewhere.
                            const enabledCount = (integrations || []).filter((i) => i.enabled).length;
                            if (enabledCount === 0) return null;
                            return (
                              <div
                                className="flex items-center gap-2 rounded-md border border-purple-300/50 bg-purple-50/50 px-3 py-2 text-[11px]"
                                data-testid="integration-scope-summary-pill"
                              >
                                <Info className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                                <span className="text-purple-900 font-medium">
                                  Integration Scope: {enabledCount} integration{enabledCount === 1 ? '' : 's'} enabled
                                </span>
                                <span className="text-slate-500 ml-1">
                                  · hours are calculated on the Integrations tab
                                </span>
                              </div>
                            );
                          })()}
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                            <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Integrations in Scope</h5>
                          </div>
                          <p className="text-sm text-slate-500 mb-4">Select integrations required for this implementation. These will be included in the Quote, Planner, and SOW.</p>
                          <div className="grid grid-cols-2 gap-3">
                            {[
                              { type: 'sso', label: 'SSO Integration' },
                              { type: 'hris', label: 'HRIS Integration' },
                              { type: 'authoring_tool', label: 'Authoring Tool Integration' },
                              { type: 'user_service', label: 'User Service Integration' },
                              { type: 'virtual_class', label: 'Virtual Class Integration' },
                              { type: 'content_library', label: 'Content Library Integration' },
                              { type: 'payment_gateway', label: 'Payment Gateway Integration' },
                              { type: 'order_management', label: 'Order Management Integration' },
                              { type: 'tax_service', label: 'Tax Service Integration' },
                              { type: 'caas', label: 'CaaS Integration' },
                              { type: 'badging', label: 'Badging Integration' },
                              { type: 'custom', label: 'Custom / Non-OOB Integration' },
                            ].map(intType => {
                              const existing = integrations.find(i => i.type === intType.type);
                              const isEnabled = existing?.enabled || false;
                              return (
                                <div key={intType.type}
                                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                    isEnabled ? 'border-[#4ABFCF] bg-[#4ABFCF]/5' : 'border-slate-200 hover:border-slate-300'
                                  }`}
                                  onClick={() => {
                                    setIntegrations(prev => {
                                      const exists = prev.find(i => i.type === intType.type);
                                      if (exists) return prev.map(i => i.type === intType.type ? { ...i, enabled: !i.enabled } : i);
                                      return [...prev, { type: intType.type, label: intType.label, enabled: true, notes: '' }];
                                    });
                                  }}
                                  data-testid={`integration-${intType.type}`}
                                >
                                  <input type="checkbox" checked={isEnabled} readOnly className="mt-0.5 rounded border-slate-300 text-[#4ABFCF] focus:ring-[#4ABFCF]" />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-medium text-slate-700">{intType.label}</span>
                                    {isEnabled && (
                                      <input type="text" value={existing?.notes || ''}
                                        onChange={(e) => { e.stopPropagation(); setIntegrations(prev => prev.map(i => i.type === intType.type ? { ...i, notes: e.target.value } : i)); }}
                                        onClick={(e) => e.stopPropagation()}
                                        placeholder="Notes (optional)"
                                        className="mt-1 w-full text-xs bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#4ABFCF]"
                                        data-testid={`integration-notes-${intType.type}`} />
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className="mt-3 text-xs text-slate-400">
                            {integrations.filter(i => i.enabled).length} integration(s) selected
                          </div>
                        </div>
                      </TabsContent>

                      {/* Project Factors Tab */}
                      <TabsContent value="project-factors" data-testid="project-factors-content">
                        <div className="space-y-8">
                          {/* Composite impact banner — shows the cumulative
                              effort delta from all 7 readiness/complexity
                              factors stacked. Data Integrity is reported
                              separately because it only scales Migration
                              components. */}
                          {(() => {
                            const composite = (
                              (modifiers.governance ?? OPTIMAL_MODIFIER_VALUES.governance) *
                              (modifiers.content_readiness ?? OPTIMAL_MODIFIER_VALUES.content_readiness) *
                              (modifiers.integration_complexity ?? OPTIMAL_MODIFIER_VALUES.integration_complexity) *
                              (modifiers.org_complexity ?? OPTIMAL_MODIFIER_VALUES.org_complexity) *
                              (modifiers.timeline_pressure ?? OPTIMAL_MODIFIER_VALUES.timeline_pressure) *
                              (modifiers.client_capability ?? OPTIMAL_MODIFIER_VALUES.client_capability) *
                              (modifiers.change_readiness ?? OPTIMAL_MODIFIER_VALUES.change_readiness)
                            );
                            const compositePct = (composite - 1.0) * 100;
                            const isNeutral = Math.abs(compositePct) < 0.5;
                            const isSaving = compositePct < 0;
                            const sign = isSaving ? '' : '+';
                            const abs = Math.abs(compositePct);
                            const formatted = abs < 1 ? abs.toFixed(1) : Math.round(abs);
                            const diPct = ((modifiers.data_integrity ?? OPTIMAL_MODIFIER_VALUES.data_integrity) - 1.0) * 100;
                            const diIsSaving = diPct < 0;
                            const diSign = diIsSaving ? '' : '+';
                            const diFormatted = Math.abs(diPct) < 1 ? Math.abs(diPct).toFixed(1) : Math.round(Math.abs(diPct));
                            const diNeutral = Math.abs(diPct) < 0.5;
                            const tone = isNeutral
                              ? 'border-slate-200 bg-slate-50/60 text-slate-700'
                              : isSaving
                                ? 'border-emerald-300 bg-emerald-50/60 text-emerald-900'
                                : 'border-amber-300 bg-amber-50/60 text-amber-900';
                            return (
                              <div
                                className={`rounded-xl border px-4 py-3 flex items-center gap-3 ${tone}`}
                                data-testid="project-factors-composite-banner"
                              >
                                <div className="text-2xl font-bold tracking-tight" data-testid="project-factors-composite-pct">
                                  {isNeutral ? 'Neutral' : `${sign}${formatted}%`}
                                </div>
                                <div className="flex-1 text-xs leading-snug">
                                  <div className="font-semibold">
                                    {isNeutral
                                      ? 'Project Factors at neutral'
                                      : isSaving
                                        ? `Project Factors saving ${formatted}% effort on every component`
                                        : `Project Factors adding ${formatted}% effort on every component`}
                                  </div>
                                  <div className="opacity-80 mt-0.5">
                                    {(() => {
                                      const seven = ['governance', 'content_readiness', 'integration_complexity', 'org_complexity', 'timeline_pressure', 'client_capability', 'change_readiness'];
                                      const offCount = implementationType === 'Project'
                                        ? seven.filter(k => k in projectDisabledFactors).length : 0;
                                      return offCount > 0
                                        ? `Stacked multiplier of the ${seven.length - offCount} applied readiness/complexity sliders (${offCount} deselected — counted as optimal).`
                                        : 'Stacked multiplier of all 7 readiness/complexity sliders (Governance × Content × Integration × Org × Timeline × Client × Change).';
                                    })()}
                                    {!diNeutral && (
                                      <span className="ml-1">
                                        {' '}Data Migration scope additionally <span className="font-semibold">{diSign}{diFormatted}%</span> via Data Integrity.
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          {/* Readiness Factors */}
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Readiness Factors</h5>
                              <span className="text-xs text-slate-400 ml-1">Higher is better</span>
                            </div>
                            {/* v260.120 — Maturity-driven suggestions banner */}
                            {modifiers?._maturity_meta?.per_factor ? (
                              <div
                                className="mb-3 flex items-start gap-3 bg-cyan-50 border border-cyan-200 rounded-md px-3 py-2.5"
                                data-testid="project-factors-maturity-banner"
                              >
                                <Sparkles className="w-4 h-4 text-cyan-700 mt-0.5 shrink-0" />
                                <div className="flex-1 text-sm text-cyan-900">
                                  <b>Readiness sliders set from {modifiers._maturity_meta.band} maturity</b>
                                  {' '}({Math.round(modifiers._maturity_meta.overall_score || 0)}/100)
                                  {' '}— applied by {modifiers._maturity_meta.applied_by_name || '—'}
                                  {modifiers._maturity_meta.applied_at ? <> on {new Date(modifiers._maturity_meta.applied_at).toLocaleDateString()}</> : null}
                                  . Move any slider to override.
                                </div>
                                <a
                                  className="text-xs text-cyan-700 hover:text-cyan-900 underline shrink-0"
                                  href={`/estimator/${loadedEstimateId}/maturity`}
                                  title="Open Maturity"
                                >
                                  Open Maturity
                                </a>
                              </div>
                            ) : null}
                            <div className="space-y-4">
                              {Object.entries(READINESS_MODIFIERS).map(([key, label]) => {
                                const impact = getModifierImpact(modifiers[key]);
                                const matMeta = modifiers?._maturity_meta?.per_factor?.[key];
                                const isAuto = matMeta && Math.abs((modifiers[key] ?? 0) - matMeta.suggested) < 1e-6;
                                const isManualOverride = matMeta && !isAuto;
                                const factorOff = implementationType === 'Project' && (key in projectDisabledFactors);
                                return (
                                <div key={key} className={`space-y-2 p-4 rounded-xl border transition-colors ${factorOff ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200 hover:border-slate-300'}`}>
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-1.5">
                                      {implementationType === 'Project' && (
                                        <input
                                          type="checkbox"
                                          checked={!factorOff}
                                          onChange={() => toggleFactorEnabled(key)}
                                          className="w-3.5 h-3.5 accent-[#0F2040] cursor-pointer"
                                          title={factorOff ? 'Factor not applied — counted at its optimal (best-case) value. Tick to re-enable.' : 'Untick to exclude this factor from the Project — it will count at its optimal value so it never penalises the effort'}
                                          data-testid={`factor-toggle-${key}`}
                                        />
                                      )}
                                      <Label className="text-sm font-medium text-[#0F2040]">{label}</Label>
                                      {FACTOR_INFO[key] && (
                                        <button onClick={() => setShowFactorInfo(FACTOR_INFO[key])} className="text-slate-400 hover:text-[#4ABFCF] transition-colors cursor-help" data-testid={`factor-info-${key}`}>
                                          <Info className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      {isAuto && (
                                        <span
                                          className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
                                          title={`Auto-applied from ${modifiers._maturity_meta.band} band (pillar score ${Math.round(matMeta.pillar_score)}/100)`}
                                          data-testid={`factor-provenance-${key}`}
                                        >
                                          AUTO · {modifiers._maturity_meta.band}
                                        </span>
                                      )}
                                      {isManualOverride && (
                                        <span
                                          className="px-1.5 py-0.5 rounded text-[9px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
                                          title={
                                            matMeta.override_reason
                                              ? `Overridden by ${matMeta.overridden_by_name || '—'}: ${matMeta.override_reason}\n(Maturity suggested ${matMeta.suggested}× from pillar ${matMeta.pillar} score ${Math.round(matMeta.pillar_score)}/100)`
                                              : `Maturity suggested ${matMeta.suggested}× (from pillar ${matMeta.pillar} score ${Math.round(matMeta.pillar_score)}/100) — slider was manually overridden`
                                          }
                                          data-testid={`factor-provenance-${key}`}
                                        >
                                          MANUAL (suggested {matMeta.suggested}×)
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {impact && (
                                        <span
                                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${impact.tone}`}
                                          title={`Multiplier ${modifiers[key]} → ${impact.label} per affected component (vs neutral 1.0)`}
                                          data-testid={`factor-delta-${key}`}
                                        >
                                          {impact.label}
                                        </span>
                                      )}
                                      <span className="text-sm font-semibold text-emerald-600">{factorOff ? 'Off (optimal)' : READINESS_LABELS[getSliderIndex(key, modifiers[key])]}</span>
                                    </div>
                                  </div>
                                  <Slider value={[getSliderIndex(key, modifiers[key] ?? OPTIMAL_MODIFIER_VALUES[key])]} onValueChange={([val]) => updateModifier(key, val)} max={4} step={1} className="w-full" disabled={factorOff} />
                                  {factorOff && (
                                    <input type="text" maxLength={140}
                                      value={(modifiers.project_disabled_notes || {})[key] || ''}
                                      onChange={(e) => setFactorNote(key, e.target.value)}
                                      placeholder="Why is this factor off? (optional — reviewers see this)"
                                      className="w-full mt-1 text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-600 placeholder:text-slate-400"
                                      data-testid={`factor-note-${key}`} />
                                  )}
                                  <div className="flex justify-between text-xs text-slate-400"><span>Very Weak</span><span>Very Strong</span></div>
                                </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Complexity & Pressure */}
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Complexity & Pressure</h5>
                              <span className="text-xs text-slate-400 ml-1">Lower is better</span>
                            </div>
                            <div className="space-y-4">
                              {Object.entries(COMPLEXITY_MODIFIERS).map(([key, label]) => {
                                const impact = getModifierImpact(modifiers[key]);
                                const factorOff = implementationType === 'Project' && (key in projectDisabledFactors);
                                return (
                                <div key={key} className={`space-y-2 p-4 rounded-xl border transition-colors ${factorOff ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-slate-200 hover:border-slate-300'}`}>
                                  <div className="flex justify-between items-center">
                                    <div className="flex items-center gap-1.5">
                                      {implementationType === 'Project' && (
                                        <input
                                          type="checkbox"
                                          checked={!factorOff}
                                          onChange={() => toggleFactorEnabled(key)}
                                          className="w-3.5 h-3.5 accent-[#0F2040] cursor-pointer"
                                          title={factorOff ? 'Factor not applied — counted at its optimal (best-case) value. Tick to re-enable.' : 'Untick to exclude this factor from the Project — it will count at its optimal value so it never penalises the effort'}
                                          data-testid={`factor-toggle-${key}`}
                                        />
                                      )}
                                      <Label className="text-sm font-medium text-[#0F2040]">{label}</Label>
                                      {FACTOR_INFO[key] && (
                                        <button onClick={() => setShowFactorInfo(FACTOR_INFO[key])} className="text-slate-400 hover:text-[#4ABFCF] transition-colors cursor-help" data-testid={`factor-info-${key}`}>
                                          <Info className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {impact && (
                                        <span
                                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${impact.tone}`}
                                          title={`Multiplier ${modifiers[key]} → ${impact.label} per affected component (vs neutral 1.0)`}
                                          data-testid={`factor-delta-${key}`}
                                        >
                                          {impact.label}
                                        </span>
                                      )}
                                      <span className="text-sm font-semibold text-amber-600">{factorOff ? 'Off (optimal)' : COMPLEXITY_LABELS[getSliderIndex(key, modifiers[key])]}</span>
                                    </div>
                                  </div>
                                  <Slider value={[getSliderIndex(key, modifiers[key] ?? OPTIMAL_MODIFIER_VALUES[key])]} onValueChange={([val]) => updateModifier(key, val)} max={4} step={1} className="w-full" disabled={factorOff} />
                                  {factorOff && (
                                    <input type="text" maxLength={140}
                                      value={(modifiers.project_disabled_notes || {})[key] || ''}
                                      onChange={(e) => setFactorNote(key, e.target.value)}
                                      placeholder="Why is this factor off? (optional — reviewers see this)"
                                      className="w-full mt-1 text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-600 placeholder:text-slate-400"
                                      data-testid={`factor-note-${key}`} />
                                  )}
                                  <div className="flex justify-between text-xs text-slate-400"><span>Low</span><span>Very High</span></div>
                                </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Data Integrity */}
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Data Integrity</h5>
                              <span className="text-xs text-slate-400 ml-1">Impacts Data Migration effort, PM & Testing</span>
                            </div>
                            <div className="space-y-4">
                              <div className={`space-y-2 p-4 rounded-xl border transition-colors ${implementationType === 'Project' && ('data_integrity' in projectDisabledFactors) ? 'border-slate-200 bg-slate-50 opacity-60' : 'border-rose-200 bg-rose-50/30 hover:border-rose-300'}`}>
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center gap-1.5">
                                    {implementationType === 'Project' && (
                                      <input
                                        type="checkbox"
                                        checked={!('data_integrity' in projectDisabledFactors)}
                                        onChange={() => toggleFactorEnabled('data_integrity')}
                                        className="w-3.5 h-3.5 accent-[#0F2040] cursor-pointer"
                                        title={('data_integrity' in projectDisabledFactors) ? 'Factor not applied — counted at its optimal (best-case) value. Tick to re-enable.' : 'Untick to exclude this factor from the Project — it will count at its optimal value so it never penalises the effort'}
                                        data-testid="factor-toggle-data_integrity"
                                      />
                                    )}
                                    <Label className="text-sm font-medium text-[#0F2040]">Data Integrity Assessment</Label>
                                    <button onClick={() => setShowFactorInfo(FACTOR_INFO.data_integrity)} className="text-slate-400 hover:text-[#4ABFCF] transition-colors cursor-help" data-testid="factor-info-data_integrity">
                                      <Info className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {(() => {
                                      const impact = getModifierImpact(modifiers.data_integrity);
                                      return impact ? (
                                        <span
                                          className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold border ${impact.tone}`}
                                          title={`Migration components scaled by ${modifiers.data_integrity}x → ${impact.label} on Data Migration scope (and PM/Testing uplift). Other components unaffected.`}
                                          data-testid="factor-delta-data_integrity"
                                        >
                                          {impact.label}
                                        </span>
                                      ) : null;
                                    })()}
                                    <span className={`text-sm font-semibold ${
                                      (modifiers.data_integrity ?? OPTIMAL_MODIFIER_VALUES.data_integrity) > 1.0 ? 'text-rose-600' :
                                      (modifiers.data_integrity ?? OPTIMAL_MODIFIER_VALUES.data_integrity) < 1.0 ? 'text-emerald-600' : 'text-slate-600'
                                    }`}>
                                      {implementationType === 'Project' && ('data_integrity' in projectDisabledFactors) ? (
                                        <>Off (optimal)<span className="text-xs font-normal text-slate-400 ml-1">({OPTIMAL_MODIFIER_VALUES.data_integrity}x)</span></>
                                      ) : (
                                        <>
                                          {DATA_INTEGRITY_LABELS[getSliderIndex('data_integrity', modifiers.data_integrity)]}
                                          <span className="text-xs font-normal text-slate-400 ml-1">({modifiers.data_integrity}x)</span>
                                        </>
                                      )}
                                    </span>
                                  </div>
                                </div>
                                <Slider value={[getSliderIndex('data_integrity', modifiers.data_integrity ?? OPTIMAL_MODIFIER_VALUES.data_integrity)]} onValueChange={([val]) => updateModifier('data_integrity', val)} max={6} step={1} className="w-full" data-testid="data-integrity-slider" disabled={implementationType === 'Project' && ('data_integrity' in projectDisabledFactors)} />
                                {implementationType === 'Project' && ('data_integrity' in projectDisabledFactors) && (
                                  <input type="text" maxLength={140}
                                    value={(modifiers.project_disabled_notes || {}).data_integrity || ''}
                                    onChange={(e) => setFactorNote('data_integrity', e.target.value)}
                                    placeholder="Why is this factor off? (optional — reviewers see this)"
                                    className="w-full mt-1 text-xs border border-slate-200 rounded-md px-2 py-1 bg-white text-slate-600 placeholder:text-slate-400"
                                    data-testid="factor-note-data_integrity" />
                                )}
                                <div className="flex justify-between text-xs text-slate-400"><span>Very Weak (2x)</span><span>Very Strong (0.7x)</span></div>
                                {(() => {
                                  const diOff = implementationType === 'Project' && ('data_integrity' in projectDisabledFactors);
                                  const di = diOff ? OPTIMAL_MODIFIER_VALUES.data_integrity : modifiers.data_integrity;
                                  if (diOff) {
                                    return (
                                      <p className="text-xs text-slate-500 mt-1">
                                        Data Integrity deselected — counted at its optimal value ({OPTIMAL_MODIFIER_VALUES.data_integrity}x), so it never penalises Data Migration effort.
                                      </p>
                                    );
                                  }
                                  if (di > 1.0) {
                                    return (
                                      <p className="text-xs text-rose-600 mt-1">
                                        Data Migration components scaled by {di}x. PM & Testing overhead {di >= 2.0 ? '+5%' : di >= 1.5 ? '+3%' : '+1.5%'} each due to additional coordination and validation.
                                      </p>
                                    );
                                  }
                                  if (di < 1.0) {
                                    return (
                                      <p className="text-xs text-emerald-600 mt-1">
                                        Strong data quality reduces Data Migration effort to {di}x of baseline. PM & Testing overhead reduced by {di <= 0.7 ? '3%' : di <= 0.8 ? '2%' : '1%'}.
                                      </p>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* PM Meetings Cadence */}
                          <div data-testid="pm-meetings-section">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">PM Meetings Cadence</h5>
                              <span className="text-xs text-slate-400 ml-1">Recurring meeting hours scale with project duration</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl border border-indigo-200 bg-indigo-50/30">
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-[#0F2040]">Weekly meetings</Label>
                                <input
                                  type="number" min="0" max="20" step="1"
                                  value={modifiers.pm_meetings_per_week}
                                  onChange={(e) => updateModifier('pm_meetings_per_week', Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))}
                                  className="w-full h-9 px-3 rounded-md border border-indigo-200 bg-white text-sm text-[#0F2040] focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                  data-testid="pm-meetings-per-week"
                                />
                                <p className="text-xs text-slate-500">e.g. 2 = a weekly standup + a steering committee</p>
                              </div>
                              <div className="space-y-2">
                                <Label className="text-sm font-medium text-[#0F2040]">FTE per meeting (1h each)</Label>
                                <input
                                  type="number" min="0" max="20" step="1"
                                  value={modifiers.pm_fte_per_meeting}
                                  onChange={(e) => updateModifier('pm_fte_per_meeting', Math.max(0, Math.min(20, parseInt(e.target.value) || 0)))}
                                  className="w-full h-9 px-3 rounded-md border border-indigo-200 bg-white text-sm text-[#0F2040] focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                  data-testid="pm-fte-per-meeting"
                                />
                                <p className="text-xs text-slate-500">total attendees billing 1h per meeting</p>
                              </div>
                              <div className="md:col-span-2 text-xs text-indigo-700 font-medium pt-1">
                                {(() => {
                                  // Use the backend's `pm_meeting_weeks` (the
                                  // pre-meeting weeks count actually fed into
                                  // the calc) rather than `projected_weeks`,
                                  // which inflates after meetings are added.
                                  const wks = results?.overhead?.pm_meeting_weeks
                                    ?? results?.projected_weeks
                                    ?? 0;
                                  const m = modifiers.pm_meetings_per_week || 0;
                                  const fte = modifiers.pm_fte_per_meeting || 0;
                                  const hrs = wks * m * fte;
                                  return wks > 0 && hrs > 0
                                    ? `≈ ${wks}w × ${m}m × ${fte} FTE = ${hrs}h of meeting time at PM rate`
                                    : wks === 0
                                      ? 'Hours appear once an estimate has run.'
                                      : 'Set meetings + FTE > 0 to add a meeting line.';
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Project Contingency */}
                          <div data-testid="project-contingency-section">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-violet-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Project Contingency</h5>
                              <span className="text-xs text-slate-400 ml-1">Confidence buffer applied to total effort & cost</span>
                            </div>
                            <div className="space-y-2 p-4 rounded-xl border border-violet-200 bg-violet-50/30">
                              <div className="flex justify-between items-center">
                                <Label className="text-sm font-medium text-[#0F2040]">Buffer applied to bottom-line</Label>
                                <span className={`text-sm font-semibold tabular-nums ${
                                  modifiers.project_contingency_pct > 0 ? 'text-violet-700' :
                                  modifiers.project_contingency_pct < 0 ? 'text-emerald-700' : 'text-slate-600'
                                }`} data-testid="project-contingency-value">
                                  {modifiers.project_contingency_pct > 0 ? '+' : ''}{modifiers.project_contingency_pct}%
                                </span>
                              </div>
                              {/*
                                Slider goes from -20% to +100%. Neutral (0%)
                                sits at 20/120 = 16.67% along the track —
                                NOT centered. Without a marker it looks like
                                the slider is "stuck near the start" when
                                actually the user is at the neutral point.
                                We add a tick + label aligned to the true
                                zero position so users immediately read the
                                slider as "neutral".
                              */}
                              {/* "Neutral" indicator above the track —
                                  triangle points down to the exact 0% mark.
                                  Radix slider clamps the thumb's centre by
                                  half its width on each side, so the value=0
                                  position is NOT at 16.67% of the track —
                                  it's at `calc(half_thumb + 16.67% × (track - thumb_width))`.
                                  Thumb is h-4/w-4 (16px), so:
                                    left = 8px + 16.6667% × (100% - 16px) / 100
                                         = 8px + 16.6667% − 2.667px
                                         = calc(16.6667% + 5.333px)
                                  Without this offset, dragging the thumb to
                                  visually sit under the indicator yields −1%
                                  rather than 0%. */}
                              <div className="relative h-4">
                                <div
                                  className="absolute -translate-x-1/2 flex flex-col items-center pointer-events-none"
                                  style={{ left: 'calc(16.6667% + 5.333px)' }}
                                  data-testid="project-contingency-zero-label"
                                >
                                  <span className="text-[9px] font-semibold text-slate-500 leading-none">0% neutral</span>
                                  <span className="text-slate-400 text-[10px] leading-none">▼</span>
                                </div>
                              </div>
                              <div className="relative">
                                {/* Two-band gradient backdrop reinforcing
                                    the asymmetric semantics of the slider:
                                      emerald  -20% → 0%   (Confidence Discount zone)
                                      amber      0% → +100% (Uncertainty Contingency zone)
                                    The gradient uses the same thumb-aware
                                    boundary as the indicator so the colour
                                    boundary, the tick, and the thumb at
                                    value=0 all line up pixel-perfectly. */}
                                <div
                                  className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full pointer-events-none"
                                  style={{
                                    background:
                                      'linear-gradient(to right, ' +
                                      'rgba(16,185,129,0.35) 0%, ' +
                                      'rgba(16,185,129,0.35) calc(16.6667% + 5.333px), ' +
                                      'rgba(245,158,11,0.35) calc(16.6667% + 5.333px), ' +
                                      'rgba(245,158,11,0.35) 100%)',
                                  }}
                                  aria-hidden="true"
                                  data-testid="project-contingency-zone-gradient"
                                />
                                <Slider
                                  value={[modifiers.project_contingency_pct]}
                                  onValueChange={([val]) => updateModifier('project_contingency_pct', val)}
                                  min={-20} max={100} step={1}
                                  className="w-full [&>span:first-child]:bg-transparent"
                                  data-testid="project-contingency-slider"
                                />
                                {/* Zero-tick on the track — same thumb-aware
                                    offset as the indicator above.
                                    `-translate-x-1/2` centres the 1px line on
                                    the calc point, so its visual centre lines
                                    up with the thumb's centre to the pixel. */}
                                <div
                                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                                  style={{ left: 'calc(16.6667% + 5.333px)', height: '14px', marginTop: '-1px' }}
                                  aria-hidden="true"
                                  data-testid="project-contingency-zero-tick"
                                >
                                  <div className="w-px h-full bg-slate-500" />
                                </div>
                              </div>
                              <div className="flex justify-between text-xs text-slate-400 mt-1">
                                <span className="text-emerald-700 font-medium" title="Apply a discount when scope is well-defined and risk is low (range −20% to 0%)">Confidence Discount</span>
                                <span className="text-amber-700 font-medium" title="Pad the estimate when requirements, integration surface, or stakeholder buy-in are uncertain (range 0% to +100%)">Uncertainty Contingency</span>
                              </div>
                              <p className="text-xs mt-2 leading-relaxed text-slate-500">
                                Negative values discount the estimate when scope is well-defined; positive values pad it when the team is unsure of requirements, integration surface, or stakeholder buy-in. Surfaces as a separate <span className="text-violet-700 font-medium">Project Contingency</span> line in Project Overhead.
                              </p>
                            </div>
                          </div>

                          {/* Timeline Compression Tax */}
                          <div data-testid="estimate-timeline-stretch-section">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-cyan-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Timeline Compression Tax</h5>
                              <span className="text-xs text-slate-400 ml-1">Applies only when Planner timeline is SHORTER than auto-projection</span>
                            </div>
                            <label
                              className="flex items-start gap-3 p-4 rounded-xl border border-cyan-200 bg-cyan-50/30 cursor-pointer hover:bg-cyan-50/60 transition-colors"
                              data-testid="apply-timeline-stretch-tax-row"
                            >
                              <input
                                type="checkbox"
                                checked={modifiers.apply_timeline_stretch_tax !== false}
                                onChange={(e) => updateModifier('apply_timeline_stretch_tax', e.target.checked)}
                                className="mt-0.5 accent-cyan-600 cursor-pointer"
                                data-testid="apply-timeline-stretch-tax-checkbox"
                              />
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-[#0F2040]">
                                  Apply duration tax to PM / CS / CF when committed timeline is compressed
                                </div>
                                <p className="text-xs mt-1 leading-relaxed text-slate-600">
                                  When the committed Planner timeline is <span className="font-medium">SHORTER</span> than the auto-projection, the team has to do the same scope in a shorter window — that adds parallel rework, faster decision cadence, and elevated coordination on <span className="text-cyan-700 font-medium">Project Manager</span>, <span className="text-cyan-700 font-medium">Consultant</span>, and <span className="text-cyan-700 font-medium">Config Specialist</span> hours. Sub-linear (Forrester PMO benchmark, 25% pass-through). <span className="font-medium">Compression-only</span> — stretches/slack are not taxed (they&rsquo;re already captured by per-week meeting cadence). Disable for fixed-fee deals or pre-negotiated timelines.
                                </p>
                              </div>
                            </label>
                          </div>

                          {/* Display Rounding */}
                          <div data-testid="estimate-rounding-section">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-sky-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Display Rounding</h5>
                              <span className="text-xs text-slate-400 ml-1">Applies to estimator &amp; exports only — stored values stay precise</span>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl border border-sky-200 bg-sky-50/30">
                              <div>
                                <Label className="text-sm font-medium text-[#0F2040] mb-1.5 block">Hours Rounding</Label>
                                <select
                                  value={rounding.hours_increment}
                                  onChange={e => setRounding(r => ({ ...r, hours_increment: parseFloat(e.target.value) || 0 }))}
                                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                  data-testid="rounding-hours-select"
                                >
                                  {HOURS_INCREMENT_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <Label className="text-sm font-medium text-[#0F2040] mb-1.5 block">Cost Rounding</Label>
                                <select
                                  value={rounding.cost_increment}
                                  onChange={e => setRounding(r => ({ ...r, cost_increment: parseFloat(e.target.value) || 0 }))}
                                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-500"
                                  data-testid="rounding-cost-select"
                                >
                                  {COST_INCREMENT_OPTIONS.map(o => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                  ))}
                                </select>
                              </div>
                              {(rounding.hours_increment > 0 || rounding.cost_increment > 0) && (
                                <div className="md:col-span-2 text-xs text-sky-800 bg-sky-100 rounded-md px-3 py-2" data-testid="rounding-preview">
                                  Preview: <strong>{formatHours(results?.effort_hours || results?.total_effort || 0, rounding)}</strong> hrs &middot;{' '}
                                  <strong>{formatCost(results?.total_cost || 0, rounding, currencySymbol)}</strong>
                                  <span className="text-sky-600/70 ml-2">(applies to on-screen totals &amp; SOW / PDF / PPT exports)</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Rate Card Override */}
                          {isAdmin && (
                          <div>
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                              <h5 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">Rate Card Override</h5>
                              <span className="text-xs text-slate-400 ml-1">Per-estimate only</span>
                              {(Object.keys(rateCardOverrides).length > 0 || rateCardBlanketDiscountPct !== 0) && (
                                <button
                                  onClick={() => { setRateCardOverrides({}); setRateCardBlanketDiscountPct(0); }}
                                  className="ml-auto text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors"
                                  data-testid="reset-rate-overrides"
                                >
                                  <RotateCcw className="w-3 h-3" /> Reset to defaults
                                </button>
                              )}
                            </div>
                            <p className="text-xs text-slate-400 mb-3">
                              Adjust hourly rates and/or apply a blanket discount for THIS estimate. Changes won't affect global settings or other estimates.
                            </p>

                            {/* ── Per-estimate Blanket Discount ────────── */}
                            <div
                              className="rounded-xl border border-[#4ABFCF]/30 bg-gradient-to-r from-[#4ABFCF]/5 to-[#0F2040]/5 p-4 mb-4"
                              data-testid="estimator-blanket-discount-card"
                            >
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                  <div className="text-sm font-bold text-[#0F2040]">Blanket Discount / Surcharge</div>
                                  <p className="text-[11px] text-slate-600 mt-1">
                                    Applied AFTER per-role overrides. Positive = discount (e.g., <code>10</code> = 10% off);
                                    Negative = surcharge (e.g., <code>-5</code> = 5% extra). Range: −50 to +50.
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="relative">
                                    <Input
                                      id="estimator-blanket-discount-pct"
                                      data-testid="estimator-blanket-discount-input"
                                      type="number"
                                      min={-50}
                                      max={50}
                                      step={0.5}
                                      value={rateCardBlanketDiscountPct}
                                      onChange={(e) => {
                                        const v = parseFloat(e.target.value);
                                        setRateCardBlanketDiscountPct(
                                          Number.isFinite(v) ? Math.max(-50, Math.min(50, v)) : 0,
                                        );
                                      }}
                                      className="w-28 pr-9 text-right font-mono"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">%</span>
                                  </div>
                                  {rateCardBlanketDiscountPct !== 0 && (
                                    <span
                                      className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                        rateCardBlanketDiscountPct > 0
                                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                          : 'bg-rose-50 text-rose-700 border border-rose-200'
                                      }`}
                                      data-testid="estimator-blanket-discount-badge"
                                    >
                                      {rateCardBlanketDiscountPct > 0
                                        ? `−${rateCardBlanketDiscountPct}%`
                                        : `+${Math.abs(rateCardBlanketDiscountPct)}%`}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {/* Sensitivity heat-strip — quick-pick chips that
                                  preview & apply common discount tiers. Always
                                  visible (one-click changes are rare clicks
                                  but high signal) and orient the user around
                                  the conversion-curve. */}
                              <div
                                className="flex flex-wrap items-center gap-1.5 mt-2"
                                data-testid="estimator-discount-heat-strip"
                              >
                                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-1">Sensitivity</span>
                                {[-5, 0, 5, 10].map((p) => {
                                  const active = rateCardBlanketDiscountPct === p;
                                  const tone = p === 0
                                    ? 'bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200'
                                    : p > 0
                                      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100'
                                      : 'bg-rose-50 text-rose-700 ring-rose-200 hover:bg-rose-100';
                                  const activeRing = active ? 'ring-2 !ring-[#4ABFCF] shadow-sm' : 'ring-1';
                                  return (
                                    <button
                                      key={p}
                                      type="button"
                                      onClick={() => setRateCardBlanketDiscountPct(p)}
                                      className={`text-[11px] font-mono font-semibold px-2 py-0.5 rounded-md transition-all ${tone} ${activeRing}`}
                                      title={p === 0 ? 'No discount' : (p > 0 ? `Apply ${p}% discount` : `Apply ${Math.abs(p)}% surcharge`)}
                                      data-testid={`estimator-discount-chip-${p}`}
                                    >
                                      {p > 0 ? `−${p}%` : (p < 0 ? `+${Math.abs(p)}%` : '0%')}
                                    </button>
                                  );
                                })}
                                <span className="text-[10px] text-slate-400 ml-1">click to apply</span>
                              </div>
                              {/* Justification input — required (gated in
                                  saveEstimate, ≥10 chars) whenever pct != 0.
                                  Builds an audit dataset for MSA negotiations. */}
                              {rateCardBlanketDiscountPct !== 0 && (
                                <div
                                  className="mt-2 space-y-1"
                                  data-testid="estimator-discount-justification-row"
                                >
                                  <div className="flex items-center gap-2">
                                    <Label
                                      htmlFor="estimator-blanket-discount-reason"
                                      className="text-[11px] text-slate-500 shrink-0 font-semibold"
                                    >
                                      Reason <span className="text-rose-500">*</span>
                                    </Label>
                                    <Input
                                      id="estimator-blanket-discount-reason"
                                      data-testid="estimator-blanket-discount-reason"
                                      type="text"
                                      placeholder='e.g. "Strategic partner — Q1 push" or "Multi-year MSA renewal"'
                                      value={rateCardBlanketDiscountReason}
                                      onChange={(e) => setRateCardBlanketDiscountReason(e.target.value.slice(0, 200))}
                                      className={`text-xs ${_discountReasonMissing ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-200' : 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-200'}`}
                                      maxLength={200}
                                      aria-invalid={_discountReasonMissing}
                                      aria-describedby="estimator-discount-reason-help"
                                    />
                                    <span
                                      data-testid="estimator-discount-reason-counter"
                                      className={`text-[10px] font-mono shrink-0 ${_discountReasonMissing ? 'text-rose-600' : 'text-emerald-600'}`}
                                    >
                                      {(rateCardBlanketDiscountReason || '').trim().length}/10
                                    </span>
                                  </div>
                                  <p
                                    id="estimator-discount-reason-help"
                                    data-testid="estimator-discount-reason-help"
                                    className={`text-[10px] ${_discountReasonMissing ? 'text-rose-600' : 'text-slate-400'}`}
                                  >
                                    {_discountReasonMissing
                                      ? `Save is disabled until you add at least 10 characters of justification (${10 - (rateCardBlanketDiscountReason || '').trim().length} more needed).`
                                      : 'Justification looks good — Save is enabled.'}
                                  </p>
                                </div>
                              )}
                              {/* Pricing impact preview — super-admin only. */}
                              {isSuperAdmin && pricingBaseline && pricingBaseline.count > 0 && (
                                <PricingImpactPreview
                                  sym={currencySymbol}
                                  pct={rateCardBlanketDiscountPct}
                                  baseline={pricingBaseline}
                                />
                              )}
                            </div>

                            {/* v380 — resolved rate-card source (regional vs global) */}
                            {(() => {
                              const _hasCard = (k) => !!(k && regionalRateCards[k] && Object.keys(regionalRateCards[k]).length > 0);
                              const _estRegion = (region || '').toUpperCase();
                              const _entRegion = (selectedEntity?.region || (selectedEntity?.regions || [])[0] || '').toUpperCase();
                              const _entCode = (selectedEntity?.code || selectedEntity?.id || '').toUpperCase();
                              let _srcLabel = 'Global default rate card';
                              if (_hasCard(_estRegion)) {
                                _srcLabel = `${_entCode ? `${_entCode} · ` : ''}${_estRegion} regional rate card`;
                              } else if (_hasCard(_entRegion)) {
                                _srcLabel = `${_entCode} · ${_entRegion} regional rate card (via ${_entCode} entity)`;
                              }
                              return (
                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-2" data-testid="rate-card-source-label">
                                  <MapPin className="w-3 h-3 text-[#4ABFCF]" />
                                  <span>Rates: <span className="font-semibold text-[#0F2040]">{_srcLabel}</span></span>
                                </div>
                              );
                            })()}
                            {/* ── Per-Role Rate Grid (Default | Custom | Delta) ── */}
                            <div className="space-y-1" data-testid="estimator-rate-card-grid">
                              <div className="grid grid-cols-12 gap-3 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                                <div className="col-span-4">Role</div>
                                <div className="col-span-2 text-right">Default</div>
                                <div className="col-span-3 text-right">Custom Rate</div>
                                <div className="col-span-3 text-right">Delta</div>
                              </div>
                              {Object.entries(RATE_CARD_NAMES).map(([role, name]) => {
                                const _regionKey = (region || selectedEntity?.region || (selectedEntity?.regions || [])[0] || '').toUpperCase();
                                const _regionalCard = _regionKey ? regionalRateCards[_regionKey] : null;
                                const defaultRate = Number((_regionalCard && _regionalCard[role]) || rateCard[role] || 100);
                                const isOverridden = rateCardOverrides[role] !== undefined;
                                const cur = Number(isOverridden ? rateCardOverrides[role] : defaultRate);
                                const delta = cur - defaultRate;
                                const deltaPct = defaultRate > 0 ? (delta / defaultRate) * 100 : 0;
                                return (
                                  <div
                                    key={role}
                                    className={`grid grid-cols-12 gap-3 items-center px-3 py-2 rounded transition-colors ${
                                      isOverridden ? 'bg-blue-50/50 border border-blue-200' : 'hover:bg-slate-50/60 border border-transparent'
                                    }`}
                                    data-testid={`rate-override-${role}`}
                                  >
                                    <div className="col-span-4">
                                      <div className="text-sm font-medium text-[#0F2040] flex items-center gap-2">
                                        {name}
                                        {isOverridden && (
                                          <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-600 border-blue-200">Custom</Badge>
                                        )}
                                      </div>
                                      <div className="text-[10px] text-slate-400">{role}</div>
                                    </div>
                                    <div className="col-span-2 text-right">
                                      <span className="text-sm font-mono text-slate-500" data-testid={`rate-default-${role}`}>
                                        {currencySymbol}{defaultRate.toFixed(0)}
                                      </span>
                                    </div>
                                    <div className="col-span-3">
                                      <div className="relative flex items-center">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{currencySymbol}</span>
                                        <Input
                                          type="number"
                                          value={isOverridden ? rateCardOverrides[role] : defaultRate}
                                          onChange={(e) => {
                                            const val = parseFloat(e.target.value);
                                            if (!isNaN(val) && val >= 0) {
                                              setRateCardOverrides((prev) => ({ ...prev, [role]: val }));
                                            }
                                          }}
                                          className="pl-7 pr-3 h-8 text-sm text-right font-mono"
                                          data-testid={`rate-input-${role}`}
                                        />
                                        {isOverridden && (
                                          <button
                                            onClick={() => setRateCardOverrides((prev) => {
                                              const next = { ...prev };
                                              delete next[role];
                                              return next;
                                            })}
                                            className="ml-1 text-slate-400 hover:text-red-500 transition-colors shrink-0"
                                            data-testid={`rate-reset-${role}`}
                                            title="Reset to default"
                                          >
                                            <RotateCcw className="w-3.5 h-3.5" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="col-span-3 text-right" data-testid={`rate-delta-${role}`}>
                                      {Math.abs(delta) < 0.01 ? (
                                        <span className="text-[11px] text-slate-400">—</span>
                                      ) : (
                                        <span
                                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                                            delta > 0
                                              ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                              : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                          }`}
                                        >
                                          {delta > 0 ? '+' : ''}${delta.toFixed(0)} ({deltaPct > 0 ? '+' : ''}{deltaPct.toFixed(1)}%)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          )}
                        </div>
                      </TabsContent>

                      {/* Prev / Next Navigation */}
                      <div className="flex justify-between items-center mt-6 pt-4 border-t border-slate-200">
                        <Button variant="outline" onClick={goPrevTab} disabled={allTabs.indexOf(activeTab) <= 0} data-testid="prev-tab-btn">
                          <ChevronLeft className="w-4 h-4 mr-2" /> Previous
                        </Button>
                        <span className="text-sm text-slate-400">{allTabs.indexOf(activeTab) + 1} of {allTabs.length}</span>
                        {allTabs.indexOf(activeTab) >= allTabs.length - 1 ? (
                          <Button onClick={() => saveEstimate()} disabled={saving || !results || _discountReasonMissing} title={_discountReasonMissing ? 'Add a discount justification (≥10 chars) to enable Save' : undefined} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-estimate-btn">
                            {saving ? <span className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" /> : <Save className="w-4 h-4 mr-2" />}
                            {saving ? 'Saving...' : 'Save'}
                          </Button>
                        ) : (
                          <Button onClick={goNextTab} className="bg-[#0F2040] hover:bg-[#172E5A] text-white" data-testid="next-tab-btn">
                            Next <ChevronRight className="w-4 h-4 ml-2" />
                          </Button>
                        )}
                      </div>
                    </Tabs>
                  </CardContent>
                </Card>
              </div>

              {/* Right: Live Results */}
              <ResultsSidebar
                results={results} modifiers={modifiers}
                breakdownView={breakdownView} setBreakdownView={setBreakdownView}
                sortedCostByRole={sortedCostByRole}
                sortedEffortByGroup={sortedEffortByGroup}
                sortedCostByGroup={sortedCostByGroup}
                setShowConfidenceInfo={setShowConfidenceInfo}
                setShowComplexityInfo={setShowComplexityInfo}
                setShowRangeInfo={setShowRangeInfo}
                estimateName={estimateName} setEstimateName={setEstimateName}
                saving={saving} savedInfo={savedInfo} setSavedInfo={setSavedInfo}
                saveEstimate={saveEstimate} savedEstimateId={savedEstimateId}
                isAdmin={isAdmin} groupChartData={groupChartData}
                hostingModel={hostingModel}
                hasUnsavedChanges={hasUnsavedChanges}
                justSaved={justSaved}
                navigateWithSaveGate={navigateWithSaveGate}
                rounding={rounding}
                currencySymbol={currencySymbol}
                currencyCode={effectiveCurrency.code || 'USD'}
                availableCurrencies={entityFilteredCurrencies}
                onCurrencyChange={setCurrencyCode}
                seertechEntities={seertechEntities}
                entityChip={{ entity: selectedEntity, region, currency: effectiveCurrency.code || 'USD' }}
                onOpenConditions={() => setEditConditionsOpen(true)}
                taxInclusive={taxInclusive}
                onTaxInclusiveChange={setTaxInclusive}
                audRateInfo={audRateInfo}
                marginInfo={marginInfo}
              />
            </div>
          </div>
        )}
      </main>

      {/* Spacer for sticky save bar */}
      {step === 4 && results && <div className="h-14" />}

      {/* Info Modals */}
      <ImplTypeInfoModal showTypeInfo={showTypeInfo} setShowTypeInfo={setShowTypeInfo} />
      <ComplexityLevelInfoModal showComplexityLevelInfo={showComplexityLevelInfo} setShowComplexityLevelInfo={setShowComplexityLevelInfo} />
      <HostingInfoModal showHostingInfo={showHostingInfo} setShowHostingInfo={setShowHostingInfo} />
      <RangeInfoModal showRangeInfo={showRangeInfo} setShowRangeInfo={setShowRangeInfo} results={results} />
      <ConfidenceInfoModal showConfidenceInfo={showConfidenceInfo} setShowConfidenceInfo={setShowConfidenceInfo} results={results} modifiers={modifiers} />
      <ComplexityInfoModal showComplexityInfo={showComplexityInfo} setShowComplexityInfo={setShowComplexityInfo} results={results} modifiers={modifiers} implementationType={implementationType} />
      <ComponentInfoModal showComponentInfo={showComponentInfo} setShowComponentInfo={setShowComponentInfo} />
      <DeliverablesEditorDialog
        componentName={deliverablesEditorFor}
        deliverables={(deliverablesEditorFor && componentDeliverables[deliverablesEditorFor]) || []}
        onClose={() => setDeliverablesEditorFor(null)}
        onChange={(next) => {
          setComponentDeliverables(prev => {
            const copy = { ...prev };
            if (next.length === 0) delete copy[deliverablesEditorFor];
            else copy[deliverablesEditorFor] = next;
            return copy;
          });
        }}
      />
      <CloneScopeSkeletonDialog
        open={cloneSkeletonOpen}
        onClose={() => setCloneSkeletonOpen(false)}
        implementationType={implementationType}
        complexity={complexity}
        selectedComponentNames={Object.keys(selectedComponents).filter(n => selectedComponents[n] > 0)}
        currentDeliverables={componentDeliverables}
        onImport={(merged) => setComponentDeliverables(merged)}
      />
      <SaveAsTemplateDialog
        open={saveAsTemplateOpen}
        onClose={() => setSaveAsTemplateOpen(false)}
        sourceEstimateId={savedEstimateId}
        defaultName={estimateName || implName || ''}
        onSaved={refreshScopeTemplates}
      />
      <FactorInfoModal showFactorInfo={showFactorInfo} setShowFactorInfo={setShowFactorInfo} />

      {/* Quick Start Demo Guide */}
      {demoMode && step === 4 && (
        <QuickStartGuide onDismiss={() => setDemoMode(false)} />
      )}

      {/* ── v260.39/v260.40/v260.41 — Highlight stepper chip (Step 4
              only). Step Prev/Next through every newly-added component
              AND integration. At idx === length we show a "Done · View
              full diff" finale that opens the complete Apply diff
              dialog (sessionStorage-stashed). */}
      {step === 4 && highlightTargets.length > 0 && !highlightDismissed && (() => {
        const compCount = highlightTargets.filter((t) => t.kind === 'component').length;
        const intCount = highlightTargets.filter((t) => t.kind === 'integration').length;
        const summaryParts = [];
        if (compCount > 0) summaryParts.push(`${compCount} component${compCount === 1 ? '' : 's'}`);
        if (intCount > 0) summaryParts.push(`${intCount} integration${intCount === 1 ? '' : 's'}`);
        const summary = `${summaryParts.join(' + ')} added`;
        const isDone = highlightIdx >= highlightTargets.length;
        return (
          <div
            className={`fixed right-6 bottom-20 z-[55] flex items-center gap-2 px-3 py-2 rounded-full border shadow-lg ${
              isDone ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'
            }`}
            data-testid="highlight-stepper"
            data-state={isDone ? 'done' : 'reviewing'}
          >
            {isDone ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                <span className="text-xs text-emerald-800">
                  <strong>All {highlightTargets.length} {highlightTargets.length === 1 ? 'addition' : 'additions'} reviewed</strong>
                </span>
                {applyDiff && (
                  <button
                    type="button"
                    onClick={() => setApplyDiffDialogOpen(true)}
                    className="text-xs font-semibold px-2.5 py-1 rounded-full border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100 transition-colors ml-1"
                    data-testid="highlight-stepper-view-diff"
                  >
                    View full diff
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setHighlightIdx((i) => Math.max(0, i - 1))}
                  className="h-6 w-6 inline-flex items-center justify-center rounded-full border border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-100 transition-colors ml-1"
                  title="Back to last addition"
                  aria-label="Back to last addition"
                  data-testid="highlight-stepper-prev"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setHighlightDismissed(true)}
                  className="h-6 w-6 inline-flex items-center justify-center rounded-full text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 transition-colors ml-0.5"
                  title="Dismiss"
                  aria-label="Dismiss highlight stepper"
                  data-testid="highlight-stepper-close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs text-amber-800">
                  <strong>{summary}</strong>
                  <span className="text-amber-600 mx-1.5">·</span>
                  <strong>{highlightIdx + 1}</strong> of {highlightTargets.length}
                </span>
                <div className="flex items-center gap-1 ml-1">
                  <button
                    type="button"
                    disabled={highlightIdx === 0}
                    onClick={() => setHighlightIdx((i) => Math.max(0, i - 1))}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-full border border-amber-300 bg-white text-amber-700 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    title="Previous addition"
                    aria-label="Previous addition"
                    data-testid="highlight-stepper-prev"
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setHighlightIdx((i) => Math.min(highlightTargets.length, i + 1))}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-full border border-amber-300 bg-white text-amber-700 hover:bg-amber-100 transition-colors"
                    title={highlightIdx === highlightTargets.length - 1 ? 'Finish review' : 'Next addition'}
                    aria-label={highlightIdx === highlightTargets.length - 1 ? 'Finish review' : 'Next addition'}
                    data-testid="highlight-stepper-next"
                  >
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setHighlightDismissed(true)}
                    className="h-6 w-6 inline-flex items-center justify-center rounded-full text-amber-600 hover:text-amber-800 hover:bg-amber-100 transition-colors ml-0.5"
                    title="Dismiss"
                    aria-label="Dismiss highlight stepper"
                    data-testid="highlight-stepper-close"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* ── v260.43 — "Last apply" floating pill. Renders only when:
              (a) Step 4 is active,
              (b) the in-memory applyDiff is populated (from
                  sessionStorage or the server-persisted fallback),
              (c) the active stepper chip is NOT showing (no current
                  ?highlight= context, or the user has dismissed the
                  chip). Clicking opens the same recall dialog so the
                  audit artifact "what changed last apply" stays one
                  click away across refreshes + teammate sessions. */}
      {step === 4 && applyDiff?.diff && (highlightTargets.length === 0 || highlightDismissed) && (() => {
        const s = applyDiff.summary || {};
        const mutated = (s.components_added || 0) + (s.integrations_added || 0)
                      + (s.deliverables_added || 0) + (s.project_conditions || 0)
                      + (s.modifiers || 0);
        if (mutated === 0) return null;
        const stamp = applyDiff.applied_at ? new Date(applyDiff.applied_at) : null;
        const stampLabel = stamp ? stamp.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '';
        const by = applyDiff.applied_by ? ` by ${applyDiff.applied_by}` : '';
        return (
          <button
            type="button"
            onClick={() => setApplyDiffDialogOpen(true)}
            className="fixed right-6 bottom-20 z-[55] inline-flex items-center gap-2 px-3 py-2 rounded-full border border-slate-300 bg-white text-slate-700 shadow-md hover:bg-slate-50 transition-colors"
            data-testid="last-apply-pill"
            title={stamp ? `Last apply: ${stampLabel}${by}` : 'Last Apply diff'}
          >
            <Clock className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs">
              Last apply · <strong>{mutated}</strong> {mutated === 1 ? 'change' : 'changes'}
            </span>
          </button>
        );
      })()}

      {/* ── v260.41/v260.42 — Full Apply diff dialog (opened from the
              stepper's Done finale). Components / Integrations /
              auto-flagged rows are clickable: closes the dialog,
              re-syncs the stepper cursor when the row is in
              highlightTargets, and re-triggers the amber-pulse jump. */}
      {/* v260.121 — Maturity override reason capture. Triggered when a
          user moves a readiness slider that was AUTO-set from a maturity
          assessment, so finance has a paper trail on every divergence. */}
      <Dialog
        open={!!pendingOverride}
        onOpenChange={(open) => {
          if (!open) {
            // Treat backdrop-click / Escape as "Skip" — commits the change
            // with no reason so the slider still moves but the audit
            // records an empty reason field.
            commitPendingOverride('');
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="maturity-override-dialog">
          <DialogHeader>
            <DialogTitle>Override maturity suggestion</DialogTitle>
            <DialogDescription>
              {pendingOverride ? (
                <>
                  You're moving <b>
                    {READINESS_MODIFIERS[pendingOverride.key]
                      || COMPLEXITY_MODIFIERS[pendingOverride.key]
                      || pendingOverride.key}
                  </b> off the value suggested by the
                  {' '}{modifiers?._maturity_meta?.band || '—'} maturity assessment
                  {' '}({modifiers?._maturity_meta?.per_factor?.[pendingOverride.key]?.suggested}×).
                  {' '}A short note here gives finance a clean paper trail.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={overrideReason}
            onChange={(e) => setOverrideReason(e.target.value)}
            placeholder="e.g. Customer confirmed strong governance committee in place — overriding to Strong."
            rows={3}
            data-testid="maturity-override-reason"
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => commitPendingOverride('')}
              data-testid="maturity-override-skip"
            >
              Skip — apply anyway
            </Button>
            <Button
              onClick={() => commitPendingOverride(overrideReason.trim())}
              disabled={!overrideReason.trim()}
              className="bg-[#0F2040] hover:bg-[#1a2f5a]"
              data-testid="maturity-override-save"
            >
              Save reason &amp; apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      <Dialog open={applyDiffDialogOpen} onOpenChange={setApplyDiffDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="apply-diff-recall-dialog">
          <DialogHeader>
            <DialogTitle>Apply diff — full breakdown</DialogTitle>
          </DialogHeader>
          {applyDiff?.diff ? (() => {
            // v260.42 — helper to jump-from-dialog. Closes the dialog,
            // re-syncs the stepper cursor (so the chip's "X of N"
            // matches the row clicked), then defers the jump until the
            // dialog close animation has settled.
            const jumpFromDialog = (kind, key) => {
              setApplyDiffDialogOpen(false);
              // Re-sync the chip cursor to the matching target index
              // when this row is in our stepper list.
              const idx = highlightTargets.findIndex((t) =>
                kind === 'component' ? (t.kind === 'component' && t.name === key)
                                     : (t.kind === 'integration' && t.type === key)
              );
              if (idx >= 0) setHighlightIdx(idx);
              setTimeout(() => {
                if (kind === 'integration') {
                  jumpToIntegration(key);
                } else {
                  const comp = components.find((c) => c.name === key);
                  if (comp) jumpToComponent(comp.name, comp.group);
                }
              }, 180);
            };
            // Parse "§X.Y → comp::Name" / "§X.Y → Name" / "§X.Y → int::type"
            // attribution strings into a clickable {kind, key, raw}.
            const parseAutoFlag = (entry) => {
              const idx = entry.indexOf('→');
              if (idx < 0) return { raw: entry };
              const rhs = entry.slice(idx + 1).trim();
              if (rhs.startsWith('int::')) return { kind: 'integration', key: rhs.slice(5).trim(), raw: entry };
              const name = rhs.startsWith('comp::') ? rhs.slice(6).trim() : rhs;
              return { kind: 'component', key: name, raw: entry };
            };
            const Row = ({ label, onClick, testid }) => onClick ? (
              <button
                type="button"
                onClick={onClick}
                className="block w-full text-left text-sm text-cyan-700 hover:text-cyan-800 hover:underline focus:outline-none focus:underline"
                data-testid={testid}
              >
                {label}
              </button>
            ) : (
              <span className="block text-sm text-slate-700">{label}</span>
            );
            const buckets = [
              { label: 'Project Conditions', items: applyDiff.diff.project_conditions, kind: 'plain', testid: 'recall-bucket-project_conditions' },
              { label: 'Project Factors',    items: applyDiff.diff.modifiers,           kind: 'plain', testid: 'recall-bucket-modifiers' },
              { label: 'Components added',   items: applyDiff.diff.components_added,    kind: 'component', testid: 'recall-bucket-components' },
              { label: 'Deliverables added', items: applyDiff.diff.deliverables_added,  kind: 'plain', testid: 'recall-bucket-deliverables' },
              { label: 'Integrations added', items: applyDiff.diff.integrations_added,  kind: 'integration', testid: 'recall-bucket-integrations' },
              { label: 'Auto-flagged from narrative', items: applyDiff.diff.auto_flagged_from_narrative, kind: 'auto_flag', testid: 'recall-bucket-auto_flag' },
              { label: 'Skipped',            items: applyDiff.diff.skipped,             kind: 'plain', testid: 'recall-bucket-skipped' },
            ];
            return (
              <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                {buckets.map(({ label, items, kind, testid }) => {
                  const arr = Array.isArray(items) ? items : [];
                  if (arr.length === 0) return null;
                  return (
                    <div key={label} className="border rounded-lg p-3 bg-slate-50" data-testid={testid}>
                      <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">{label} · {arr.length}</div>
                      <ul className="space-y-1 list-disc list-inside marker:text-slate-400">
                        {arr.map((entry, i) => {
                          if (kind === 'component') {
                            return <li key={i}><Row label={String(entry)} onClick={() => jumpFromDialog('component', String(entry))} testid={`recall-row-comp-${i}`} /></li>;
                          }
                          if (kind === 'integration') {
                            return <li key={i}><Row label={String(entry)} onClick={() => jumpFromDialog('integration', String(entry))} testid={`recall-row-int-${i}`} /></li>;
                          }
                          if (kind === 'auto_flag') {
                            const parsed = parseAutoFlag(String(entry));
                            return <li key={i}><Row
                              label={parsed.raw}
                              onClick={parsed.kind ? () => jumpFromDialog(parsed.kind, parsed.key) : undefined}
                              testid={`recall-row-flag-${i}`}
                            /></li>;
                          }
                          return <li key={i}><Row label={String(entry)} /></li>;
                        })}
                      </ul>
                    </div>
                  );
                })}
                {applyDiff.applied_at && (
                  <div className="text-[11px] text-slate-400">Applied at: {applyDiff.applied_at}</div>
                )}
              </div>
            );
          })() : (
            <div className="text-sm text-slate-500">No apply diff available.</div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyDiffDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Sticky Save Bar (Step 4 only) ── */}
      {step === 4 && results && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t border-slate-200 shadow-lg" data-testid="sticky-save-bar">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-4">
            {/* Left: Status — name now lives in the workspace header h1
                (click-to-rename), so the save bar focuses on save state,
                horizon, and overrides instead of duplicating the input. */}
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                {hasUnsavedChanges && !justSaved && (
                  <span className="flex items-center gap-1.5 text-xs text-amber-600 font-medium whitespace-nowrap" data-testid="save-bar-unsaved-indicator">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved changes
                  </span>
                )}
                {justSaved && (
                  <span className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium whitespace-nowrap animate-in fade-in duration-300">
                    <Check className="w-3.5 h-3.5" />
                    Saved
                  </span>
                )}
                {!hasUnsavedChanges && !justSaved && savedInfo && (
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    Saved {savedInfo.time ? new Date(savedInfo.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                )}
                {results?.projected_weeks && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200/80 whitespace-nowrap hover:bg-cyan-100 hover:ring-cyan-300 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-400"
                        data-testid="project-horizon-chip"
                        title="Click for full scheduling context"
                      >
                        <CalendarDays className="w-3 h-3" />
                        Project horizon: ~{results.projected_weeks}w
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-72 p-0" data-testid="project-horizon-popover">
                      <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-br from-cyan-50 to-white">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-cyan-700/80">Scheduling Snapshot</div>
                        <div className="mt-0.5 text-sm font-semibold text-[#0F2040]">Project horizon: ~{results.projected_weeks} weeks</div>
                      </div>
                      <dl className="px-4 py-3 space-y-2 text-xs">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Complexity</dt>
                          <dd className="font-medium text-[#0F2040] capitalize" data-testid="horizon-complexity">{COMPLEXITY_LABELS?.[complexity] || complexity || '—'}</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Total effort</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="horizon-effort">
                            {Math.round(results?.total_effort || 0).toLocaleString()} hrs
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Total cost</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="horizon-cost">
                            {currencySymbol}{Math.round(results?.total_cost || 0).toLocaleString()}
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Horizon</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="horizon-weeks">{results.projected_weeks} weeks</dd>
                        </div>
                        <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-slate-100">
                          <dt className="text-slate-500">Preferred start</dt>
                          <dd>
                            <DatePickerField
                              value={preferredStartDate}
                              onChange={(iso) => setPreferredStartDate(iso)}
                              testId="horizon-preferred-start-input"
                              placeholder="Pick start"
                              widthClass="w-36"
                            />
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Go-Live est.</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="horizon-golive">
                            {(() => {
                              const base = preferredStartDate ? new Date(preferredStartDate + 'T00:00:00') : new Date();
                              if (isNaN(base.getTime())) return '—';
                              const d = new Date(base);
                              d.setDate(d.getDate() + results.projected_weeks * 7);
                              return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
                            })()}
                          </dd>
                        </div>
                      </dl>
                      <div className="px-4 py-2 bg-slate-50 rounded-b-md text-[10px] text-slate-500 leading-relaxed">
                        Estimate based on effort, complexity, and timing modifiers. Goal-dates in the Planner will override the Go-Live projection.
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {results?.timing_meta?.explicit_go_live_anchor && (results?.timing_meta?.schedule_overrun_weeks || 0) > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 ring-1 ring-rose-200/80 whitespace-nowrap hover:bg-rose-100 hover:ring-rose-300 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-rose-400"
                        data-testid="schedule-risk-chip"
                        title="Effort + breaks exceed your committed go-live window. Click for details."
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Schedule risk: overruns go-live by {results.timing_meta.schedule_overrun_weeks}w
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-0" data-testid="schedule-risk-popover">
                      <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-br from-rose-50 to-white">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-rose-700/80">Schedule Risk</div>
                        <div className="mt-0.5 text-sm font-semibold text-[#0F2040]">
                          Committed go-live: {results.timing_meta.calendar_span_weeks ?? '—'}w window
                        </div>
                      </div>
                      <dl className="px-4 py-3 space-y-2 text-xs">
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Effort-based weeks</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="risk-effort-weeks">
                            {results.timing_meta.effort_based_weeks ?? '—'}w
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Honored breaks</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="risk-break-weeks">
                            +{results.timing_meta.break_extension_weeks ?? 0}w
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <dt className="text-slate-500">Calendar span</dt>
                          <dd className="font-medium text-[#0F2040]" data-testid="risk-span-weeks">
                            {results.timing_meta.calendar_span_weeks ?? '—'}w
                          </dd>
                        </div>
                        <div className="flex items-center justify-between gap-3 pt-1.5 border-t border-slate-100">
                          <dt className="text-rose-600 font-semibold">Overrun</dt>
                          <dd className="font-bold text-rose-700" data-testid="risk-overrun-weeks">
                            {results.timing_meta.schedule_overrun_weeks}w
                          </dd>
                        </div>
                      </dl>
                      <div className="px-4 py-2 bg-rose-50/60 rounded-b-md text-[10px] text-rose-700 leading-relaxed">
                        Go-live is anchored to the date you committed in Project Conditions — it will <strong>not</strong> auto-shift. To resolve: compress scope, drop a break/freeze, or move the committed go-live date.
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {activeScheduleOverrides.count > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-amber-50 text-amber-800 ring-1 ring-amber-200 whitespace-nowrap hover:bg-amber-100 hover:ring-amber-300 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400"
                        data-testid="active-overrides-pill"
                        title="Click to view and manage your per-component schedule overrides"
                      >
                        <Settings className="w-3 h-3" />
                        {activeScheduleOverrides.count} override{activeScheduleOverrides.count === 1 ? '' : 's'}
                        <span className="text-amber-600/80 font-normal">
                          · {activeScheduleOverrides.groups.length} group{activeScheduleOverrides.groups.length === 1 ? '' : 's'}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-80 p-0" data-testid="active-overrides-popover">
                      <div className="px-4 py-3 border-b border-slate-200 bg-gradient-to-br from-amber-50 to-white">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700/80">Schedule Overrides</div>
                            <div className="mt-0.5 text-sm font-semibold text-[#0F2040]">
                              {activeScheduleOverrides.count} component{activeScheduleOverrides.count === 1 ? '' : 's'} with custom timing
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={clearAllScheduleOverrides}
                            className="text-[11px] font-medium text-amber-700 hover:text-amber-900 underline decoration-dotted underline-offset-2 whitespace-nowrap"
                            data-testid="active-overrides-clear-all"
                            title="Reset every per-component Start / Weeks override to auto-schedule"
                          >
                            Clear all
                          </button>
                        </div>
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {activeScheduleOverrides.groups.map((group) => (
                          <div key={group} className="px-4 py-2.5 border-b border-slate-100 last:border-b-0">
                            <div className="flex items-center justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="w-1.5 h-1.5 rounded-full"
                                  style={{ backgroundColor: GROUP_COLORS[group] || '#64748B' }}
                                />
                                <span className="text-[11px] font-semibold text-[#0F2040]">{group}</span>
                                <span className="text-[10px] text-slate-400">({activeScheduleOverrides.byGroup[group].length})</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const groupNames = activeScheduleOverrides.byGroup[group].map((i) => i.name);
                                  setPlannerStartOverrides((prev) => {
                                    const next = { ...prev };
                                    groupNames.forEach((n) => { delete next[n]; });
                                    return next;
                                  });
                                  setPlannerWeeksOverrides((prev) => {
                                    const next = { ...prev };
                                    groupNames.forEach((n) => { delete next[n]; });
                                    return next;
                                  });
                                }}
                                className="text-[10px] text-slate-400 hover:text-slate-700 underline decoration-dotted underline-offset-2"
                                data-testid={`active-overrides-clear-group-${group.toLowerCase().replace(/\s+/g, '-')}`}
                                title={`Reset ${group} overrides`}
                              >
                                clear
                              </button>
                            </div>
                            <ul className="space-y-1">
                              {activeScheduleOverrides.byGroup[group].map((i) => (
                                <li key={i.name}>
                                  <button
                                    type="button"
                                    onClick={() => jumpToComponent(i.name, i.group)}
                                    className="w-full flex items-center justify-between gap-2 text-[11px] text-slate-600 hover:text-[#0F2040] hover:bg-amber-50/70 rounded px-1.5 py-1 -mx-1.5 transition-colors text-left group/jump"
                                    data-testid={`active-overrides-row-${i.name.replace(/\s+/g, '-').toLowerCase()}`}
                                    title={`Jump to ${i.name} in ${i.group}`}
                                  >
                                    <span className="truncate flex-1" title={i.name}>{i.name}</span>
                                    <span className="font-medium text-[#0F2040] tabular-nums whitespace-nowrap">
                                      {i.start ? `W${i.start}` : 'Auto'}
                                      <span className="text-slate-300 mx-1">·</span>
                                      {i.weeks ? `${i.weeks}w` : 'Auto'}
                                    </span>
                                    <ChevronRight className="w-3 h-3 text-slate-300 group-hover/jump:text-amber-600 transition-colors shrink-0" />
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="px-4 py-2 bg-slate-50 rounded-b-md text-[10px] text-slate-500 leading-relaxed">
                        These override the auto-scheduled timing in the Planner. Use <span className="font-medium">Clear all</span> to revert every component to auto-schedule.
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            {/* Center: Quick Nav (gated) */}
            <div className="hidden md:flex items-center gap-1 border border-slate-200 rounded-lg p-0.5 bg-slate-50">
              <button
                onClick={() => navigateWithSaveGate('/workspace/{id}/planner')}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-[#0F2040] hover:bg-white rounded transition-all"
                data-testid="save-bar-planner-btn"
              >
                <CalendarDays className="w-3 h-3" /> Planner
              </button>
              <button
                onClick={() => navigateWithSaveGate('/workspace/{id}/quote')}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-[#0F2040] hover:bg-white rounded transition-all"
                data-testid="save-bar-quote-btn"
              >
                <Briefcase className="w-3 h-3" /> Quote
              </button>
              <button
                onClick={() => navigateWithSaveGate('/workspace/{id}/compare')}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-[#0F2040] hover:bg-white rounded transition-all"
                data-testid="save-bar-compare-btn"
              >
                <GitCompareArrows className="w-3 h-3" /> Compare
              </button>
              <button
                onClick={() => navigateWithSaveGate('/workspace/{id}/overview')}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-slate-600 hover:text-[#0F2040] hover:bg-white rounded transition-all"
                data-testid="save-bar-report-btn"
              >
                <BarChart3 className="w-3 h-3" /> Report
              </button>
            </div>

            {/* Right: Save Split Button — primary updates current
                version in-place; caret menu offers "Save as new version
                (vN → vN+1)" for explicit version bumps. */}
            <div className="flex items-stretch shadow-md shadow-[#4ABFCF]/25 rounded-md overflow-hidden" data-testid="save-bar-save-split">
              <Button
                onClick={() => saveEstimate({ createNewVersion: false })}
                disabled={saving || !results || _discountReasonMissing}
                title={_discountReasonMissing ? 'Add a discount justification (≥10 chars) to enable Save' : (savedEstimateId ? `Save updates v${savedInfo?.version || '?'} in place` : 'Save estimate')}
                className={`h-8 px-5 text-sm font-semibold rounded-r-none border-r border-white/20 transition-all ${
                  hasUnsavedChanges
                    ? 'bg-[#4ABFCF] hover:bg-[#3aa8b8] text-white'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                }`}
                data-testid="save-bar-save-btn"
              >
                {saving ? (
                  <><span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-1.5" /> Saving...</>
                ) : justSaved ? (
                  <><Check className="w-3.5 h-3.5 mr-1.5" /> Saved</>
                ) : (
                  <><Save className="w-3.5 h-3.5 mr-1.5" /> {hasUnsavedChanges ? 'Save Estimate' : 'Save'}</>
                )}
              </Button>
              {savedEstimateId && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      disabled={saving || !results || _discountReasonMissing}
                      className={`h-8 px-2 rounded-l-none transition-all ${
                        hasUnsavedChanges
                          ? 'bg-[#4ABFCF] hover:bg-[#3aa8b8] text-white'
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                      }`}
                      title="More save options"
                      data-testid="save-bar-save-menu-trigger"
                    >
                      <ChevronDown className="w-3.5 h-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400">Save options</DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => saveEstimate({ createNewVersion: false })}
                      data-testid="save-menu-in-place"
                      className="flex flex-col items-start gap-0.5 py-2"
                    >
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Save className="w-3.5 h-3.5 text-emerald-600" /> Update current version
                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded px-1 py-0">DEFAULT</span>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug pl-5.5">
                        Keeps v{savedInfo?.version || '?'}; your edit is appended to its audit trail.
                      </p>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => saveEstimate({ createNewVersion: true })}
                      data-testid="save-menu-new-version"
                      className="flex flex-col items-start gap-0.5 py-2"
                    >
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <GitBranch className="w-3.5 h-3.5 text-blue-600" /> Save as new version
                        <span className="text-[10px] font-mono text-blue-700 bg-blue-50 border border-blue-200 rounded px-1 py-0">v{savedInfo?.version || '?'} → v{(savedInfo?.version || 0) + 1}</span>
                      </div>
                      <p className="text-[11px] text-slate-500 leading-snug pl-5.5">
                        Creates a new immutable snapshot — use for stakeholder-visible milestones.
                      </p>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Pre-save change review ── */}
      <SaveReviewDialog
        open={!!saveReview}
        review={saveReview?.review}
        saving={saving}
        versionLabel={savedInfo?.version != null ? `v${savedInfo.version}` : 'version'}
        lock={effortCostLock}
        onClose={() => setSaveReview(null)}
        onConfirm={confirmReviewedSave}
        onRestoreAll={restoreAllSaved}
        onRestoreField={restoreSavedField}
      />

      {/* ── Save Gate Modal ── */}
      <Dialog open={!!showSaveGateModal} onOpenChange={(open) => { if (!open) setShowSaveGateModal(null); }}>
        <DialogContent className="sm:max-w-[420px]" aria-describedby="save-gate-description">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#0F2040]">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Save Before Continuing
            </DialogTitle>
          </DialogHeader>
          <p id="save-gate-description" className="text-sm text-slate-600">
            {!savedEstimateId
              ? 'You need to save your estimate before accessing other features. This ensures your configuration is preserved.'
              : 'You have unsaved changes. Save your estimate to ensure the latest configuration is available in all views.'}
          </p>
          {!estimateName.trim() && (
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Estimate Name</Label>
              <Input
                value={estimateName}
                onChange={(e) => setEstimateName(e.target.value)}
                placeholder={autoGenerateName()}
                className="h-8 text-sm"
                data-testid="save-gate-name-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowSaveGateModal(null)} data-testid="save-gate-cancel-btn">
              Cancel
            </Button>
            {savedEstimateId && (
              <Button
                onClick={() => saveEstimate({ navigateTo: showSaveGateModal, createNewVersion: true })}
                disabled={saving || _discountReasonMissing}
                title={_discountReasonMissing ? 'Add a discount justification (≥10 chars) first' : `Snapshot a new version (v${savedInfo?.version || '?'} → v${(savedInfo?.version || 0) + 1}) before continuing`}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="save-gate-new-version-btn"
              >
                <GitBranch className="w-3.5 h-3.5 mr-1.5" />
                {saving ? 'Saving...' : `Save as v${(savedInfo?.version || 0) + 1}`}
              </Button>
            )}
            <Button
              onClick={() => saveEstimate({ navigateTo: showSaveGateModal, createNewVersion: false })}
              disabled={saving || _discountReasonMissing}
              title={_discountReasonMissing ? 'Add a discount justification (≥10 chars) first' : (savedEstimateId ? `Update v${savedInfo?.version || '?'} in place and continue` : 'Save & continue')}
              className="bg-[#4ABFCF] hover:bg-[#3aa8b8] text-white"
              data-testid="save-gate-save-btn"
            >
              {saving ? 'Saving...' : (savedEstimateId ? 'Update & Continue' : 'Save & Continue')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ComponentDocSheet
        open={!!docComponent}
        onOpenChange={(o) => { if (!o) setDocComponent(null); }}
        component={docComponent}
      />

      {/* Quick-edit Project Conditions sheet — renders for any estimate
          flagged `has_conditions=true`, including unsaved wizard-created
          ones. When `loadedEstimateId` is null the dialog operates against
          local state and `onSaved` patches `pendingConditions` directly. */}
      {(hasConditions || editConditionsOpen) && (
        <EditConditionsDialog
          open={editConditionsOpen}
          onOpenChange={setEditConditionsOpen}
          estimateId={loadedEstimateId}
          initialConditions={pendingConditions}
          availableComponentNames={Object.keys(selectedComponents).filter(n => selectedComponents[n] > 0)}
          highlightBreakId={firstViolatedBreakId}
          hasConditionsValue={hasConditions}
          entityContext={{
            entities: seertechEntities,
            entityId: contractingEntityId,
            region,
            onApplied: (eid, reg, ccy) => {
              setContractingEntityId(eid || null);
              setRegion(reg || null);
              if (ccy) setCurrencyCode(ccy);
            },
          }}
          onSaved={async (localConditions) => {
            // For UNSAVED estimates the dialog hands us back the edited
            // conditions object; sync it into local state so the rest of
            // the page (calculator, planner subnav) sees the change.
            if (!loadedEstimateId && localConditions) {
              setPendingConditions(localConditions);
              setHasConditions(true);
              calculateEstimate();
              return;
            }
            // For SAVED estimates re-load to pick up the server-side recalc.
            try {
              const r = await axios.get(
                `${API_URL}/api/estimates/${loadedEstimateId}`,
                { withCredentials: true },
              );
              const est = r.data;
              if (est?.has_conditions && est?.project_conditions) {
                setPendingConditions(est.project_conditions);
                setHasConditions(true);
              }
              calculateEstimate();
            } catch (e) { /* silent */ }
            // Refresh the SOW drift summary so the chip updates if
            // saving Conditions widened or resolved drift on
            // downstream SOWs.
            fetchSowDriftSummary(loadedEstimateId);
          }}
        />
      )}
    </div>
  );
}

/**
 * PricingImpactPreview — live revenue indicator below the per-estimate
 * blanket discount input. Multiplies the cached avg total cost (last 50
 * estimates, super-admin scoped) by the discount factor to give an
 * at-a-glance "≈ −$12.4k on the average estimate" feel.
 *
 * Sign convention: positive `pct` is a discount → impact rendered as a
 * NEGATIVE delta (red); negative `pct` is a surcharge → POSITIVE delta (green).
 */
function PricingImpactPreview({ pct, baseline, sym = '$' }) {
  const avg = Number(baseline?.avg_total_cost) || 0;
  const count = Number(baseline?.count) || 0;
  if (avg <= 0 || count <= 0) return null;
  const factor = 1 - (Number(pct) || 0) / 100;
  const newAvg = avg * factor;
  const delta = newAvg - avg;
  const fmt = (n) => {
    const abs = Math.abs(n);
    if (abs >= 1_000_000) return `${sym}${(n / 1_000_000).toFixed(2)}M`;
    if (abs >= 1_000) return `${sym}${(n / 1_000).toFixed(1)}k`;
    return `${sym}${Math.round(n).toLocaleString()}`;
  };
  const isZero = Math.abs(delta) < 0.5;
  const tone = isZero
    ? 'bg-slate-50 text-slate-600 border-slate-200'
    : delta < 0
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <div
      className="mt-3 pt-3 border-t border-[#4ABFCF]/20 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2"
      data-testid="estimator-pricing-impact-preview"
    >
      <div className="text-[11px] text-slate-600">
        <span className="font-semibold text-[#0F2040]">Pricing impact</span>
        <span className="text-slate-500"> · last {count} estimates · avg total {fmt(avg)}</span>
      </div>
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${tone} text-[11px] font-semibold`}>
        {isZero ? (
          <span>≈ no change</span>
        ) : (
          <>
            <span data-testid="estimator-pricing-impact-delta">
              ≈ {delta < 0 ? '−' : '+'}{fmt(Math.abs(delta))}
            </span>
            <span className="opacity-70">on the average estimate</span>
          </>
        )}
      </div>
    </div>
  );
}

